import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { 
  useTaskDetail,
  useTaskStatuses,
  useSystemSettings,
  useUpdateTaskDetail,
  useProjectDetail,
  useUsers,
  useCreateTimeEntry,
  useUpdateTimeEntry,
  useDeleteTimeEntry,
  useTaskActivity,
  useTaskSubtasks,
  useTaskNotes,
  useDailyHoursForTask,
  useGitHubConnectionStatus,
  useGitHubOrganizations,
  useGitHubRepositories,
  useRepositoryCommits,
  useAddTaskComment,
  useUpdateTaskStatus,
  useAddTaskNote,
  useCreateSubtask,
  useUpdateSubtask,
  useDeleteSubtask,
  useTaskComments,
  useNotes,
  useGitHubStatus,
  useUpdateTaskComment,
  useDeleteTaskComment,
  useAddCommentReaction,
  useRemoveCommentReaction,
  useUploadScreenshot,
  useDeleteScreenshot,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
  useCreateGitHubCommit,
  useCreateGitHubPR,
  useConnectTaskToGitHub,
  useDisconnectTaskFromGitHub,
  useAddGitHubCollaborator,
  useRemoveGitHubCollaborator,
  useCreateGitHubRepository,
  useCreateTask,
  useSprints
} from '../hooks/api';
import Attachment from './Attachment';
import {
    ArrowLeft,
    ArrowRight,
    Clock,
    Play,
    Square,
    MessageSquare,
    Image,
    Upload,
    Trash2,
    Tag,
    AlertTriangle,
    Calendar,
    User,
    Send,
    ChevronDown,
    Plus,
    Paperclip,
    List,
    CheckSquare,
    X,
    Search,
    Pause,
    UserPlus,
    UserMinus,
    ExternalLink,
    Download,
    Edit,
    AlertCircle,
    Minus,
    StickyNote,
    Lock,
    Globe,
    Eye,
    EyeOff,
    Loader2,
    Mic,
    MicOff,
    FileText,
    File,
    Smile,
    ThumbsUp,
    Heart,
    Laugh,
    Wow,
    Sad,
    Angry,
    Copy,
    CopyPlus,
    Flag,
    Users,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';
import FormDrawer from './FormDrawer';
import { toUTCPlus5, toUTCPlus5DateOnly, formatDurationHMS, formatDueDate } from '../utils/time';
import { getAttachmentUrl } from '../utils/attachmentUtils';
import { useTimer } from '../contexts/TimerContext';
import { useIdleDetection } from './IdleDetection';
import { timeTrackingService } from '../services/timeTracking';
import { releaseManagementService } from '../services/releaseManagement';
import { codeReviewService } from '../services/codeReviews';
import { getErrorMessage } from '../utils/errorUtils';
import { TextWithLinks } from './LinkPreview';
import { parseTextWithLinks } from '../utils/linkUtils';
import VoiceRecorder from './VoiceRecorder';
import VoiceNotePlayer from './VoiceNotePlayer';
import { Button, Panel, StatCard, EmptyState, StatusPill, ViewTabs } from './ui';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';

// Official Microsoft Office Icons
const WordIcon = ({ className = "h-6 w-6" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none">
        <rect width="20" height="20" x="2" y="2" rx="2" fill="#2B579A"/>
        <path d="M8 6h8v12H8z" fill="white"/>
        <path d="M10 8h4v8h-4z" fill="#2B579A"/>
        <path d="M12 10h2v4h-2z" fill="white"/>
    </svg>
);

const ExcelIcon = ({ className = "h-6 w-6" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none">
        <rect width="20" height="20" x="2" y="2" rx="2" fill="#217346"/>
        <path d="M8 6h8v12H8z" fill="white"/>
        <path d="M10 8h4v8h-4z" fill="#217346"/>
        <path d="M12 10h2v4h-2z" fill="white"/>
        <path d="M11 11h1v1h-1z" fill="#217346"/>
        <path d="M13 11h1v1h-1z" fill="#217346"/>
        <path d="M11 13h1v1h-1z" fill="#217346"/>
        <path d="M13 13h1v1h-1z" fill="#217346"/>
    </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TaskDetail = ({ taskId, onBack }) => {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const { activeTimer, startTimer, stopTimer, elapsedTime } = useTimer();
    
  //
    const { 
        data: taskData, 
        isLoading: taskLoading,
        refetch: fetchTaskDetails
    } = useTaskDetail(taskId);
    
    const { 
        data: projectData 
    } = useProjectDetail(taskData?.task?.project_id);
    
    const { data: sprintsData } = useSprints(taskData?.task?.project_id);
    const sprints = sprintsData?.results || [];
    const taskSprint = taskData?.task?.sprint_id ? sprints.find(s => s.id === taskData.task.sprint_id) : null;
    
    const { data: usersData } = useUsers();
    const allUsers = usersData?.users || usersData || [];
    
    const queryClient = useQueryClient();
    const updateTaskDetailMutation = useUpdateTaskDetail();
    
    const { 
        data: statusesData, 
        isLoading: statusesLoading,
        refetch: fetchStatuses
    } = useTaskStatuses();
    
    const { data: systemSettings } = useSystemSettings();
    const dailyHoursCap = systemSettings?.daily_hours_cap ?? SYSTEM_SETTINGS_DEFAULTS.daily_hours_cap;
    const minDescLen = systemSettings?.min_description_length ?? SYSTEM_SETTINGS_DEFAULTS.min_description_length;
    
    // Mutations
    const createTimeEntryMutation = useCreateTimeEntry();
    const updateTimeEntryMutation = useUpdateTimeEntry();
    const deleteTimeEntryMutation = useDeleteTimeEntry();
    const addTaskCommentMutation = useAddTaskComment();
    const updateTaskStatusMutation = useUpdateTaskStatus();
    const addTaskNoteMutation = useAddTaskNote();
    const createSubtaskMutation = useCreateSubtask();
    const updateSubtaskMutation = useUpdateSubtask();
    const deleteSubtaskMutation = useDeleteSubtask();
    
    // Additional data hooks
    const { 
        data: taskCommentsData, 
        isLoading: taskCommentsLoading 
    } = useTaskComments(taskId);
    
    const { 
        data: notesData, 
        isLoading: notesLoading 
    } = useNotes('task', taskId);
    
    const { 
        data: githubStatusData, 
        isLoading: githubStatusLoading 
    } = useGitHubStatus();
    
    // Additional mutations
    const updateTaskCommentMutation = useUpdateTaskComment();
    const deleteTaskCommentMutation = useDeleteTaskComment();
    const addCommentReactionMutation = useAddCommentReaction();
    const removeCommentReactionMutation = useRemoveCommentReaction();
    const uploadScreenshotMutation = useUploadScreenshot();
    const deleteScreenshotMutation = useDeleteScreenshot();
    const createNoteMutation = useCreateNote();
    const updateNoteMutation = useUpdateNote();
    const deleteNoteMutation = useDeleteNote();
    const createGitHubCommitMutation = useCreateGitHubCommit();
    const createGitHubPRMutation = useCreateGitHubPR();
    const connectTaskToGitHubMutation = useConnectTaskToGitHub();
    const disconnectTaskFromGitHubMutation = useDisconnectTaskFromGitHub();
    const addGitHubCollaboratorMutation = useAddGitHubCollaborator();
    const removeGitHubCollaboratorMutation = useRemoveGitHubCollaborator();
    const createGitHubRepositoryMutation = useCreateGitHubRepository();
    const createTaskMutation = useCreateTask();
    
    // Extract data from React Query responses
    const taskComments = taskCommentsData || [];
    const notes = notesData || [];
    const githubStatus = githubStatusData || {};
    
    // UI State (not data state)
    const [loading, setLoading] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showLogTimeModal, setShowLogTimeModal] = useState(false);
    const [logTime, setLogTime] = useState({ hours: '', minutes: '', description: '' });
    const [logTimeLoading, setLogTimeLoading] = useState(false);
    const [showTimerStopModal, setShowTimerStopModal] = useState(false);
    const [timerStopDescription, setTimerStopDescription] = useState('');
    const [timerStopLoading, setTimerStopLoading] = useState(false);
    const [dailyHoursTracked, setDailyHoursTracked] = useState(0);
    const [activity, setActivity] = useState([]);
    const [activityOffset, setActivityOffset] = useState(0);
    const [activityHasMore, setActivityHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [showScreenshotInput, setShowScreenshotInput] = useState(false);
    const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
    const [mentionFilter, setMentionFilter] = useState("");
    const [cursorPosition, setCursorPosition] = useState(0);
    const commentInputRef = useRef(null);
    const [subtasks, setSubtasks] = useState([]);
    const [showSubtaskModal, setShowSubtaskModal] = useState(false);
    const [isCreatingSubtask, setIsCreatingSubtask] = useState(false);
    const [editingSubtaskId, setEditingSubtaskId] = useState(null);
    const [addingComment, setAddingComment] = useState(false);
    const [newSubtask, setNewSubtask] = useState({
        name: '',
        description: '',
        estimated_hours: '',
        priority_level: 'medium',
        due_date: '',
        assigned_to: []
    });
    
    // Notes functionality
    const [newNote, setNewNote] = useState('');
    const [noteVisibility, setNoteVisibility] = useState('public');
    const [addingNote, setAddingNote] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteContent, setEditingNoteContent] = useState('');
    const [activeTab, setActiveTab] = useState('activity'); // 'activity' or 'notes'
    const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);
    
    // Voice recording state
    const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
    const [isRecordingVoice, setIsRecordingVoice] = useState(false);
    const [startingTimer, setStartingTimer] = useState(false);
    const [stoppingTimer, setStoppingTimer] = useState(false);
    const [updatingNoteVisibility, setUpdatingNoteVisibility] = useState(null);
    const [deletingNote, setDeletingNote] = useState(false);
    const [memberSearchFilter, setMemberSearchFilter] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(null);
    const [reactions, setReactions] = useState({});
    const ACTIVITY_LIMIT = 10;
    const activityContainerRef = useRef(null);
    const [activityContainerHeight, setActivityContainerHeight] = useState('auto');
    const prevActivityLength = useRef(0);
    const isLoadingOlder = useRef(false);
    const [selectedScreenshot, setSelectedScreenshot] = useState(null);
    const [isScreenshotMaximized, setIsScreenshotMaximized] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ open: false, screenshotId: null });
    const [showDeleteSubtaskModal, setShowDeleteSubtaskModal] = useState(false);
    const [subtaskToDelete, setSubtaskToDelete] = useState(null);
    const [showDeleteCommentModal, setShowDeleteCommentModal] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);

    const [editingCommentId, setEditingCommentId] = useState(null);
    const [inlineEditText, setInlineEditText] = useState('');
    const [isUpdatingComment, setIsUpdatingComment] = useState(false);
    const [filePreviewModal, setFilePreviewModal] = useState({ open: false, file: null, loading: false });
    // Upload states for Files & Attachments section
    const [attachmentUploadProgress, setAttachmentUploadProgress] = useState(0);
    const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);
    
    // Upload states for Comment attachment
    const [commentUploadProgress, setCommentUploadProgress] = useState(0);
    const [isCommentUploading, setIsCommentUploading] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [showAllMembersModal, setShowAllMembersModal] = useState(false);
    const [showTimeBreakdownModal, setShowTimeBreakdownModal] = useState(false);
    const [showGitHubConnectModal, setShowGitHubConnectModal] = useState(false);
    const [githubConnectLoading, setGithubConnectLoading] = useState(false);
    const [showReleaseManagementModal, setShowReleaseManagementModal] = useState(false);
    const [showCodeReviewModal, setShowCodeReviewModal] = useState(false);
    const [showTimeEntryModal, setShowTimeEntryModal] = useState(false);
    const [selectedCommitForTimeEntry, setSelectedCommitForTimeEntry] = useState(null);
    const [githubDisconnectLoading, setGithubDisconnectLoading] = useState(false);
    const [showAddCollaboratorModal, setShowAddCollaboratorModal] = useState(false);
    const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
    // Inline editing state
    const [editingField, setEditingField] = useState(null); // 'name' | 'description' | 'estimated' | 'due_date' | 'priority' | null
    const [inlineEditValue, setInlineEditValue] = useState('');
    const [isUpdatingTask, setIsUpdatingTask] = useState(false);
    const [showAddMemberDropdown, setShowAddMemberDropdown] = useState(false);
    const inlineEditInputRef = useRef(null);
    const addMemberDropdownRef = useRef(null);
    const [showRemoveCollaboratorModal, setShowRemoveCollaboratorModal] = useState(false);
    const [showGitHubCommitModal, setShowGitHubCommitModal] = useState(false);
    const [collaboratorToRemove, setCollaboratorToRemove] = useState(null);
    const [addCollaboratorLoading, setAddCollaboratorLoading] = useState(false);
    const [newCollaborator, setNewCollaborator] = useState({ username: '', permission: 'push' });
    const [showCreateRepoForm, setShowCreateRepoForm] = useState(false);
    const [selectedCommit, setSelectedCommit] = useState(null);
    const [commits, setCommits] = useState([]);
    const [commitsLoading, setCommitsLoading] = useState(false);
    const [commitSearchQuery, setCommitSearchQuery] = useState('');
    const [filteredCommits, setFilteredCommits] = useState([]);
    const [commitFilter, setCommitFilter] = useState('all'); // all, recent, branch, author
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedAuthor, setSelectedAuthor] = useState('');
    const [availableBranches, setAvailableBranches] = useState([]);
    const [availableAuthors, setAvailableAuthors] = useState([]);
    const [githubConnectionStatus, setGithubConnectionStatus] = useState(null);
    const [showEnhancedCommentModal, setShowEnhancedCommentModal] = useState(false);
    const [enhancedCommentType, setEnhancedCommentType] = useState('commit'); // commit, issue, pr, file
    const [enhancedCommentQuery, setEnhancedCommentQuery] = useState('');
    const [enhancedCommentResults, setEnhancedCommentResults] = useState([]);
    const [enhancedCommentLoading, setEnhancedCommentLoading] = useState(false);
    const [repositoriesLoading, setRepositoriesLoading] = useState(false);
    const [hoveredReference, setHoveredReference] = useState(null);
    const [referenceContext, setReferenceContext] = useState({});
    const [contextLoading, setContextLoading] = useState(false);
    const [contextHoverTimeout, setContextHoverTimeout] = useState(null);
    const { settings } = useIdleDetection();
    const idleDetectionEnabled = !!activeTimer;
    const idleThresholdMs = settings?.idle_threshold_minutes
    const idleDetection = useIdleDetection(idleDetectionEnabled ? idleThresholdMs : null);
    const isIdle = idleDetection.isIdle;
    const idleStartTime = idleDetection.idleStartTime;
    const idleDuration = idleStartTime ? Date.now() - idleStartTime : 0;



    // 🎯 NO MORE useEffect FOR DATA FETCHING! React Query handles it automatically

    useEffect(() => {
        if (githubConnectionStatus?.connected && user) {
            fetchGitHubOrganizations();
            // Pre-load repositories if not already loaded
            if (!user?.github_repositories || user.github_repositories.length === 0) {
                fetchGitHubRepositories();
            }
        }
    }, [githubConnectionStatus, user]);

    // Fetch daily hours when modal opens
    useEffect(() => {
        if (showLogTimeModal) {
            fetchDailyHours();
        }
    }, [showLogTimeModal]);

    // Fetch commits when GitHub commit modal opens
    useEffect(() => {
        if (showGitHubCommitModal && taskData?.task?.github_integration) {
            fetchRepositoryCommits();
        }
    }, [showGitHubCommitModal, taskData?.task?.github_integration]);

    // Focus inline edit input when entering edit mode
    useEffect(() => {
        if (editingField) {
            setTimeout(() => inlineEditInputRef.current?.focus(), 50);
        }
    }, [editingField]);

    // Close add member dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showAddMemberDropdown && addMemberDropdownRef.current && !addMemberDropdownRef.current.contains(e.target)) {
                setShowAddMemberDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showAddMemberDropdown]);

    // Cleanup hover timeout on unmount
    useEffect(() => {
        return () => {
            if (contextHoverTimeout) {
                clearTimeout(contextHoverTimeout);
            }
        };
    }, [contextHoverTimeout]);

    // Keyboard shortcuts for GitHub features
    useEffect(() => {
        // Don't set up keyboard shortcuts if data isn't loaded yet
        if (!taskData?.task || !user) return;
        
        const handleKeyDown = (e) => {
            // Only handle shortcuts when not typing in input fields
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            
            // Ctrl/Cmd + G for GitHub commit modal
            if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
                e.preventDefault();
                if (user?.github_connected && taskData?.task?.github_integration) {
                    setShowGitHubCommitModal(true);
                }
            }
            
            // Ctrl/Cmd + Shift + G for enhanced comment modal
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G') {
                e.preventDefault();
                if (user?.github_connected) {
                    setShowEnhancedCommentModal(true);
                }
            }
            
            // Ctrl/Cmd + Enter for quick comment submission
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                if (newComment.trim() && !addingComment) {
                    handleAddComment(e);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [user?.github_connected, taskData?.task?.github_integration, newComment, addingComment]);

    useEffect(() => {
        if (activityContainerRef.current) {
            if (!isLoadingOlder.current) {
                // Scroll to bottom for new activities (newest at bottom)
                activityContainerRef.current.scrollTop = activityContainerRef.current.scrollHeight;
            } else {
                // Maintain scroll position when loading older activities at the top
                const prevHeight = activityContainerRef.current.scrollHeight;
                setTimeout(() => {
                    if (activityContainerRef.current) {
                        // Keep the same relative position when older activities are loaded at top
                        const newHeight = activityContainerRef.current.scrollHeight;
                        const heightDifference = newHeight - prevHeight;
                        activityContainerRef.current.scrollTop = heightDifference;
                    }
                }, 0);
                isLoadingOlder.current = false;
            }
        }
        prevActivityLength.current = activity.length;
    }, [activity]);

    // Dynamic height calculation based on content
    useEffect(() => {
        const calculateDynamicHeight = () => {
            if (activityContainerRef.current) {
                const container = activityContainerRef.current;
                const contentHeight = container.scrollHeight;
                const minHeight = 300; // Minimum height in pixels
                const maxHeight = window.innerHeight * 0.7; // 70% of viewport height
                
                // Calculate dynamic height based on content
                let dynamicHeight = Math.max(minHeight, contentHeight);
                dynamicHeight = Math.min(dynamicHeight, maxHeight);
                
                setActivityContainerHeight(`${dynamicHeight}px`);
            }
        };

        // Calculate height when activity changes
        calculateDynamicHeight();

        // Recalculate on window resize
        const handleResize = () => calculateDynamicHeight();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [activity, selectedCommit]);

    // Add scroll event listener for infinite scrolling
    useEffect(() => {
        const activityContainer = activityContainerRef.current;
        if (!activityContainer) return;

        let scrollTimeout;
        const handleScroll = () => {
            // Clear any existing timeout
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
            
            // Debounce scroll events to prevent too many calls
            scrollTimeout = setTimeout(() => {
                // Check if user has scrolled to the top (since oldest activities are at top)
                // Load more when user scrolls to within 150px of the top for better responsiveness
                if (activityContainer.scrollTop <= 150 && activityHasMore && !isLoadingMore && !isLoadingOlder.current) {
                    // Prevent multiple simultaneous loading requests
                    setIsLoadingMore(true);
                    const newOffset = activity.length;
                    setActivityOffset(newOffset);
                    isLoadingOlder.current = true;
                    
                    // Add a small delay to prevent rapid successive calls
                    setTimeout(() => {
                        fetchActivity(newOffset, true);
                    }, 100);
                }
            }, 50); // 50ms debounce
        };

        activityContainer.addEventListener('scroll', handleScroll);
        return () => {
            activityContainer.removeEventListener('scroll', handleScroll);
            if (scrollTimeout) {
                clearTimeout(scrollTimeout);
            }
        };
    }, [activity.length, activityHasMore, isLoadingMore]);

    // fetchTaskDetails is now handled by React Query useTaskDetail hook

    // fetchStatuses is now handled by React Query useTaskStatuses hook

    const fetchActivity = async (offset = 0, append = false) => {
        try {
            const response = await axios.get(`/api/tasks/${taskId}/activity?offset=${offset}&limit=${ACTIVITY_LIMIT}`);
            
            if (append) {
                // When appending older activities, add them to the beginning (oldest first)
                setActivity(prev => [...response.data, ...prev]);
                setActivityHasMore(response.data.length === ACTIVITY_LIMIT);
                setIsLoadingMore(false); // Reset loading state
            } else {
                // For initial fetch, reverse the order so oldest appears at top, newest at bottom
                setActivity([...response.data].reverse());
                setActivityHasMore(response.data.length === ACTIVITY_LIMIT);
                // Scroll to bottom after initial fetch to show newest activities
                setTimeout(() => {
                    if (activityContainerRef.current) {
                        activityContainerRef.current.scrollTop = activityContainerRef.current.scrollHeight;
                    }
                }, 0);
            }
        } catch (error) {
            if (!append) setActivity([]);
            setIsLoadingMore(false); // Reset loading state on error
        }
    };

    useEffect(() => {
        setActivityOffset(0);
        fetchActivity(0, false);
        fetchSubtasks();
    }, [taskId]);

    // fetchSystemSettings is now handled by the local useSystemSettings hook from IdleDetection

    const fetchSubtasks = async () => {
        try {
            const response = await axios.get(`/api/tasks/${taskId}/subtasks`);
            setSubtasks(response.data.subtasks || []);
        } catch (error) {
            console.error('Error fetching subtasks:', error);
            setSubtasks([]);
        }
    };

    const handleCreateSubtask = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!newSubtask.name.trim()) {
            toast.error('Subtask name is required');
            return;
        }
        
        if (!newSubtask.description || newSubtask.description.trim() === '') {
            toast.error('Subtask description is required');
            return;
        }
        
        if (!newSubtask.estimated_hours || parseFloat(newSubtask.estimated_hours) <= 0) {
            toast.error('Estimated hours must be greater than 0');
            return;
        }
        
        if (!newSubtask.due_date || !newSubtask.due_date.trim()) {
            toast.error('Due date is required');
            return;
        }
        
        if (!newSubtask.assigned_to || newSubtask.assigned_to.length === 0) {
            toast.error('At least one member must be assigned');
            return;
        }

        setIsCreatingSubtask(true);
        try {
            const subtaskData = {
                ...newSubtask,
                estimated_hours: parseFloat(newSubtask.estimated_hours),
                due_date: newSubtask.due_date.trim(),
                parent_task_id: taskId // Add parent task reference
            };
            
            if (editingSubtaskId) {
                // Edit existing subtask
                await axios.put(`/api/tasks/${taskId}/subtasks/${editingSubtaskId}`, subtaskData);
                toast.success('Subtask updated successfully');
            } else {
                // Create new subtask
                await axios.post(`/api/tasks/${taskId}/subtasks`, subtaskData);
                toast.success('Subtask created successfully');
            }
            
            setShowSubtaskModal(false);
            setEditingSubtaskId(null);
            setNewSubtask({
                name: '',
                description: '',
                estimated_hours: '',
                priority_level: 'medium',
                due_date: '',
                assigned_to: []
            });
            fetchSubtasks();
            fetchActivity(0, false); // Refresh activity to show subtask creation
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to create subtask'));
            console.error('Error creating subtask:', error);
        } finally {
            setIsCreatingSubtask(false);
        }
    };

    const handleDeleteSubtask = (subtaskId) => {
        setSubtaskToDelete(subtaskId);
        setShowDeleteSubtaskModal(true);
    };

    const handleEditSubtask = (subtask) => {
        // Populate form with subtask data for editing
        setNewSubtask({
            name: subtask.name,
            description: subtask.description || '',
            priority_level: subtask.priority_level || 'medium',
            estimated_hours: subtask.estimated_hours || '',
            due_date: subtask.due_date || '',
            assigned_to: subtask.assigned_to || []
        });
        setEditingSubtaskId(subtask.id);
        setShowSubtaskModal(true);
    };

    const confirmDeleteSubtask = async () => {
        try {
            await axios.delete(`/api/tasks/${taskId}/subtasks/${subtaskToDelete}`);
            toast.success('Subtask deleted successfully');
            fetchSubtasks();
            fetchActivity(0, false); // Refresh activity to show subtask deletion
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to delete subtask'));
            console.error('Error deleting subtask:', error);
        } finally {
            setShowDeleteSubtaskModal(false);
            setSubtaskToDelete(null);
        }
    };

    const handleSubtaskStatusChange = async (subtaskId, newStatus) => {
        // Check if trying to move to QA or Done status for subtasks
        if (newStatus === 'QA' || newStatus === 'In QA' || newStatus === 'Done') {
            // For subtasks, we need to check if the parent task has BOTH screenshots AND time logged
            // since subtasks inherit the parent task's context
            const hasScreenshots = taskData.screenshots && taskData.screenshots.length > 0;
            const hasTimeLogged = taskData.time_entries && taskData.time_entries.length > 0;
            
            if (!hasScreenshots || !hasTimeLogged) {
                toast.error(`Cannot move subtask to ${newStatus}. The parent task must have BOTH screenshots AND time logged before moving to this status.`);
                return;
            }
        }
        
        try {
            await axios.patch(`/api/tasks/${subtaskId}/status`, { status: newStatus });
            toast.success('Subtask status updated');
            fetchSubtasks();
            fetchActivity(0, false);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to update subtask status'));
            console.error('Error updating subtask status:', error);
        }
    };

    const navigateToSubtask = (subtaskId) => {
        // Navigate to the subtask detail view
        // The subtask will show the parent task information
        navigate(`/tasks/${subtaskId}`);
    };

    const isManualLoggingEnabled = () => {
        if (!taskData?.task) return false;

        // Check task-specific setting first
        if (taskData.task.enable_manual_logging !== null && taskData.task.enable_manual_logging !== undefined) {
            return taskData.task.enable_manual_logging;
        }

        // Fall back to system setting
        return systemSettings?.enable_manual_logging;
    };

    const handleStatusChange = async (newStatus) => {
        if (!taskData) return;
        
        // Check if trying to move to QA and there are subtasks
        if (newStatus === 'QA' && subtasks.length > 0) {
            // Check if all subtasks are in QA or Done status
            const subtasksNotReady = subtasks.filter(subtask => 
                subtask.status !== 'QA' && subtask.status !== 'Done'
            );
            
            if (subtasksNotReady.length > 0) {
                const subtaskNames = subtasksNotReady.map(s => s.name).join(', ');
                toast.error(`Cannot move to QA. The following subtasks must be completed first: ${subtaskNames}`);
                setShowStatusDropdown(false);
                return;
            }
        }
        
        // Check if trying to move to QA or Done status
        if (newStatus === 'QA' || newStatus === 'In QA' || newStatus === 'Done') {
            // Check if there are BOTH screenshots AND time logged (AND logic)
            const hasScreenshots = taskData.screenshots && taskData.screenshots.length > 0;
            const hasTimeLogged = taskData.time_entries && taskData.time_entries.length > 0;
            
            if (!hasScreenshots || !hasTimeLogged) {
                toast.error(`Cannot move to ${newStatus}. You must BOTH attach a screenshot AND log time before moving to this status.`);
                setShowStatusDropdown(false);
                return;
            }
        }
        
        setIsUpdatingStatus(true);
        try {
            await axios.patch(`/api/tasks/${taskId}/status`, {
                status: newStatus
            });
            fetchTaskDetails();
            fetchActivity(0, false);
            toast.success('Status updated');
        } catch (error) {
            const msg = error?.response?.data?.detail || error?.message || 'Failed to update status';
            toast.error(msg);
        } finally {
            setIsUpdatingStatus(false);
            setShowStatusDropdown(false);
        }
    };

    // Inline edit handlers for task name, description, estimated hours
    const canEditTask = user?.role === 'admin' || user?.role === 'project_lead' || (user?.role === 'member' && taskData?.task?.assigned_to?.includes(user?.id));

    const handleDuplicateTask = async () => {
        if (!taskData?.task || createTaskMutation.isPending) return;
        const t = taskData.task;
        const desc = t.description || '';
        const assignees = Array.isArray(t.assigned_to) && t.assigned_to.length > 0
            ? [...t.assigned_to]
            : (user?.id ? [user.id] : []);
        if (assignees.length === 0) {
            toast.error('Cannot duplicate: assign at least one member to the task first');
            return;
        }
        const payload = {
            name: `${(t.name || 'Task').trim()} (Copy)`,
            description: desc.length > 250 ? desc.slice(0, 250) : desc,
            project_id: t.project_id,
            estimated_hours: t.estimated_hours ?? undefined,
            priority_level: t.priority_level || 'medium',
            tags: Array.isArray(t.tags) ? [...t.tags] : [],
            due_date: t.due_date ? new Date(t.due_date).toISOString() : undefined,
            status: 'To Do',
            assigned_to: assignees,
        };
        try {
            const created = await createTaskMutation.mutateAsync(payload);
            setShowDuplicateModal(false);
            toast.success('Task duplicated successfully');
            navigate(`/tasks/${created.id}`);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Failed to duplicate task';
            toast.error(msg);
        }
    };
    
    const startInlineEdit = (field, currentValue) => {
        setEditingField(field);
        setInlineEditValue(currentValue ?? '');
        setTimeout(() => inlineEditInputRef.current?.focus(), 0);
    };

    const cancelInlineEdit = () => {
        setEditingField(null);
        setInlineEditValue('');
    };

    const saveInlineEdit = async () => {
        if (!taskData?.task || !editingField || isUpdatingTask) return;
        const task = taskData.task;
        let newValue = inlineEditValue;
        
        if (editingField === 'name') {
            if (!newValue.trim()) {
                toast.error('Task name cannot be empty');
                return;
            }
        } else if (editingField === 'estimated') {
            if (newValue.trim() === '') {
                newValue = null; // Allow clearing estimate
            } else {
                const num = parseFloat(newValue);
                if (isNaN(num) || num < 0) {
                    toast.error('Please enter a valid number');
                    return;
                }
                newValue = num;
            }
        } else if (editingField === 'due_date') {
            newValue = newValue.trim() === '' ? null : newValue;
        } else if (editingField === 'priority') {
            if (!['low', 'medium', 'high', 'urgent'].includes(newValue)) {
                toast.error('Invalid priority');
                return;
            }
        }
        
        // No change - close without API call
        const origName = (task.name || '').trim();
        const origDesc = (task.description || '').trim();
        const origEst = task.estimated_hours != null ? String(task.estimated_hours) : '';
        const origDue = task.due_date ? format(new Date(task.due_date), 'yyyy-MM-dd') : '';
        const origPriority = task.priority_level || 'medium';
        const hasChanged = (
            (editingField === 'name' && newValue.trim() !== origName) ||
            (editingField === 'description' && newValue.trim() !== origDesc) ||
            (editingField === 'estimated' && (newValue === null ? origEst !== '' : String(newValue) !== origEst)) ||
            (editingField === 'due_date' && ((newValue === null && origDue !== '') || (newValue !== null && newValue !== origDue))) ||
            (editingField === 'priority' && newValue !== origPriority)
        );
        if (!hasChanged) {
            setEditingField(null);
            setInlineEditValue('');
            return;
        }
        
        setIsUpdatingTask(true);
        const updateData = {
            name: editingField === 'name' ? newValue : task.name,
            description: editingField === 'description' ? newValue : (task.description || ''),
            project_id: task.project_id,
            estimated_hours: editingField === 'estimated' ? newValue : task.estimated_hours,
            priority_level: editingField === 'priority' ? newValue : (task.priority_level || 'medium'),
            tags: task.tags || [],
            due_date: editingField === 'due_date' ? newValue : (task.due_date || null),
            status: task.status || 'To Do',
            assigned_to: task.assigned_to || [],
            enable_manual_logging: task.enable_manual_logging
        };
        // Optimistic update - instant UI
        queryClient.setQueryData(['taskDetail', taskId], (old) => old ? {
            ...old,
            task: { ...old.task, ...updateData }
        } : old);
        setEditingField(null);
        setInlineEditValue('');
        try {
            await updateTaskDetailMutation.mutateAsync({ id: taskId, data: updateData });
            toast.success('Updated');
        } catch (error) {
            fetchTaskDetails(); // Rollback on error
            toast.error(error?.response?.data?.detail || 'Failed to update');
        } finally {
            setIsUpdatingTask(false);
        }
    };

    const handleAddMemberToTask = async (memberId) => {
        if (!taskData?.task || isUpdatingTask) return;
        const currentAssigned = taskData.task.assigned_to || [];
        if (currentAssigned.includes(memberId)) return;
        const newAssigned = [...currentAssigned, memberId];
        const member = (projectData?.assigned_members || allUsers).find(m => m.id === memberId);
        
        setIsUpdatingTask(true);
        setShowAddMemberDropdown(false);
        const updateData = {
            name: taskData.task.name,
            description: taskData.task.description || '',
            project_id: taskData.task.project_id,
            estimated_hours: taskData.task.estimated_hours,
            priority_level: taskData.task.priority_level || 'medium',
            tags: taskData.task.tags || [],
            due_date: taskData.task.due_date || null,
            status: taskData.task.status || 'To Do',
            assigned_to: newAssigned,
            enable_manual_logging: taskData.task.enable_manual_logging
        };
        // Optimistic update - instant UI
        if (member) {
            queryClient.setQueryData(['taskDetail', taskId], (old) => old ? {
                ...old,
                task: { ...old.task, assigned_to: newAssigned },
                assigned_members: [...(old.assigned_members || []), member]
            } : old);
        }
        try {
            await updateTaskDetailMutation.mutateAsync({ id: taskId, data: updateData });
            toast.success('Member added');
        } catch (error) {
            fetchTaskDetails(); // Rollback on error
            toast.error(error?.response?.data?.detail || 'Failed to add member');
        } finally {
            setIsUpdatingTask(false);
        }
    };

    const handleRemoveMemberFromTask = async (memberId) => {
        if (!taskData?.task || isUpdatingTask) return;
        const currentAssigned = taskData.task.assigned_to || [];
        const newAssigned = currentAssigned.filter(id => id !== memberId);
        if (newAssigned.length === 0) {
            toast.error('At least one member must be assigned');
            return;
        }
        
        setIsUpdatingTask(true);
        const updateData = {
            name: taskData.task.name,
            description: taskData.task.description || '',
            project_id: taskData.task.project_id,
            estimated_hours: taskData.task.estimated_hours,
            priority_level: taskData.task.priority_level || 'medium',
            tags: taskData.task.tags || [],
            due_date: taskData.task.due_date || null,
            status: taskData.task.status || 'To Do',
            assigned_to: newAssigned,
            enable_manual_logging: taskData.task.enable_manual_logging
        };
        // Optimistic update - instant UI
        queryClient.setQueryData(['taskDetail', taskId], (old) => old ? {
            ...old,
            task: { ...old.task, assigned_to: newAssigned },
            assigned_members: (old.assigned_members || []).filter(m => m.id !== memberId)
        } : old);
        try {
            await updateTaskDetailMutation.mutateAsync({ id: taskId, data: updateData });
            toast.success('Member removed');
        } catch (error) {
            fetchTaskDetails(); // Rollback on error
            toast.error(error?.response?.data?.detail || 'Failed to remove member');
        } finally {
            setIsUpdatingTask(false);
        }
    };

    const handleCommentInputChange = (e) => {
        const text = e.target.value;
        setNewComment(text);
        const cursorPos = e.target.selectionStart;
        setCursorPosition(cursorPos);
        // Check for @ mentions
        const beforeCursor = text.substring(0, cursorPos);
        const mentionMatch = beforeCursor.match(/@(\w*)$/);
        if (mentionMatch) {
            setShowMentionSuggestions(true);
            setMentionFilter(mentionMatch[1]);
        } else {
            setShowMentionSuggestions(false);
            setMentionFilter("");
        }
    };

    const handleMentionSelect = (member) => {
        if (!commentInputRef.current) return;
        const beforeMention = newComment.substring(0, cursorPosition - mentionFilter.length - 1);
        const afterMention = newComment.substring(cursorPosition);
        const newText = `${beforeMention}@${member.username} ${afterMention}`;
        setNewComment(newText);
        setShowMentionSuggestions(false);
        setMentionFilter("");
        setTimeout(() => {
            if (commentInputRef.current) {
                commentInputRef.current.focus();
                const newPosition = beforeMention.length + member.username.length + 2;
                commentInputRef.current.setSelectionRange(newPosition, newPosition);
            }
        }, 0);
    };

    const extractMentions = (text) => {
        const mentionRegex = /@([a-zA-Z0-9._-]+)/g;
        const mentions = [];
        let match;
        if (!taskData || !Array.isArray(taskData.assigned_members)) return mentions;
        while ((match = mentionRegex.exec(text)) !== null) {
            const username = match[1];
            const member = taskData.assigned_members.find(m => m.username === username);
            if (member) {
                mentions.push(member.id);
            }
        }
        return mentions;
    };

    const renderCommentWithMentions = (commentText) => {
        if (!commentText) return commentText;
        
        // First, parse the text for links
        const linkParts = parseTextWithLinks(commentText);
        const finalParts = [];
        
        linkParts.forEach((part, partIndex) => {
            if (part.type === 'text') {
                // Process mentions and commit references in text parts
                if (taskData?.assigned_members) {
                    const mentionRegex = /@([a-zA-Z0-9._-]+)/g;
                    const commitRegex = /@commit:([a-f0-9]{7,40})/gi;
                    const textParts = [];
                    let lastIndex = 0;
                    let match;
                    
                    // Process commit references first
                    while ((match = commitRegex.exec(part.content)) !== null) {
                        // Add text before the commit reference
                        if (match.index > lastIndex) {
                            const beforeText = part.content.substring(lastIndex, match.index);
                            if (beforeText) {
                                textParts.push(beforeText);
                            }
                        }
                        
                        const commitSha = match[1];
                        // Style commit references as green badges
                        textParts.push(
                            <span key={`commit-${partIndex}-${match.index}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full font-mono">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                                {commitSha}
                            </span>
                        );
                        
                        lastIndex = match.index + match[0].length;
                    }
                    
                    // Process mentions in remaining text
                    const remainingText = part.content.substring(lastIndex);
                    if (remainingText) {
                        let mentionLastIndex = 0;
                        const mentionMatch = mentionRegex.exec(remainingText);
                        
                        if (mentionMatch) {
                            // Add text before mention
                            if (mentionMatch.index > mentionLastIndex) {
                                const beforeMention = remainingText.substring(mentionLastIndex, mentionMatch.index);
                                if (beforeMention) {
                                    textParts.push(beforeMention);
                                }
                            }
                            
                            const username = mentionMatch[1];
                            const member = taskData.assigned_members.find(m => m.username === username);
                            
                            if (member) {
                                // Simple bold styling for valid mentions
                                textParts.push(
                                    <span key={`mention-${partIndex}-${mentionMatch.index}`} className="font-bold text-blue-600">
                                        @{member.username}
                                    </span>
                                );
                            } else {
                                // Red color for invalid mentions
                                textParts.push(
                                    <span key={`mention-${partIndex}-${mentionMatch.index}`} className="text-red-500">
                                        @{username}
                                    </span>
                                );
                            }
                            
                            // Add remaining text after mention
                            if (mentionMatch.index + mentionMatch[0].length < remainingText.length) {
                                const afterMention = remainingText.substring(mentionMatch.index + mentionMatch[0].length);
                                if (afterMention) {
                                    textParts.push(afterMention);
                                }
                            }
                        } else {
                            textParts.push(remainingText);
                        }
                    }
                    
                    finalParts.push(...textParts);
                } else {
                    finalParts.push(part.content);
                }
            } else if (part.type === 'link') {
                // For special platforms (YouTube, Figma, GitHub, etc.), show full preview
                if (part.isYouTube || part.isDesign || part.isDev || part.isDocument) {
                    finalParts.push(
                        <div key={`link-preview-${partIndex}`} className="my-2">
                            <TextWithLinks text={part.content} showPreviews={true} />
                        </div>
                    );
                } else {
                    // For other links, show inline
                    finalParts.push(
                        <span key={`link-inline-${partIndex}`}>
                            <TextWithLinks text={part.content} showPreviews={false} />
                        </span>
                    );
                }
            }
        });
        
        return finalParts.length > 0 ? finalParts : commentText;
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;



        setAddingComment(true);
        try {
            const mentions = extractMentions(newComment);
            
            // Parse enhanced comment references
            const { parsedComment, references } = parseEnhancedComment(newComment);
            
            // Prepare comment data with GitHub commit if attached
            const commentData = {
                comment: newComment,
                mentions,
                ...(selectedCommit && {
                    github_commit: {
                        sha: selectedCommit.sha,
                        message: selectedCommit.message,
                        author: selectedCommit.author?.name,
                        date: selectedCommit.date,
                        repository: taskData.task.github_integration.repository,
                        branch: taskData.task.github_integration.branch_name
                    }
                }),
                ...(references.length > 0 && {
                    github_references: references
                })
            };

            await axios.post(`/api/tasks/${taskId}/comments`, commentData);
            
            // Reset form and state
            setNewComment('');
            setSelectedCommit(null);
            
            // Refresh data
            fetchTaskDetails();
            fetchActivity(0, false);
            
            toast.success('Comment added successfully');
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to add comment');
        } finally {
            setAddingComment(false);
        }
    };

    // Voice recording handlers
    const handleVoiceNoteSend = async (audioBlob) => {
        setAddingComment(true);
        try {
            const formData = new FormData();
            formData.append('voice_note', audioBlob, 'voice_note.webm');
            
            // Send as multipart form data without comment object
            await axios.post(`/api/tasks/${taskId}/comments`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            // Refresh data
            fetchTaskDetails();
            fetchActivity(0, false);
            
            toast.success('Voice note sent successfully');
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to send voice note');
        } finally {
            setAddingComment(false);
            setShowVoiceRecorder(false);
        }
    };

    const handleVoiceRecorderCancel = () => {
        setShowVoiceRecorder(false);
    };

    const toggleVoiceRecorder = () => {
        setShowVoiceRecorder(true); // Always start recording when mic is clicked
    };

    const handleDeleteComment = (commentId) => {
        setCommentToDelete(commentId);
        setShowDeleteCommentModal(true);
    };

    // Notes functions - now handled by React Query

    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!newNote.trim()) return;

        setAddingNote(true);
        try {
            const noteData = {
                content: newNote,
                note_type: 'task',
                related_id: taskId,
                visibility: noteVisibility
            };

            await axios.post('/api/notes', noteData);
            
            // Reset form
            setNewNote('');
            setNoteVisibility('public');
            
            // Notes will be refreshed automatically by React Query
            
            toast.success('Note added successfully');
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to add note');
        } finally {
            setAddingNote(false);
        }
    };

    const handleEditNote = async (noteId, content) => {
        try {
            setEditingNoteId(noteId); // This serves as loading state for the specific note
            await axios.put(`/api/notes/${noteId}`, { content });
            setEditingNoteId(null);
            setEditingNoteContent('');
            // Notes will be refreshed automatically by React Query
            toast.success('Note updated successfully');
        } catch (error) {
            toast.error('Failed to update note');
            setEditingNoteId(null);
            setEditingNoteContent('');
        }
    };

    const handleDeleteNoteClick = (note) => {
        setNoteToDelete(note);
        setShowDeleteNoteModal(true);
    };

    const handleDeleteNote = async () => {
        if (!noteToDelete) return;
        
        setDeletingNote(true);
        try {
            await axios.delete(`/api/notes/${noteToDelete.id}`);
            // Notes will be refreshed automatically by React Query
            toast.success('Note deleted successfully');
            setShowDeleteNoteModal(false);
            setNoteToDelete(null);
        } catch (error) {
            toast.error('Failed to delete note');
            setShowDeleteNoteModal(false);
            setNoteToDelete(null);
        } finally {
            setDeletingNote(false);
        }
    };

    const handleToggleNoteVisibility = async (noteId, currentVisibility) => {
        const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
        setUpdatingNoteVisibility(noteId);
        try {
            await axios.put(`/api/notes/${noteId}`, { visibility: newVisibility });
            // Notes will be refreshed automatically by React Query
            toast.success(`Note is now ${newVisibility}`);
        } catch (error) {
            toast.error('Failed to update note visibility');
        } finally {
            setUpdatingNoteVisibility(null);
        }
    };



    const confirmDeleteComment = async () => {
        try {
            await axios.delete(`/api/tasks/${taskId}/comments/${commentToDelete}`);
            fetchTaskDetails();
            fetchActivity(0, false);
            toast.success('Comment deleted successfully');
        } catch (error) {
            const errorMessage = error?.response?.data?.detail || 'Failed to delete comment';
            toast.error(errorMessage);
            
            // Show specific message for 24-hour rule
            if (errorMessage.includes('24 hours')) {
                toast.error('You can only delete comments within 24 hours after the task is moved to Done or In QA status');
            }
        } finally {
            setShowDeleteCommentModal(false);
            setCommentToDelete(null);
        }
    };

    const handleEditComment = (commentId, commentText) => {
        setEditingCommentId(commentId);
        setInlineEditText(commentText);
    };

    const handleSaveInlineComment = async (commentId) => {
        try {
            setIsUpdatingComment(true);
            await axios.put(`/api/tasks/${taskId}/comments/${commentId}`, {
                comment: inlineEditText
            });
            fetchTaskDetails();
            fetchActivity(0, false);
            toast.success('Comment updated successfully');
            setEditingCommentId(null);
            setInlineEditText('');
        } catch (error) {
            const errorMessage = error?.response?.data?.detail || 'Failed to update comment';
            toast.error(errorMessage);
            
            // Show specific message for 24-hour rule
            if (errorMessage.includes('24 hours')) {
                toast.error('You can only edit comments within 24 hours after the task is moved to Done or In QA status');
            }
        } finally {
            setIsUpdatingComment(false);
        }
    };

    const handleCancelInlineEdit = () => {
        setEditingCommentId(null);
        setInlineEditText('');
    };

    // Emoji reactions functionality
    const emojiCategories = {
        'recent': ['👍', '❤️', '😂', '😮', '😢', '😡', '🎉', '👏', '🔥', '💯'],
        'smileys': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕', '😟', '🙁', '☹️', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻', '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾'],
        'gestures': ['👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '👊', '✊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳', '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃', '🧠', '🦷', '🦴', '👀', '👁️', '👅', '👄', '💋', '🩸'],
        'people': ['👶', '🧒', '👦', '👧', '🧑', '👨', '👩', '🧓', '👴', '👵', '👤', '👥', '🫂', '👪', '👨‍👩‍👧', '👨‍👩‍👧‍👦', '👨‍👩‍👦‍👦', '👨‍👩‍👧‍👧', '👨‍👨‍👦', '👨‍👨‍👧', '👨‍👨‍👧‍👦', '👨‍👨‍👦‍👦', '👨‍👨‍👧‍👧', '👩‍👩‍👦', '👩‍👩‍👧', '👩‍👩‍👧‍👦', '👩‍👩‍👦‍👦', '👩‍👩‍👧‍👧', '👨‍👦', '👨‍👦‍👦', '👨‍👧', '👨‍👧‍👦', '👨‍👧‍👧', '👩‍👦', '👩‍👦‍👦', '👩‍👧', '👩‍👧‍👦', '👩‍👧‍👧', '🗣️', '👤', '👥', '👣'],
        'animals': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷️', '🕸️', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🦙', '🐐', '🦌', '🐕', '🐩', '🦮', '🐕‍🦺', '🐈', '🐓', '🦃', '🦚', '🦜', '🦢', '🦩', '🕊️', '🐇', '🦝', '🦨', '🦡', '🦦', '🦥', '🐁', '🐀', '🐿️', '🦔'],
        'food': ['🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🫑', '🌽', '🥕', '🫒', '🧄', '🧅', '🥔', '🍠', '🥐', '🥖', '🍞', '🥨', '🥯', '🧀', '🥚', '🍳', '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟', '🍕', '🫓', '🥙', '🌮', '🌯', '🫔', '🥗', '🥘', '🫕', '🥫', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🦪', '🍤', '🍙', '🍚', '🍘', '🍥', '🥠', '🥮', '🍢', '🍡', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿', '🍩', '🍪', '🌰', '🥜', '🍯'],
        'activities': ['⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛷', '⛸️', '🥌', '🎿', '⛷️', '🏂', '🪂', '🏋️‍♀️', '🏋️', '🏋️‍♂️', '🤼‍♀️', '🤼', '🤼‍♂️', '🤸‍♀️', '🤸', '🤸‍♂️', '⛹️‍♀️', '⛹️', '⛹️‍♂️', '🤺', '🤾‍♀️', '🤾', '🤾‍♂️', '🏌️‍♀️', '🏌️', '🏌️‍♂️', '🏇', '🧘‍♀️', '🧘', '🧘‍♂️', '🏄‍♀️', '🏄', '🏄‍♂️', '🏊‍♀️', '🏊', '🏊‍♂️', '🤽‍♀️', '🤽', '🤽‍♂️', '🚣‍♀️', '🚣', '🚣‍♂️', '🧗‍♀️', '🧗', '🧗‍♂️', '🚵‍♀️', '🚵', '🚵‍♂️', '🚴‍♀️', '🚴', '🚴‍♂️', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️', '🏵️', '🎗️', '🎫', '🎟️', '🎪', '🤹', '🤹‍♂️', '🤹‍♀️', '🎭', '🩰', '🎨', '🎬', '🎤', '🎧', '🎼', '🎵', '🎶', '🥁', '🪘', '🎹', '🎷', '🎺', '🎸', '🪕', '🎻', '🎲', '♠️', '♥️', '♦️', '♣️', '♟️', '🃏', '🀄', '🎴', '🎯', '🎳', '🎮', '🕹️', '🎰', '🧩'],
        'travel': ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🛻', '🚚', '🚛', '🚜', '🏍️', '🛵', '🚲', '🛴', '🛹', '🛼', '🚁', '✈️', '🛩️', '🛫', '🛬', '🪂', '💺', '🚀', '🛸', '🚉', '🚞', '🚝', '🚄', '🚅', '🚈', '🚂', '🚆', '🚇', '🚊', '🚍', '🚘', '🚖', '🚡', '🚠', '🚟', '🎢', '🎡', '🎠', '⛵', '🛥️', '🚤', '⛴️', '🛳️', '🚢', '⚓', '🚧', '⛽', '🚏', '🚦', '🚥', '🗺️', '🗿', '🗽', '🗼', '🏰', '🏯', '🏟️', '🎡', '🎢', '🎠', '⛲', '⛱️', '🏖️', '🏝️', '🏔️', '⛰️', '🌋', '🗻', '🏕️', '⛺', '🛖', '🏠', '🏡', '🏘️', '🏚️', '🏗️', '🏭', '🏢', '🏬', '🏣', '🏤', '🏥', '🏦', '🏨', '🏪', '🏫', '🏩', '💒', '🏛️', '⛪', '🕌', '🛕', '🕍', '🕋', '⛩️', '🛤️', '🛣️', '🗾', '🎑', '🏞️', '🌅', '🌄', '🌠', '🎇', '🎆', '🌇', '🌆', '🏙️', '🌃', '🌌', '🌉', '🌁'],
        'objects': ['⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '🕹️', '🗜️', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽️', '🎞️', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙️', '🎚️', '🎛️', '🧭', '⏱️', '⏲️', '⏰', '🕰️', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯️', '🪔', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '💰', '💳', '💎', '⚖️', '🧰', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🔩', '⚙️', '🧱', '⛓️', '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡️', '⚔️', '🛡️', '🚬', '⚰️', '🪦', '⚱️', '🏺', '🔮', '📿', '🧿', '💈', '⚗️', '🔭', '🔬', '🕳️', '🩹', '🩺', '💊', '💉', '🧬', '🦠', '🧫', '🧪', '🌡️', '🧹', '🧺', '🧻', '🚽', '🚰', '🚿', '🛁', '🛀', '🧼', '🪒', '🧽', '🧴', '🛎️', '🔑', '🗝️', '🚪', '🪑', '🛋️', '🛏️', '🛌', '🧸', '🖼️', '🪞', '🪟', '🛍️', '🛒', '🎁', '🎈', '🎏', '🎀', '🎊', '🎉', '🎎', '🏮', '🎐', '🧧', '✉️', '📩', '📨', '📧', '💌', '📥', '📤', '📦', '🏷️', '📪', '📫', '📬', '📭', '📮', '🗳️', '✏️', '✒️', '🖋️', '🖊️', '🖌️', '🖍️', '📝', '💼', '📁', '📂', '🗂️', '📅', '📆', '🗒️', '🗓️', '📇', '📈', '📉', '📊', '📋', '📌', '📍', '📎', '🖇️', '📏', '📐', '✂️', '🗃️', '🗄️', '🗑️', '🔒', '🔓', '🔏', '🔐', '🔑', '🗝️', '🔨', '⛏️', '⚒️', '🛠️', '🗡️', '⚔️', '🔫', '🏹', '🛡️', '🔧', '🔩', '⚙️', '🗜️', '⚖️', '🔗', '⛓️', '🧰', '🧲', '⚗️', '🧪', '🧫', '🧬', '🔬', '🔭', '📡', '💉', '💊', '🩹', '🩺', '🚪', '🛏️', '🛋️', '🚽', '🚿', '🛁', '🧴', '🧷', '🧹', '🧺', '🧻', '🚰', '🧼', '🪒', '🧽', '🛎️', '🔑', '🗝️', '🪑', '🛌', '🧸', '🖼️', '🪞', '🪟', '🛍️', '🛒', '🎁', '🎈', '🎏', '🎀', '🎊', '🎉', '🎎', '🏮', '🎐', '🧧'],
        'symbols': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳', '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️', '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️', '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', '♨️', '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕', '❓', '❔', '‼️', '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️', '🔰', '♻️', '✅', '🈯', '💹', '❇️', '✳️', '❎', '🌐', '💠', 'Ⓜ️', '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🈳', '🈂️', '🛂', '🛃', '🛄', '🛅', '🚹', '🚺', '🚼', '⚧️', '🚻', '🚮', '🎦', '📶', '🈁', '🔣', 'ℹ️', '🔤', '🔡', '🔠', '🆖', '🆗', '🆙', '🆒', '🆕', '🆓', '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟']
    };
    
    const [selectedEmojiCategory, setSelectedEmojiCategory] = useState('smileys');
    
    const handleAddReaction = async (commentId, emoji) => {
        try {
            await axios.post(`/api/tasks/${taskId}/comments/${commentId}/reactions`, {
                emoji: emoji
            });
            // Refresh activity to show updated reactions
            fetchActivity(0, false);
        } catch (error) {
            toast.error('Failed to add reaction');
        }
    };

    const handleRemoveReaction = async (commentId, emoji) => {
        try {
            await axios.delete(`/api/tasks/${taskId}/comments/${commentId}/reactions`, {
                data: { emoji: emoji }
            });
            // Refresh activity to show updated reactions
            fetchActivity(0, false);
        } catch (error) {
            toast.error('Failed to remove reaction');
        }
    };

    const toggleEmojiPicker = (commentId) => {
        setShowEmojiPicker(showEmojiPicker === commentId ? null : commentId);
    };

    const insertEmojiIntoComment = (emoji) => {
        const textarea = commentInputRef.current;
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = newComment;
            const before = text.substring(0, start);
            const after = text.substring(end, text.length);
            const newText = before + emoji + after;
            setNewComment(newText);
            
            // Set cursor position after the emoji
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + emoji.length, start + emoji.length);
            }, 0);
        }
        setShowEmojiPicker(null);
    };

    // Close emoji picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showEmojiPicker && !event.target.closest('.emoji-picker-container')) {
                setShowEmojiPicker(null);
            }
        };

        if (showEmojiPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]);

    const handleAttachmentUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setIsAttachmentUploading(true);
        setAttachmentUploadProgress(0);
        
        // Simulate progress (since we can't track actual upload progress with current setup)
        const progressInterval = setInterval(() => {
            setAttachmentUploadProgress(prev => {
                if (prev >= 90) {
                    clearInterval(progressInterval);
                    return 90;
                }
                return prev + 10;
            });
        }, 100);
        
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`/api/tasks/${taskId}/screenshots`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setAttachmentUploadProgress(100);
            setTimeout(() => {
                setIsAttachmentUploading(false);
                setAttachmentUploadProgress(0);
            }, 500);
            fetchTaskDetails();
            fetchActivity(0, false);
            toast.success('Attachment uploaded successfully');
        } catch (error) {
            clearInterval(progressInterval);
            setIsAttachmentUploading(false);
            setAttachmentUploadProgress(0);
            toast.error(error?.response?.data?.detail || 'Failed to upload attachment');
        }
        setShowScreenshotInput(false);
    };

    const handleCommentAttachmentUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setIsCommentUploading(true);
        setCommentUploadProgress(0);
        
        // Simulate progress (since we can't track actual upload progress with current setup)
        const progressInterval = setInterval(() => {
            setCommentUploadProgress(prev => {
                if (prev >= 90) {
                    clearInterval(progressInterval);
                    return 90;
                }
                return prev + 10;
            });
        }, 100);
        
        const formData = new FormData();
        formData.append('file', file);
        try {
            await axios.post(`/api/tasks/${taskId}/screenshots`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setCommentUploadProgress(100);
            setTimeout(() => {
                setIsCommentUploading(false);
                setCommentUploadProgress(0);
            }, 500);
            fetchTaskDetails();
            fetchActivity(0, false);
            toast.success('Attachment uploaded successfully');
        } catch (error) {
            clearInterval(progressInterval);
            setIsCommentUploading(false);
            setCommentUploadProgress(0);
            toast.error(error?.response?.data?.detail || 'Failed to upload attachment');
        }
    };

    const handleDeleteScreenshot = (screenshotId) => {
        setDeleteModal({ open: true, screenshotId });
    };

    const handleDeleteScreenshotConfirmed = async () => {
        if (!deleteModal.screenshotId) return;
        try {
            await axios.delete(`/api/tasks/${taskId}/screenshots/${deleteModal.screenshotId}`);
            fetchTaskDetails();
            fetchActivity(0, false);
            toast.success('Attachment deleted successfully');
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to delete attachment');
        }
        setDeleteModal({ open: false, screenshotId: null });
    };

    const handleFilePreview = async (file) => {
        setFilePreviewModal({ open: true, file, loading: true });
        
        // Get fresh URL for the file
        try {
            const freshUrl = await getAttachmentUrl(file.filename || file.blob_name);
            setFilePreviewModal({ open: true, file: { ...file, freshUrl: freshUrl || file.url }, loading: false });
        } catch (error) {
            console.error('Failed to get fresh URL for file preview:', error);
            setFilePreviewModal({ open: true, file, loading: false });
        }
    };

    // Function to check if timer can be started (daily hours limit)
    const canStartTimer = async () => {
        try {
            const totalHoursToday = await fetchDailyHours();
            
            if (totalHoursToday >= dailyHoursCap) {
                toast.error(`Daily time limit reached. You have already tracked ${totalHoursToday.toFixed(1)} hours today. Maximum allowed is ${dailyHoursCap} hours per day.`);
                return false;
            }
            
            return true;
        } catch (error) {
            console.error('Error checking if timer can start:', error);
            // If there's an error checking, allow the timer start (backend will handle validation)
            return true;
        }
    };

    const handleStartTimer = async () => {
        // Check if timer can be started
        const canStart = await canStartTimer();
        if (!canStart) {
            return;
        }
        if (!taskData?.task) return;
        setStartingTimer(true);
        try {
            const description = `Working on: ${taskData.task.name}`;
            const success = await startTimer(taskData.task.project_id, description, taskId);
            if (success) {
                // No need to reset elapsedTime here, as it's handled by the useTimer context
            }
        } catch (error) {
            console.error('Error starting timer:', error);
        } finally {
            setStartingTimer(false);
        }
    };

    // Function to fetch daily hours tracked
    const fetchDailyHours = async () => {
        try {
            // Use backend daily-hours endpoint for consistent timezone handling
            const response = await axios.get('/api/time-entries/daily-hours');
            const totalHoursToday = response.data.total_hours;
            
            setDailyHoursTracked(totalHoursToday);
            return totalHoursToday;
        } catch (error) {
            console.error('Error fetching daily hours:', error);
            return 0;
        }
    };

    // Function to check daily hours limit
    const checkDailyHoursLimit = async (newHours, newMinutes) => {
        try {
            const totalHoursToday = await fetchDailyHours();
            
            // Add the new entry hours
            const newEntryHours = newHours + (newMinutes / 60);
            const totalAfterEntry = totalHoursToday + newEntryHours;
            
            if (totalAfterEntry > dailyHoursCap) {
                toast.error(`Daily time limit exceeded. Currently tracking ${totalHoursToday.toFixed(1)} hours today. Adding ${newEntryHours.toFixed(1)} hours would result in ${totalAfterEntry.toFixed(1)} hours total. Maximum allowed is ${dailyHoursCap} hours per day.`);
                return false;
            }
            
            return true;
        } catch (error) {
            console.error('Error checking daily hours:', error);
            // If there's an error checking, allow the entry (backend will handle validation)
            return true;
        }
    };

    const handleLogTime = async (e) => {
        e.preventDefault();
        
        // Validation: Ensure description is provided
        if (!logTime.description.trim()) {
            toast.error('Please provide a description for the time entry');
            return;
        }
        
        if (logTime.description.trim().length < minDescLen) {
            toast.error(`Description must be at least ${minDescLen} characters long`);
            return;
        }
        
        // Validation: Ensure at least one time field is filled
        if ((!logTime.hours || logTime.hours === '0') && (!logTime.minutes || logTime.minutes === '0')) {
            toast.error('Please enter at least some time (hours or minutes)');
            return;
        }

        const hours = parseInt(logTime.hours) || 0;
        const minutes = parseInt(logTime.minutes) || 0;
        const totalMinutes = hours * 60 + minutes;

        if (totalMinutes === 0) {
            toast.error('Please enter a valid time');
            return;
        }

        // Check daily hours limit
        const canTrackTime = await checkDailyHoursLimit(hours, minutes);
        if (!canTrackTime) {
            return;
        }

        setLogTimeLoading(true);
        try {
            const timeEntryData = {
                task_id: taskId,
                hours: hours,
                minutes: minutes,
                description: logTime.description.trim()
            };

            await axios.post('/api/time-entries/manual', timeEntryData);
            toast.success('Time logged successfully');
            setShowLogTimeModal(false);
            setLogTime({ hours: '', minutes: '', description: '' });
            fetchTaskDetails(); // Refresh task data to show updated time
        } catch (error) {
            console.error('Error logging time:', error);
            
            // Handle different types of error responses
            if (error?.response?.data?.detail) {
                const detail = error.response.data.detail;
                
                // Handle array of validation errors
                if (Array.isArray(detail)) {
                    const errorMessages = detail.map(err => {
                        if (err.msg && err.loc) {
                            return `${err.loc.join('.')}: ${err.msg}`;
                        }
                        return err.msg || 'Validation error';
                    }).join(', ');
                    toast.error(errorMessages);
                } else {
                    // Handle single error message
                    toast.error(detail);
                }
            } else if (error?.response?.status === 422) {
                toast.error('Invalid data provided. Please check your input.');
            } else if (error?.response?.status === 403) {
                toast.error('You do not have permission to log time for this task.');
            } else if (error?.response?.status === 404) {
                toast.error('Task not found.');
            } else {
                toast.error('Failed to log time. Please try again.');
            }
        } finally {
            setLogTimeLoading(false);
        }
    };

    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent': return 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200';
            case 'high': return 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200';
            case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200';
            case 'low': return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200';
            default: return 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300';
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'urgent': return <AlertTriangle className="h-4 w-4" />;
            case 'high': return <AlertTriangle className="h-4 w-4" />;
            case 'medium': return <Clock className="h-4 w-4" />;
            case 'low': return <Calendar className="h-4 w-4" />;
            default: return <Clock className="h-4 w-4" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'To Do': return 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-200';
            case 'In Progress': return 'bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200';
            case 'In QA': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200';
            case 'QA': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200';
            case 'Done': return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200';
            default: return 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-200';
        }
    };

    // Helper to get total tracked time for this task
    const getTotalTrackedTime = () => {
        let total = 0;
        
        // Use the total time info from backend if available (includes subtasks)
        if (taskData?.total_time_info) {
            total = taskData.total_time_info.total_time;
        } else if (taskData && taskData.time_entries) {
            // Fallback to old method if total_time_info is not available
            const visibleEntries = taskData.time_entries.filter(entry => !entry.is_deleted);
            total = visibleEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
        }
        
        // If timer is running for THIS task, add live elapsedTime
        if (activeTimer && activeTimer.task_id === taskId && !activeTimer.end_time) {
            total += elapsedTime;
        }
        
        return total;
    };

    // Helper to get time breakdown by member
    const getTimeBreakdownByMember = () => {
        if (!taskData?.time_entries) return [];
        
        
        const memberTimeMap = new Map();
        
        // Process time entries
        taskData.time_entries.forEach(entry => {
            if (entry.is_deleted) return;
            
            const userId = entry.user_id;
            const userName = entry.user_name || 'Unknown User';
            const duration = entry.duration || 0;
            const startTime = entry.start_time;
            const endTime = entry.end_time;
            const description = entry.description || '';
            const isManual = entry.is_manual || false;
            
            if (!memberTimeMap.has(userId)) {
                memberTimeMap.set(userId, {
                    userId,
                    userName,
                    totalTime: 0,
                    entries: []
                });
            }
            
            const memberData = memberTimeMap.get(userId);
            memberData.totalTime += duration;
            memberData.entries.push({
                duration,
                startTime,
                endTime,
                description,
                isManual,
                timestamp: startTime
            });
        });
        
        // Sort entries by timestamp for each member
        memberTimeMap.forEach(memberData => {
            memberData.entries.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });
        
        // Convert to array and sort by total time
        return Array.from(memberTimeMap.values()).sort((a, b) => b.totalTime - a.totalTime);
    };

    const getAvailableStatuses = () => {
        if (!statusesData || !taskData) return [];

        let availableStatuses = statusesData;

        // Check if trying to move to QA and there are subtasks
        if (subtasks.length > 0) {
            // Check if all subtasks are in QA or Done status
            const subtasksNotReady = subtasks.filter(subtask => 
                subtask.status !== 'QA' && subtask.status !== 'Done'
            );
            
            if (subtasksNotReady.length > 0) {
                // Filter out QA status if subtasks aren't ready
                availableStatuses = availableStatuses.filter(status => status.name !== 'QA');
            }
        }

        // If user is admin or project_lead, they can change to any available status
        if (user?.role === 'admin' || user?.role === 'project_lead') {
            return availableStatuses;
        }

        // If user is member, they can only move:
        // - "To Do" → "In Progress"
        // - "In Progress" → "In QA" (only if subtasks are ready)
        if (user?.role === 'member') {
            if (taskData.task.status === 'To Do') {
                return availableStatuses.filter(status => status.name === 'In Progress');
            }
            if (taskData.task.status === 'In Progress') {
                return availableStatuses.filter(status => status.name === 'In QA');
            }
            // Members cannot change status in any other case
            return [];
        }

        return [];
    };



    // Handler to stop timer and refresh task details
    const handleStopTimer = async () => {
        setShowTimerStopModal(true);
    };

    // Function to check daily hours limit for timer stop
    const checkDailyHoursLimitForTimer = async () => {
        try {
            const totalHoursToday = await fetchDailyHours();
            
            // Calculate current timer duration
            if (!activeTimer) {
                return true; // No active timer, allow
            }
            
            const timerStartTime = new Date(activeTimer.start_time);
            const now = new Date();
            const timerDurationMs = now - timerStartTime;
            const currentTimerHours = timerDurationMs / (1000 * 60 * 60); // Convert to hours
            
            const totalAfterTimer = totalHoursToday + currentTimerHours;
            
            if (totalAfterTimer > dailyHoursCap) {
                const remainingHours = Math.max(0, dailyHoursCap - totalHoursToday);
                // Always allow stop — backend saves up to the daily-cap boundary
                toast.warning(
                    `Timer will be capped at ${remainingHours.toFixed(1)}h (saved) to stay within daily limit. You have tracked ${totalHoursToday.toFixed(1)} hours today.`
                );
                return true;
            }
            
            return true;
        } catch (error) {
            console.error('Error checking daily hours for timer:', error);
            // If there's an error checking, allow the timer stop (backend will handle validation)
            return true;
        }
    };

    // Handler to actually stop the timer with description
    const handleConfirmStopTimer = async () => {
        if (!timerStopDescription.trim()) {
            toast.error('Please provide a description for the timer session');
            return;
        }

        if (timerStopDescription.trim().length < minDescLen) {
            toast.error(`Description must be at least ${minDescLen} characters long`);
            return;
        }

        // Check daily hours limit before stopping timer
        const canStopTimer = await checkDailyHoursLimitForTimer();
        if (!canStopTimer) {
            return;
        }

        setTimerStopLoading(true);
        try {
            await stopTimer(timerStopDescription.trim());
            setShowTimerStopModal(false);
            setTimerStopDescription('');
            fetchTaskDetails();
            fetchActivity(0, false);
        } catch (error) {
            console.error('Error stopping timer:', error);
        } finally {
            setTimerStopLoading(false);
        }
    };

    // GitHub Integration Functions
    const handleGitHubCommit = async (taskId) => {
        try {
            const response = await axios.post(`/api/tasks/${taskId}/github-commit`, {
                message: `Task update: ${taskData.task.name}`
            });
            
            if (response.data.success) {
                toast.success('GitHub commit created successfully');
                fetchTaskDetails(); // Refresh to get updated commit info
                fetchActivity(0, false); // Refresh activity section to show the commit creation
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to create GitHub commit');
        }
    };

    const handleCreateGitHubPR = async (taskId) => {
        try {
            const response = await axios.post(`/api/tasks/${taskId}/github-create-pr`);
            
            if (response.data.success) {
                toast.success('GitHub pull request created successfully');
                fetchTaskDetails(); // Refresh to get updated PR info
                fetchActivity(0, false); // Refresh activity section to show the PR creation
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to create GitHub pull request');
        }
    };

    const handleConnectGitHub = async (githubConfig) => {
        try {
            setGithubConnectLoading(true);
            const response = await axios.post(`/api/tasks/${taskId}/github-connect`, githubConfig);
            
            if (response.data.success) {
                toast.success('Task connected to GitHub successfully');
                setShowGitHubConnectModal(false);
                fetchTaskDetails(); // Refresh to show GitHub integration
                fetchActivity(0, false); // Refresh activity section to show the connect activity
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to connect task to GitHub');
        } finally {
            setGithubConnectLoading(false);
        }
    };

    const handleDisconnectGitHub = async () => {
        setGithubDisconnectLoading(true);
        try {
            const response = await axios.delete(`/api/tasks/${taskId}/github-disconnect`);
            
            if (response.data.success) {
                toast.success('Task disconnected from GitHub successfully');
                fetchTaskDetails(); // Refresh to remove GitHub integration
                fetchActivity(0, false); // Refresh activity section to show the disconnect activity
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to disconnect task from GitHub');
        } finally {
            setGithubDisconnectLoading(false);
        }
    };

    const addCollaborator = async () => {
        setAddCollaboratorLoading(true);
        try {
            if (!newCollaborator.username.trim()) {
                toast.error('Please enter a username');
                return;
            }

            const response = await axios.post(`/api/tasks/${taskId}/github-collaborators`, {
                username: newCollaborator.username.trim(),
                permission: newCollaborator.permission
            });

            if (response.data.success) {
                toast.success('Collaborator added successfully');
                setNewCollaborator({ username: '', permission: '' });
                setShowAddCollaboratorModal(false);
                fetchTaskDetails(); // Refresh task data
                fetchActivity(0, false); // Refresh activity section to show the collaborator addition
            }
        } catch (error) {
            console.error('Error adding collaborator:', error);
            toast.error(error.response?.data?.detail || 'Failed to add collaborator');
        } finally {
            setAddCollaboratorLoading(false);
        }
    };

    const removeCollaborator = async (index) => {
        try {
            const collaborator = taskData.task.github_integration.collaborators[index];
            const response = await axios.delete(`/api/tasks/${taskId}/github-collaborators/${collaborator.username}`);

            if (response.data.success) {
                toast.success('Collaborator removed successfully');
                fetchTaskDetails(); // Refresh task data
                fetchActivity(0, false); // Refresh activity section to show the collaborator removal
            }
        } catch (error) {
            console.error('Error removing collaborator:', error);
            toast.error(error.response?.data?.detail || 'Failed to remove collaborator');
        }
    };

    // Handle back navigation
    const handleBack = () => {
        if (onBack) {
            // Use the provided onBack function if available
            onBack();
        } else {
            // Fallback to browser history or default navigation
            if (window.history.length > 1) {
                navigate(-1); // Go back in browser history
            } else {
                // If no history, go to tasks list
                navigate('/tasks');
            }
        }
    };

    const refreshGitHubRepositories = async () => {
        try {
            const response = await axios.get('/api/github/repositories');
            if (response.data.repositories) {
                // Update user object with repositories
                const updatedUser = { ...user, github_repositories: response.data.repositories };
                // You might need to update this in your auth context
                // For now, we'll just update the local state
                setUser(updatedUser);
                toast.success(`Refreshed ${response.data.repositories.length} repositories`);
            }
        } catch (error) {
            toast.error('Failed to refresh repositories: ' + (error.response?.data?.detail || error.message));
        }
    };

    const fetchGitHubOrganizations = async () => {
        try {
            const response = await axios.get('/api/github/organizations');
            if (response.data.organizations) {
                // Update user object with organizations
                const updatedUser = { ...user, github_organizations: response.data.organizations };
                setUser(updatedUser);
            }
        } catch (error) {
            // Don't show error for organizations, just log it
        }
    };

    const fetchRepositoryCommits = async () => {
        if (!taskData.task.github_integration?.repository) return;
        
        setCommitsLoading(true);
        try {
            const response = await axios.get(`/api/github/repositories/${taskData.task.github_integration.repository.replace('/', '%2F')}/commits`);
            if (response.data.commits) {
                let commitsData = response.data.commits;
                
                // Filter commits to show only those created by the current user
                if (user?.github_username) {
                    commitsData = commitsData.filter(commit => 
                        commit.author?.name === user.github_username ||
                        commit.author?.login === user.github_username ||
                        commit.author?.email === user.email
                    );
                }
                
                setCommits(commitsData);
                
                // Extract unique branches and authors for filtering
                const branches = [...new Set(commitsData.map(c => c.branch || 'main'))];
                const authors = [...new Set(commitsData.map(c => c.author?.name).filter(Boolean))];
                
                setAvailableBranches(branches);
                setAvailableAuthors(authors);
                
                // Set default branch
                if (branches.length > 0 && !selectedBranch) {
                    setSelectedBranch(branches[0]);
                }
                
                // Apply initial filtering
                applyCommitFilters(commitsData, commitSearchQuery, commitFilter, selectedBranch, selectedAuthor);
            }
        } catch (error) {
            console.error('Error fetching commits:', error);
            toast.error('Failed to fetch repository commits');
        } finally {
            setCommitsLoading(false);
        }
    };

    const applyCommitFilters = (commitsData, searchQuery, filter, branch, author) => {
        let filtered = [...commitsData];
        
        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(commit => 
                commit.message.toLowerCase().includes(query) ||
                commit.sha.toLowerCase().includes(query) ||
                commit.author?.name?.toLowerCase().includes(query) ||
                commit.branch?.toLowerCase().includes(query)
            );
        }
        
        // Apply type filter
        switch (filter) {
            case 'recent':
                filtered = filtered.slice(0, 20); // Show only last 20 commits
                break;
            case 'branch':
                if (branch) {
                    filtered = filtered.filter(c => c.branch === branch);
                }
                break;
            case 'author':
                if (author) {
                    filtered = filtered.filter(c => c.author?.name === author);
                }
                break;
            default:
                break;
        }
        
        setFilteredCommits(filtered);
    };

    const handleCommitSearch = (query) => {
        setCommitSearchQuery(query);
        applyCommitFilters(commits, query, commitFilter, selectedBranch, selectedAuthor);
    };

    const handleCommitFilterChange = (newFilter) => {
        setCommitFilter(newFilter);
        applyCommitFilters(commits, commitSearchQuery, newFilter, selectedBranch, selectedAuthor);
    };

    const handleBranchChange = (branch) => {
        setSelectedBranch(branch);
        applyCommitFilters(commits, commitSearchQuery, commitFilter, branch, selectedAuthor);
    };

    const handleAuthorChange = (author) => {
        setSelectedAuthor(author);
        applyCommitFilters(commits, commitSearchQuery, commitFilter, selectedBranch, author);
    };

    const handleEnhancedCommentSearch = async (type, query) => {
        if (!query.trim() || !taskData.task.github_integration) return;
        
        setEnhancedCommentLoading(true);
        try {
            let endpoint = '';
            switch (type) {
                case 'commit':
                    endpoint = `/api/github/repositories/${taskData.task.github_integration.repository.replace('/', '%2F')}/commits?search=${encodeURIComponent(query)}`;
                    break;
                case 'issue':
                    endpoint = `/api/github/repositories/${taskData.task.github_integration.repository.replace('/', '%2F')}/issues?search=${encodeURIComponent(query)}`;
                    break;
                case 'pr':
                    endpoint = `/api/github/repositories/${taskData.task.github_integration.repository.replace('/', '%2F')}/pulls?search=${encodeURIComponent(query)}`;
                    break;
                case 'file':
                    endpoint = `/api/github/repositories/${taskData.task.github_integration.repository.replace('/', '%2F')}/files?search=${encodeURIComponent(query)}`;
                    break;
                default:
                    return;
            }
            
            const response = await axios.get(endpoint);
            setEnhancedCommentResults(response.data.results || []);
        } catch (error) {
            console.error(`Error searching ${type}:`, error);
            toast.error(`Failed to search ${type}s`);
        } finally {
            setEnhancedCommentLoading(false);
        }
    };

    const handleEnhancedCommentSelect = (item, type) => {
        let reference = '';
        switch (type) {
            case 'commit':
                reference = `@commit:${item.sha.substring(0, 7)}`;
                break;
            case 'issue':
                reference = `@issue:#${item.number}`;
                break;
            case 'pr':
                reference = `@pr:#${item.number}`;
                break;
            case 'file':
                reference = `@file:${item.path}`;
                break;
        }
        
        setNewComment(prev => prev + (prev ? ' ' : '') + reference);
        setShowEnhancedCommentModal(false);
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} reference added to comment`);
    };

    const parseEnhancedComment = (comment) => {
        const patterns = {
            commit: /@commit:([a-f0-9]{7,40})/gi,
            issue: /@issue:#(\d+)/gi,
            pr: /@pr:#(\d+)/gi,
            file: /@file:([^\s]+)/gi
        };
        
        let parsedComment = comment;
        let references = [];
        
        Object.entries(patterns).forEach(([type, pattern]) => {
            const matches = comment.match(pattern);
            if (matches) {
                matches.forEach(match => {
                    const value = match.split(':')[1];
                    references.push({ type, value, original: match });
                });
            }
        });
        
        return { parsedComment, references };
    };

    const fetchGitHubContext = async (reference) => {
        if (!taskData.task.github_integration || !reference) return;
        
        const cacheKey = `${reference.type}:${reference.value}`;
        if (referenceContext[cacheKey]) return; // Already cached
        
        setContextLoading(true);
        try {
            let endpoint = '';
            switch (reference.type) {
                case 'commit':
                    endpoint = `/api/github/repositories/${taskData.task.github_integration.repository.replace('/', '%2F')}/commits/${reference.value}`;
                    break;
                case 'issue':
                    endpoint = `/api/github/repositories/${taskData.task.github_integration.repository.replace('/', '%2F')}/issues/${reference.value}`;
                    break;
                case 'pr':
                    endpoint = `/api/github/repositories/${taskData.task.github_integration.repository.replace('/', '%2F')}/pulls/${reference.value}`;
                    break;
                case 'file':
                    endpoint = `/api/github/repositories/${taskData.task.github_integration.repository.replace('/', '%2F')}/files/${encodeURIComponent(reference.value)}`;
                    break;
                default:
                    return;
            }
            
            const response = await axios.get(endpoint);
            setReferenceContext(prev => ({
                ...prev,
                [cacheKey]: response.data
            }));
        } catch (error) {
            console.error(`Error fetching ${reference.type} context:`, error);
            // Don't show error toast for context fetching
        } finally {
            setContextLoading(false);
        }
    };

    const handleReferenceHover = (reference) => {
        // Clear existing timeout
        if (contextHoverTimeout) {
            clearTimeout(contextHoverTimeout);
        }
        
        // Set new timeout for hover
        const timeout = setTimeout(() => {
            setHoveredReference(reference);
            fetchGitHubContext(reference);
        }, 500); // 500ms delay before showing context
        
        setContextHoverTimeout(timeout);
    };

    const handleReferenceLeave = () => {
        if (contextHoverTimeout) {
            clearTimeout(contextHoverTimeout);
        }
        setHoveredReference(null);
    };

    const getSmartCommitSuggestions = () => {
        if (!commits.length) return [];
        
        const suggestions = [];
        
        // Recent commits by current user
        const userCommits = commits
            .filter(c => c.author?.name === user?.full_name || c.author?.name === user?.username)
            .slice(0, 3);
        if (userCommits.length > 0) {
            suggestions.push({
                category: 'Your Recent Commits',
                commits: userCommits
            });
        }
        
        // Recent commits on current branch
        const branchCommits = commits
            .filter(c => c.branch === taskData.task.github_integration?.branch_name)
            .slice(0, 3);
        if (branchCommits.length > 0) {
            suggestions.push({
                category: `Recent on ${taskData.task.github_integration?.branch_name}`,
                commits: branchCommits
            });
        }
        
        // Commits with similar patterns (based on task name/keywords)
        const taskKeywords = taskData.task.name.toLowerCase().split(' ').filter(w => w.length > 3);
        const relevantCommits = commits
            .filter(c => taskKeywords.some(keyword => c.message.toLowerCase().includes(keyword)))
            .slice(0, 3);
        if (relevantCommits.length > 0) {
            suggestions.push({
                category: 'Related to Task',
                commits: relevantCommits
            });
        }
        
        return suggestions;
    };

    const handleSmartCommitSuggestion = (commit) => {
        handleCommitSelect(commit);
    };

    const handleCommitSelect = (commit) => {
        setSelectedCommit(commit);
        setShowGitHubCommitModal(false);
        
        // Add commit reference to comment
        const commitRef = `@commit:${commit.sha.substring(0, 7)}`;
        setNewComment(prev => prev + (prev ? ' ' : '') + commitRef);
    };

    const handleCreateTimeEntryFromCommit = async (commit) => {
        try {
            // Set the selected commit and open time entry modal
            setSelectedCommitForTimeEntry(commit);
            setShowTimeEntryModal(true);
            setShowGitHubCommitModal(false);
        } catch (error) {
            toast.error('Error preparing time entry: ' + error.message);
        }
    };

    const handleSubmitTimeEntry = async (description, durationMinutes) => {
        try {
            if (!selectedCommitForTimeEntry) {
                toast.error('No commit selected for time entry');
                return;
            }

            // Create time entry with commit data
            const timeEntryData = {
                description,
                duration_minutes: durationMinutes,
                github_commit: {
                    sha: selectedCommitForTimeEntry.sha,
                    message: selectedCommitForTimeEntry.message,
                    author: selectedCommitForTimeEntry.author?.name || 'Unknown',
                    date: selectedCommitForTimeEntry.date,
                    branch: selectedCommitForTimeEntry.branch || taskData.task.github_integration.branch_name,
                    repository: taskData.task.github_integration.repository
                }
            };
            
            const response = await timeTrackingService.createTimeEntryFromCommit(
                taskId, 
                timeEntryData.github_commit, 
                timeEntryData.description, 
                timeEntryData.duration_minutes
            );
            
            toast.success('Time entry created successfully with commit link');
            // Refresh activity to show the new time entry
            fetchActivity(0, false);
            // Close the modal and reset
            setShowTimeEntryModal(false);
            setSelectedCommitForTimeEntry(null);
        } catch (error) {
            toast.error('Error creating time entry: ' + (error.response?.data?.detail || error.message));
        }
    };

    const fetchGitHubRepositories = async () => {
        try {
            setRepositoriesLoading(true);
            const response = await axios.get('/api/github/repositories');
            if (response.data.repositories) {
                // Update user object with repositories
                const updatedUser = { ...user, github_repositories: response.data.repositories };
                setUser(updatedUser);
            }
        } catch (error) {
            toast.error('Failed to refresh repositories: ' + (error.response?.data?.detail || error.message));
        } finally {
            setRepositoriesLoading(false);
        }
    };

    const fetchGitHubConnectionStatus = async () => {
        try {
            const response = await axios.get('/api/github/status');
            setGithubConnectionStatus(response.data);
        } catch (error) {
            setGithubConnectionStatus({ connected: false });
        }
    };

    const createNewRepository = async () => {
        try {
            const repoName = document.getElementById('new-repo-name').value;
            const owner = document.getElementById('new-repo-owner').value;
            const isPrivate = document.getElementById('new-repo-private').checked;
            const autoInit = document.getElementById('new-repo-auto-init').checked;

            if (!repoName.trim()) {
                toast.error('Please enter a repository name');
                return;
            }

            const response = await axios.post('/api/github/create-repository', {
                name: repoName.trim(),
                owner: owner || undefined,
                private: isPrivate,
                auto_init: autoInit
            });

            if (response.data.success) {
                toast.success('New repository created successfully');
                // Set the new repository as selected
                document.getElementById('github-repo-input').value = response.data.repository.full_name;
                // Refresh the repositories list
                await refreshGitHubRepositories();
                // Close the modal
                setShowGitHubConnectModal(false);
                // Refresh task details to show the new integration
                fetchTaskDetails();
                // Refresh activity section to show the repository creation
                fetchActivity(0, false);
            } else {
                toast.error('Failed to create repository: ' + response.data.message);
            }
        } catch (error) {
            toast.error('Error creating repository: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleRepositorySelectChange = (event) => {
        const value = event.target.value;
        if (value === '__create_new__') {
            setShowCreateRepoForm(true);
        } else {
            setShowCreateRepoForm(false);
        }
    };

    const handleSelectFocus = (event) => {
        // Force the select dropdown to stay within modal bounds
        const select = event.target;
        const modal = select.closest('.modal-backdrop-full');
        if (modal) {
            modal.style.overflow = 'hidden';
            modal.style.zIndex = '9999';
        }
    };

    const handleSelectBlur = (event) => {
        // Reset modal overflow when select loses focus
        const select = event.target;
        const modal = select.closest('.modal-backdrop-full');
        if (modal) {
            modal.style.overflow = 'hidden';
            modal.style.zIndex = '50';
        }
    };

    const handleRepositorySelectClick = async () => {
        // Load repositories when dropdown is clicked
        if (githubConnectionStatus?.connected && (!user?.github_repositories || user.github_repositories.length === 0)) {
            await fetchGitHubRepositories();
        }
    };

    if (loading || !taskData || !taskData.task || !user) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-secondary-600">Loading task details...</p>
                </div>
            </div>
        );
    }

    // --- ClickUp-style header ---
    return (
        <div className="space-y-4">
            {/* Header with Breadcrumbs */}
            <div className="flex items-center gap-4 mb-4">
                <div className="flex gap-2">
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-50 hover:bg-primary-100 text-primary-700 font-medium shadow-sm border border-primary-200 transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    {canEditTask && (
                        <button
                            onClick={() => setShowDuplicateModal(true)}
                            className="flex items-center justify-center p-2 rounded-lg text-secondary-400 hover:text-primary-600 hover:bg-primary-50/80 border border-secondary-100 hover:border-primary-200 transition-all duration-200 shadow-sm"
                            title="Copy task"
                        >
                            <CopyPlus className="h-5 w-5" />
                        </button>
                    )}
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm text-secondary-600 mb-1">
                        <button 
                            onClick={() => navigate('/projects')}
                            className="hover:text-primary-600 hover:underline cursor-pointer transition-colors"
                        >
                            Projects
                        </button>
                        <span>/</span>
                        <button 
                            onClick={() => navigate(`/projects/${taskData.project?.id}`)}
                            className="hover:text-primary-600 hover:underline cursor-pointer transition-colors"
                        >
                            {taskData.project?.name}
                        </button>
                        <span>/</span>
                        <button 
                            onClick={() => navigate('/tasks')}
                            className="hover:text-primary-600 hover:underline cursor-pointer transition-colors"
                        >
                            Tasks
                        </button>
                        <span>/</span>
                        <span className="text-secondary-900 font-medium">{taskData?.task?.name || 'Task'}</span>
                    </div>
                    <h1 className="text-xl font-bold text-secondary-900">{taskData.task.name}</h1>
                </div>
            </div>

            {/* ClickUp-style status/timer row */}
            <div className="flex flex-wrap items-center gap-4 mb-3">
                {/* Status Dropdown */}
                <div className="relative">
                    <button
                        className={`flex items-center gap-2 px-3 py-1 rounded-full font-medium text-sm border ${showStatusDropdown ? 'border-primary-600' : 'border-secondary-200'} shadow-sm transition-colors ${statusesData?.find(s => s.name === taskData.task.status)?.color || 'bg-gray-100 text-gray-800'} ${getAvailableStatuses().length === 0 ? 'opacity-50 cursor-not-allowed' : ''} ${isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => getAvailableStatuses().length > 0 && !isUpdatingStatus && setShowStatusDropdown(v => !v)}
                        disabled={getAvailableStatuses().length === 0 || isUpdatingStatus}
                    >
                        {isUpdatingStatus ? (
                            <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                                Updating...
                            </>
                        ) : (
                            <>
                                {taskData.task.status || 'To Do'}
                                <ChevronDown className="h-4 w-4" />
                            </>
                        )}
                    </button>
                    {showStatusDropdown && (
                        <div className="absolute z-10 mt-2 w-48 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg">
                            {getAvailableStatuses().map(status => (
                                <button
                                    key={status.id}
                                    className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-primary-50 ${status.color} ${isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={() => !isUpdatingStatus && handleStatusChange(status.name)}
                                    disabled={isUpdatingStatus}
                                >
                                    {isUpdatingStatus && status.name === taskData.task.status ? (
                                        <>
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                                            Updating...
                                        </>
                                    ) : (
                                        status.name
                                    )}
                                </button>
                            ))}
                            
                            {/* Show message if QA is disabled due to subtasks */}
                            {subtasks.length > 0 && statusesData?.find(s => s.name === 'QA') && 
                             !getAvailableStatuses().find(s => s.name === 'QA') && (
                                <div className="px-4 py-2 text-xs text-secondary-500 border-t border-secondary-100 bg-secondary-50">
                                    <div className="font-medium mb-1">QA Status Disabled</div>
                                    <div>Complete all subtasks first</div>
                                </div>
                            )}
                            
                           
                        </div>
                    )}
                </div>
                {/* Timer & Log Time with Total Time in One Row */}
                {taskData.task.assigned_to && taskData.task.assigned_to.includes(user?.id) && (
                    <div className="flex items-center gap-3">
                        {activeTimer && activeTimer.task_id === taskId && !activeTimer.end_time ? (
                            <button
                                onClick={handleStopTimer}
                                disabled={stoppingTimer || (taskData.task.status === 'Done' && (user?.role === 'member'))}
                                className={`flex items-center gap-2 px-4 py-2 text-sm text-white rounded shadow ${
                                    stoppingTimer 
                                        ? 'bg-red-500 cursor-not-allowed' 
                                        : 'bg-red-600 hover:bg-red-700'
                                }`}
                            >
                                {stoppingTimer ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Stopping Timer...
                                    </>
                                ) : (
                                    <>
                                        <Square className="h-4 w-4" />
                                        Stop Timer
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={handleStartTimer}
                                disabled={startingTimer || (taskData.task.status === 'Done' && (user?.role === 'member')) || (!!activeTimer && activeTimer.task_id !== taskId && !activeTimer.end_time)}
                                className={`flex items-center gap-2 px-4 py-2 text-sm text-white rounded shadow ${
                                    startingTimer 
                                        ? 'bg-green-500 cursor-not-allowed' 
                                        : 'bg-green-600 hover:bg-green-700'
                                }`}
                            >
                                {startingTimer ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Starting Timer...
                                    </>
                                ) : (
                                    <>
                                        <Play className="h-4 w-4" />
                                        Start Timer
                                    </>
                                )}
                            </button>
                        )}
                        {isManualLoggingEnabled() && (
                            <button
                                onClick={() => setShowLogTimeModal(true)}
                                className="btn-secondary flex items-center gap-2 px-4 py-2 text-sm"
                                disabled={taskData.task.status === 'Done' && (user?.role === 'member')}
                            >
                                <Plus className="h-4 w-4" />
                                Log Time
                            </button>
                        )}
                    </div>
                )}
                
                {/* Total Time Display - Show to all users (admin, project_lead, and assigned members) */}
                <button
                    onClick={() => setShowTimeBreakdownModal(true)}
                    className="bg-gradient-to-r from-primary-50 via-blue-50 to-indigo-50 dark:from-secondary-700 dark:via-secondary-700 dark:to-secondary-700 border border-primary-200 dark:border-secondary-600 rounded-xl px-4 py-2 shadow-sm hover:from-primary-100 hover:via-blue-100 hover:to-indigo-100 dark:hover:from-secondary-600 dark:hover:via-secondary-600 dark:hover:to-secondary-600 hover:border-primary-300 dark:hover:border-secondary-500 hover:shadow-lg transition-all duration-300 cursor-pointer group focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 transform hover:scale-[1.02]"
                    title="Click to view detailed time breakdown by member"
                >
                    <div className="flex items-center gap-3 text-primary-700 dark:text-secondary-100">
                        <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-primary-600 dark:text-secondary-300 group-hover:scale-110 transition-transform duration-300" />
                            <span className="text-sm font-medium text-primary-600 dark:text-secondary-300 group-hover:text-primary-700 dark:group-hover:text-secondary-200 transition-colors">
                                Total Tracked Time:
                            </span>
                        </div>
                        <div className="text-lg font-mono font-bold text-primary-800 dark:text-white group-hover:text-primary-900 dark:group-hover:text-white transition-colors">
                            {formatTime(getTotalTrackedTime())}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-primary-500 dark:text-secondary-400 font-medium whitespace-nowrap">View Details</span>
                            <div className="w-2 h-2 bg-primary-400 dark:bg-secondary-400 rounded-full group-hover:bg-primary-500 dark:group-hover:bg-secondary-300 transition-colors"></div>
                        </div>
                    </div>
                </button>
                
                {/* Live Timer Display (when active for this task) - Only show to assigned users */}
                {activeTimer && activeTimer.task_id === taskId && !activeTimer.end_time && taskData.task.assigned_to && taskData.task.assigned_to.includes(user?.id) && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg px-3 py-2 shadow-sm animate-pulse">
                        <div className="flex items-center gap-2 text-green-700">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                            <span className="text-sm font-medium">Live Timer:</span>
                            <span className="text-lg font-mono font-bold">{formatTime(elapsedTime)}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Log Time Modal */}
            {showLogTimeModal && (
                <div className="modal-backdrop-full flex items-center justify-center p-4" onClick={() => setShowLogTimeModal(false)}>
                    <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 scale-100 animate-in fade-in-0 zoom-in-95" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100 dark:border-secondary-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-primary-100 rounded-lg">
                                    <Clock className="h-5 w-5 text-primary-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Log Time</h3>
                                    <p className="text-sm text-gray-500 dark:text-secondary-400">Record time spent on this task</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowLogTimeModal(false)}
                                className="p-2 text-gray-400 dark:text-secondary-400 hover:text-gray-600 dark:hover:text-secondary-200 hover:bg-gray-100 dark:hover:bg-secondary-700 rounded-lg transition-colors duration-200"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleLogTime} className="p-6 pt-4">
                            <div className="space-y-5">
                                {/* Time Inputs */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-secondary-300 mb-3">Time Spent</label>
                                    <div className="flex gap-3">
                                        <div className="flex-1">
                                            <div className="relative group">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="input-field w-full text-center text-lg font-medium pr-8 transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 group-hover:border-primary-300"
                                                    placeholder="0"
                                                    value={logTime.hours}
                                                    onChange={e => setLogTime(l => ({ ...l, hours: e.target.value }))}
                                                />
                                                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 font-medium">hr</span>
                                            </div>
                                            <p className="text-xs text-gray-500 text-center mt-1">Hours</p>
                                        </div>
                                        <div className="flex items-center">
                                            <span className="text-2xl text-gray-300 dark:text-secondary-500 font-light">:</span>
                                        </div>
                                        <div className="flex-1">
                                            <div className="relative group">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="59"
                                                    className="input-field w-full text-center text-lg font-medium pr-8 transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 group-hover:border-primary-300"
                                                    placeholder="0"
                                                    value={logTime.minutes}
                                                    onChange={e => setLogTime(l => ({ ...l, minutes: e.target.value }))}
                                                />
                                                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 font-medium">min</span>
                                            </div>
                                            <p className="text-xs text-gray-500 text-center mt-1">Minutes</p>
                                        </div>
                                    </div>
                                    
                                    {/* Quick Time Suggestions */}
                                    <div className="mt-3">
                                        <p className="text-xs text-gray-500 mb-2">Quick select:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {[
                                                { label: '15m', hours: 0, minutes: 15 },
                                                { label: '30m', hours: 0, minutes: 30 },
                                                { label: '1h', hours: 1, minutes: 0 },
                                                { label: '2h', hours: 2, minutes: 0 },
                                                { label: '4h', hours: 4, minutes: 0 },
                                                { label: `${dailyHoursCap}h`, hours: dailyHoursCap, minutes: 0 }
                                            ].map((preset) => (
                                                <button
                                                    key={preset.label}
                                                    type="button"
                                                    onClick={() => setLogTime({ hours: preset.hours.toString(), minutes: preset.minutes.toString(), description: logTime.description })}
                                                    className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-secondary-300 bg-gray-100 dark:bg-secondary-700 hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:text-primary-700 dark:hover:text-primary-400 rounded-lg transition-all duration-200 hover:scale-105"
                                                >
                                                    {preset.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Daily Hours Tracking Display */}
                                    <div className="mt-4 bg-gray-50 dark:bg-secondary-700 rounded-lg p-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700">Hours tracked today:</span>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-lg font-semibold ${dailyHoursTracked >= dailyHoursCap ? 'text-red-600' : dailyHoursTracked >= dailyHoursCap * 0.6 ? 'text-yellow-600' : 'text-green-600'}`}>
                                                    {dailyHoursTracked.toFixed(1)}h
                                                </span>
                                                <span className="text-xs text-gray-500">/ {dailyHoursCap}h</span>
                                            </div>
                                        </div>
                                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className={`h-2 rounded-full transition-all duration-300 ${dailyHoursTracked >= dailyHoursCap ? 'bg-red-500' : dailyHoursTracked >= dailyHoursCap * 0.6 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                                style={{ width: `${Math.min((dailyHoursTracked / dailyHoursCap) * 100, 100)}%` }}
                                            ></div>
                                        </div>
                                        {dailyHoursTracked >= dailyHoursCap ? (
                                            <p className="text-xs text-red-600 mt-1 font-medium">Daily limit reached! Cannot start timer.</p>
                                        ) : (
                                            <p className="text-xs text-gray-600 mt-1">
                                                Remaining: {(dailyHoursCap - dailyHoursTracked).toFixed(1)}h available
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description <span className="text-red-500 text-xs font-normal">*</span>
                                        <span className="text-xs text-gray-500 font-normal ml-1">(min. {minDescLen} characters)</span>
                                    </label>
                                    <textarea
                                        className="input-field w-full resize-none transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-primary-300"
                                        placeholder="What did you work on? Please provide detailed description of your work..."
                                        rows={3}
                                        value={logTime.description}
                                        onChange={e => setLogTime(l => ({ ...l, description: e.target.value }))}
                                        required
                                    />
                                    <div className="flex justify-between items-center mt-1">
                                        <span className={`text-xs font-medium ${logTime.description.length < minDescLen ? 'text-red-500' : 'text-green-600'}`}>
                                            {logTime.description.length}/{minDescLen}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-4 border-t border-gray-100 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowLogTimeModal(false)}
                                    className="flex-1 btn-secondary py-3 px-4 text-sm font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="flex-1 btn-primary py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={logTimeLoading || ((!logTime.hours || logTime.hours === '0') && (!logTime.minutes || logTime.minutes === '0') || !logTime.description.trim() || logTime.description.trim().length < minDescLen)}
                                >
                                    {logTimeLoading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Logging...
                                        </>
                                    ) : (
                                        <>
                                            <Clock className="h-4 w-4" />
                                            Log Time
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Timer Stop Description Modal */}
            {showTimerStopModal && (
                <div className="modal-backdrop-full flex items-center justify-center p-4" onClick={() => setShowTimerStopModal(false)}>
                    <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 scale-100 animate-in fade-in-0 zoom-in-95" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100 dark:border-secondary-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-red-100 rounded-lg">
                                    <Square className="h-5 w-5 text-red-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Stop Timer</h3>
                                    <p className="text-sm text-gray-500">Add a description for your work session</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowTimerStopModal(false)}
                                className="p-2 text-gray-400 dark:text-secondary-400 hover:text-gray-600 dark:hover:text-secondary-200 hover:bg-gray-100 dark:hover:bg-secondary-700 rounded-lg transition-colors duration-200"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Form */}
                        <div className="p-6 pt-4">
                            <div className="space-y-5">
                                {/* Timer Duration Display */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-700">Session Duration:</span>
                                        <span className="text-lg font-semibold text-gray-900">
                                            {activeTimer && formatTime(elapsedTime)}
                                        </span>
                                    </div>
                                    {/* Show warning if timer will be capped */}
                                    {activeTimer && (() => {
                                        const currentTimerHours = elapsedTime / 3600;
                                        const totalAfterTimer = dailyHoursTracked + currentTimerHours;
                                        
                                        if (totalAfterTimer > dailyHoursCap) {
                                            const remainingHours = Math.max(0, dailyHoursCap - dailyHoursTracked);
                                            return (
                                                <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                                                    ⚠️ Timer will be capped at {remainingHours.toFixed(1)}h (saved) to stay within daily limit
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description <span className="text-red-500 text-xs font-normal">*</span>
                                        <span className="text-xs text-gray-500 font-normal ml-1">(min. {minDescLen} characters)</span>
                                    </label>
                                    <textarea
                                        className="input-field w-full resize-none transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-primary-300"
                                        placeholder="What did you work on during this session? Please provide detailed description of your work..."
                                        rows={3}
                                        value={timerStopDescription}
                                        onChange={e => setTimerStopDescription(e.target.value)}
                                        required
                                    />
                                    <div className="flex justify-between items-center mt-1">
                                        <span className={`text-xs font-medium ${timerStopDescription.length < minDescLen ? 'text-red-500' : 'text-green-600'}`}>
                                            {timerStopDescription.length}/{minDescLen}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-4 border-t border-gray-100 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowTimerStopModal(false)}
                                    className="flex-1 btn-secondary py-3 px-4 text-sm font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button"
                                    onClick={handleConfirmStopTimer}
                                    className="flex-1 btn-primary py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={timerStopLoading || !timerStopDescription.trim() || timerStopDescription.trim().length < minDescLen}
                                >
                                    {timerStopLoading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Stopping...
                                        </>
                                    ) : (
                                        <>
                                            <Square className="h-4 w-4" />
                                            Stop Timer
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Subtask Drawer */}
            <FormDrawer
                open={showSubtaskModal}
                onClose={() => {
                    setShowSubtaskModal(false);
                    setEditingSubtaskId(null);
                }}
                title={editingSubtaskId ? 'Edit Subtask' : 'Create Subtask'}
                subtitle={editingSubtaskId ? 'Update the subtask details.' : 'Break this task down into a smaller unit of work.'}
                icon={<CheckSquare className="h-5 w-5" />}
                iconColor="violet"
                footer={(
                    <>
                        <button
                            type="button"
                            onClick={() => {
                                setShowSubtaskModal(false);
                                setEditingSubtaskId(null);
                            }}
                            className="btn-secondary"
                            disabled={isCreatingSubtask}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="subtask-form"
                            className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={isCreatingSubtask}
                        >
                            {isCreatingSubtask ? (editingSubtaskId ? 'Updating…' : 'Creating…') : (editingSubtaskId ? 'Update Subtask' : 'Create Subtask')}
                        </button>
                    </>
                )}
            >
                        <form id="subtask-form" onSubmit={handleCreateSubtask} className="space-y-4">
                           
                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-1">
                                    Subtask Name <span className="text-red-500 text-xs">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="input-field w-full border-l-4 border-l-red-200"
                                    placeholder="Enter subtask name"
                                    value={newSubtask.name}
                                    onChange={e => setNewSubtask(prev => ({ ...prev, name: e.target.value }))}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-1">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    className="input-field w-full border-l-4 border-l-red-200"
                                    placeholder="Enter subtask description"
                                    rows={3}
                                    value={newSubtask.description}
                                    onChange={e => {
                                        const value = e.target.value;
                                        if (value.length <= 250) {
                                            setNewSubtask(prev => ({ ...prev, description: value }));
                                        }
                                    }}
                                    maxLength={250}
                                    required
                                />
                                <div className="flex justify-between items-center mt-1">
                                    <span className="text-xs text-secondary-500">
                                        <span className="font-bold">
                                            {newSubtask.description ? newSubtask.description.length : 0}
                                        </span>
                                        /250
                                    </span>
                                    <span className="text-xs text-secondary-500">
                                        Max 250 characters
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                                        Estimated Hours <span className="text-red-500 text-xs">*</span>
                                    </label>
                                                                    <input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    className="input-field w-full border-l-4 border-l-red-200"
                                    placeholder="0"
                                    value={newSubtask.estimated_hours}
                                    onChange={e => setNewSubtask(prev => ({ ...prev, estimated_hours: e.target.value }))}
                                    required
                                />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                                        Priority
                                    </label>
                                    <select
                                        className="input-field w-full"
                                        value={newSubtask.priority_level}
                                        onChange={e => setNewSubtask(prev => ({ ...prev, priority_level: e.target.value }))}
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-1">
                                    Due Date <span className="text-red-500 text-xs">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="input-field w-full border-l-4 border-l-red-200"
                                    value={newSubtask.due_date}
                                    onChange={e => setNewSubtask(prev => ({ ...prev, due_date: e.target.value }))}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-2">
                                    Assign To <span className="text-red-500 text-xs">*</span>
                                </label>
                                
                                {/* Search Filter */}
                                <div className="relative mb-4">
                                    <input
                                        type="text"
                                        placeholder="Search team members..."
                                        value={memberSearchFilter}
                                        onChange={(e) => setMemberSearchFilter(e.target.value)}
                                        className="w-full px-3 py-2 pr-10 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    />
                                    <Search className="absolute right-3 top-2.5 h-4 w-4 text-secondary-400" />
                                </div>

                                {/* Selected Members - Simple List */}
                                {newSubtask.assigned_to.length > 0 && (
                                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="text-xs text-green-600 font-medium mb-2">
                                            Selected ({newSubtask.assigned_to.length})
                                        </div>
                                        <div className="space-y-1">
                                            {newSubtask.assigned_to.map(userId => {
                                                const member = taskData?.assigned_members?.find(m => m.id === userId);
                                                return member ? (
                                                    <div key={userId} className="flex items-center justify-between p-2 bg-white rounded border border-green-200">
                                                        <div className="flex items-center gap-2">
                                                            <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xs">
                                                                {member.full_name ? member.full_name[0] : member.username[0]}
                                                            </div>
                                                            <span className="text-sm text-secondary-800 font-medium">
                                                                {member.full_name || member.username}
                                                                {member.id === user?.id && ' (You)'}
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setNewSubtask(prev => ({
                                                                    ...prev,
                                                                    assigned_to: prev.assigned_to.filter(id => id !== userId)
                                                                }));
                                                            }}
                                                            className="text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors"
                                                            title="Remove"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div key={userId} className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-200">
                                                        <div className="flex items-center gap-2">
                                                            <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center text-red-700 font-bold text-xs">
                                                                ?
                                                            </div>
                                                            <span className="text-sm text-red-800 font-medium">
                                                                Unknown Member
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setNewSubtask(prev => ({
                                                                    ...prev,
                                                                    assigned_to: prev.assigned_to.filter(id => id !== userId)
                                                                }));
                                                            }}
                                                            className="text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-colors"
                                                            title="Remove"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Available Members - Clean Checkbox List */}
                                <div className="space-y-2">
                                    <div className="text-xs text-secondary-600 font-medium">Available Members:</div>
                                    {(taskData?.assigned_members && Array.isArray(taskData.assigned_members) && taskData.assigned_members.length > 0) ? (
                                        taskData.assigned_members
                                            .filter(member => 
                                                member.full_name?.toLowerCase().includes(memberSearchFilter.toLowerCase()) ||
                                                member.username.toLowerCase().includes(memberSearchFilter.toLowerCase())
                                            )
                                            .map(member => (
                                        <label key={member.id} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-secondary-50 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={newSubtask.assigned_to.includes(member.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setNewSubtask(prev => ({
                                                            ...prev,
                                                            assigned_to: [...prev.assigned_to, member.id]
                                                        }));
                                                    } else {
                                                        setNewSubtask(prev => ({
                                                            ...prev,
                                                            assigned_to: prev.assigned_to.filter(id => id !== member.id)
                                                        }));
                                                    }
                                                }}
                                                className="h-4 w-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                                            />
                                            <div className="h-8 w-8 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-700 font-bold text-sm">
                                                {member.full_name ? member.full_name[0] : member.username[0]}
                                            </div>
                                            <div className="flex-1">
                                                <div className="text-sm text-secondary-900 font-medium">
                                                    {member.full_name || member.username}
                                                    {member.id === user?.id && ' (You)'}
                                                </div>
                                                <div className="text-xs text-secondary-500">
                                                    @{member.username}
                                                </div>
                                            </div>
                                            {newSubtask.assigned_to.includes(member.id) && (
                                                <div className="text-green-600">
                                                    <CheckSquare className="h-4 w-4" />
                                                </div>
                                            )}
                                        </label>
                                    ))
                                    ) : (
                                        <div className="text-center py-4 text-secondary-500">
                                            <div className="text-sm">
                                                {taskData?.assigned_members && Array.isArray(taskData.assigned_members) && taskData.assigned_members.length === 0 
                                                    ? 'No members assigned to this task' 
                                                    : 'No members available'
                                                }
                                            </div>
                                            <div className="text-xs text-secondary-400 mt-1">
                                                {!taskData ? 'Task data not loaded' : 
                                                 taskData.assigned_members && Array.isArray(taskData.assigned_members) && taskData.assigned_members.length === 0 
                                                    ? 'Contact an admin to assign members to this task' 
                                                    : 'Loading members...'
                                                }
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <p className="text-xs text-secondary-500 mt-2">
                                    <span className="text-red-500">*</span> At least one member must be assigned
                                </p>
                            </div>

                        </form>
            </FormDrawer>

            {/* GitHub Connect Modal */}
            {showGitHubConnectModal && (
                <div className="modal-backdrop-full flex items-center justify-center" onClick={() => setShowGitHubConnectModal(false)} style={{ contain: 'layout style paint' }}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] max-h-[90dvh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} style={{ contain: 'layout style paint', position: 'relative' }}>
                        <div className="flex items-center justify-between mb-4 flex-shrink-0">
                            <h3 className="text-lg font-semibold">
                                {taskData.task.github_integration 
                                    ? 'Edit Repository Settings' 
                                    : 'Connect Repository'
                                }
                            </h3>
                            <button
                                onClick={() => setShowGitHubConnectModal(false)}
                                className="text-secondary-400 hover:text-secondary-600"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        
                        <div className="space-y-4 flex-1 overflow-y-auto">
                            {!githubConnectionStatus?.connected ? (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-yellow-800 mb-2">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.719-1.857 2.743-1.857 3.462 0l5.58 14.163c.76 1.94-.907 3.963-2.731 3.963H5.408c-1.824 0-3.491-2.022-2.731-3.963L8.257 3.099zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-medium">GitHub Account Not Connected</span>
                                    </div>
                                    <p className="text-sm text-yellow-700 mb-3">
                                        You need to connect your GitHub account first to manage repository integrations.
                                    </p>
                                    <button
                                        onClick={() => {
                                            setShowGitHubConnectModal(false);
                                            navigate('/github-integration');
                                        }}
                                        className="btn-primary text-sm"
                                    >
                                        Connect GitHub Account
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="relative">
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                                        Repository <span className="text-red-500">*</span>
                                    </label>
                                        <div className="relative overflow-hidden" style={{ contain: 'layout style paint', position: 'relative', zIndex: 1 }}>
                                            <select
                                                className="input-field w-full relative z-10"
                                        id="github-repo-input"
                                        defaultValue={taskData.task.github_integration?.repository || ''}
                                                style={{
                                                    position: 'relative',
                                                    zIndex: 10,
                                                    maxHeight: '200px',
                                                    overflow: 'hidden',
                                                    contain: 'layout style paint'
                                                }}
                                                onChange={handleRepositorySelectChange}
                                                onFocus={handleSelectFocus}
                                                onBlur={handleSelectBlur}
                                                onClick={handleRepositorySelectClick}
                                                disabled={repositoriesLoading || (taskData.task.github_integration && user?.role !== 'admin')}
                                            >
                                                <option value="">
                                                    {repositoriesLoading ? 'Loading repositories...' : 'Select a repository'}
                                                </option>
                                                {user?.role === 'admin' && (
                                                    <option value="__create_new__" className="font-semibold text-blue-600">➕ Create New Repository</option>
                                                )}
                                                <optgroup label="Your Repositories">
                                                    {repositoriesLoading ? (
                                                        <option value="" disabled>Loading...</option>
                                                    ) : user?.github_repositories?.length > 0 ? (
                                                        user.github_repositories.map((repo) => (
                                                            <option key={repo.id} value={repo.full_name}>
                                                                {repo.source === 'organization' ? `🏢 ${repo.full_name}` : `👤 ${repo.full_name}`}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <option value="" disabled>No repositories found</option>
                                                    )}
                                                </optgroup>
                                            </select>
                                        </div>
                                    <p className="text-xs text-secondary-500 mt-1">
                                        {repositoriesLoading ? (
                                            <span className="flex items-center gap-2">
                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" />
                                                Loading repositories...
                                            </span>
                                        ) : user?.github_repositories?.length > 0 ? (
                                            `Found ${user.github_repositories.filter(r => r.source === 'user').length} user repos, ${user.github_repositories.filter(r => r.source === 'organization').length} organization repos`
                                        ) : (
                                            'No repositories found. Please refresh your GitHub connection.'
                                        )}
                                        {taskData.task.github_integration && user?.role !== 'admin' && (
                                            <span className="block mt-1 text-blue-600">
                                                ⓘ You can only change the branch. Repository selection is restricted to admins.
                                            </span>
                                        )}
                                    </p>
                                </div>

                                    {/* New Repository Creation Form - Admin Only */}
                                    {showCreateRepoForm && user?.role === 'admin' && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                                            <div className="flex items-center gap-2 text-blue-800 mb-2">
                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                                </svg>
                                                <span className="font-medium">Create New Repository</span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-blue-700 mb-1">
                                                        Repository Name *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input-field w-full text-sm"
                                                        placeholder="my-new-project"
                                                        id="new-repo-name"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-blue-700 mb-1">
                                                        Owner
                                                    </label>
                                                    <select
                                                        className="input-field w-full text-sm"
                                                        id="new-repo-owner"
                                                    >
                                                        <option value={user?.github_username || ''}>👤 {user?.github_username || 'Your Account'}</option>
                                                        {user?.github_organizations?.map((org) => (
                                                            <option key={org.login} value={org.login}>🏢 {org.login}</option>
                                                        )) || []}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            id="new-repo-private"
                                                            defaultChecked={true}
                                                            className="rounded border-blue-300"
                                                        />
                                                        <span className="text-xs text-blue-700">Private repository</span>
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            id="new-repo-auto-init"
                                                            defaultChecked={true}
                                                            className="rounded border-blue-300"
                                                        />
                                                        <span className="text-xs text-blue-700">Initialize with README</span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={createNewRepository}
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors text-xs cursor-pointer"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                    </svg>
                                                    Create Repository
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowCreateRepoForm(false);
                                                        document.getElementById('github-repo-input').value = '';
                                                    }}
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors text-xs cursor-pointer"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={refreshGitHubRepositories}
                                            disabled={repositoriesLoading}
                                            className={`text-sm flex items-center gap-1 ${repositoriesLoading
                                                    ? 'text-gray-400 cursor-not-allowed'
                                                    : 'text-blue-600 hover:text-blue-800'
                                                }`}
                                        >
                                            {repositoriesLoading ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" />
                                                    Loading...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                    Refresh Repositories
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => navigate('/github-integration')}
                                            className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            Manage GitHub
                                        </button>
                                    </div>
                                </>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-1">
                                    Branch Name
                                </label>
                                <input
                                    type="text"
                                    className={`input-field w-full ${user?.role === 'project_lead' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="Leave empty for auto-generation"
                                    id="github-branch-input"
                                    defaultValue={taskData.task.github_integration?.branch_name || ''}
                                    disabled={user?.role === 'project_lead'}
                                />
                                <p className="text-xs text-secondary-500 mt-1">
                                    {user?.role === 'project_lead' ? (
                                        <span className="text-blue-600">ⓘ Branch settings can only be changed by admins</span>
                                    ) : (
                                        `Leave empty to auto-generate: task/${taskData?.task?.name?.toLowerCase().replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-')}`
                                    )}
                                </p>
                            </div>

                            {/* Collaborator Management Section */}
                            <div className="border-t pt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-secondary-700">Team Collaborators</h4>
                                    {(user?.role === 'admin' || user?.role === 'project_lead') && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAddCollaboratorModal(true)}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors text-xs cursor-pointer"
                                        id="add-collaborator-btn"
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                                        </svg>
                                        Add Collaborator
                                    </button>
                                    )}
                                </div>
                                
                                {/* Current Collaborators List */}
                                <div className="space-y-2">
                                    {taskData.task.github_integration?.collaborators?.length > 0 ? (
                                        taskData.task.github_integration.collaborators.map((collaborator, index) => (
                                            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                                                        <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                                                        </svg>
                                                    </div>
                                                    <span className="text-sm text-gray-700">{collaborator.username}</span>
                                                    <span className="text-xs text-gray-500">({collaborator.permission})</span>
                                                </div>
                                                {(user?.role === 'admin' || user?.role === 'project_lead') && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeCollaborator(index)}
                                                    className="text-red-500 hover:text-red-700 p-1"
                                                >
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.705a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                                            <div className="text-sm text-gray-500">No collaborators added yet</div>
                                            <div className="text-xs text-gray-400 mt-1">
                                                {user?.role === 'member' ? 
                                                    'Only admins and project leads can add collaborators' : 
                                                    'Add team members to collaborate on this repository'
                                                }
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        

                        <div className="flex gap-2 justify-end pt-4 flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setShowGitHubConnectModal(false)}
                                    className="btn-secondary"
                                    disabled={githubConnectLoading}
                                >
                                    Cancel
                                </button>
                            {githubConnectionStatus?.connected && (
                                <button 
                                    type="button"
                                    onClick={() => {
                                        const repo = document.getElementById('github-repo-input').value;
                                        const branch = document.getElementById('github-branch-input').value;
                                        
                                        if (!repo || !repo.includes('/')) {
                                            toast.error('Please select a valid repository');
                                            return;
                                        }
                                        
                                        handleConnectGitHub({
                                            repository: repo,
                                            branch_name: branch,
                                            branch_prefix: 'task'
                                        });
                                    }}
                                    className="btn-primary"
                                    disabled={githubConnectLoading}
                                >
                                    {githubConnectLoading ? 'Updating...' : (taskData.task.github_integration 
                                    ? 'Update Settings' 
                                    : 'Connect Repository'
                                )}
                                </button>
                                )}
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Task Info */}
                    <div className="card p-6 flex flex-col gap-4 md:flex-row md:gap-8">
                        {/* Left: Task Info */}
                        <div className="flex-1">
                            {/* Task Title - Inline editable */}
                            <div className="mb-6">
                                {editingField === 'name' ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            ref={inlineEditInputRef}
                                            type="text"
                                            value={inlineEditValue}
                                            onChange={e => setInlineEditValue(e.target.value)}
                                            onBlur={saveInlineEdit}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') { e.preventDefault(); saveInlineEdit(); }
                                                if (e.key === 'Escape') cancelInlineEdit();
                                            }}
                                            onFocus={e => e.target.select()}
                                            className="flex-1 text-2xl font-bold text-secondary-900 border-b-2 border-primary-500 bg-transparent px-1 py-0.5 focus:outline-none focus:ring-0"
                                            placeholder="Task name"
                                        />
                                        {isUpdatingTask && <Loader2 className="h-5 w-5 animate-spin text-primary-600" />}
                                    </div>
                                ) : (
                                    <h2 
                                        className={`text-2xl font-bold text-secondary-900 mb-2 leading-tight ${canEditTask ? 'cursor-text rounded px-1 py-0.5 -mx-1 -my-0.5 transition-colors group' : ''}`}
                                        onDoubleClick={canEditTask ? () => startInlineEdit('name', taskData.task.name) : undefined}
                                        title={canEditTask ? 'Double-click to edit' : undefined}
                                    >
                                        {taskData.task.name}
                                        {canEditTask && <Edit className="h-4 w-4 inline-block ml-2 opacity-0 group-hover:opacity-50 text-secondary-500" />}
                                    </h2>
                                )}
                                {taskData?.task?.parent_task_id && (
                                    <div className="flex items-center gap-2 text-sm text-secondary-500">
                                        <span>Subtask of</span>
                                        <button
                                            onClick={() => navigate(`/tasks/${taskData.task.parent_task_id}`)}
                                            className="text-primary-600 hover:text-primary-700 font-medium hover:underline transition-colors"
                                        >
                                            {taskData.parent_task_name || 'Loading...'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Task Details - ClickUp Style */}
                            <div className="space-y-6">
                                {/* Description - Inline editable */}
                                <div>
                                    <h3 className="text-base font-semibold text-secondary-900 mb-2">Description</h3>
                                    {editingField === 'description' ? (
                                        <div className="space-y-2">
                                            <textarea
                                                ref={inlineEditInputRef}
                                                value={inlineEditValue}
                                                onChange={e => setInlineEditValue(e.target.value)}
                                                onBlur={saveInlineEdit}
                                                onKeyDown={e => {
                                                    if (e.key === 'Escape') cancelInlineEdit();
                                                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); saveInlineEdit(); }
                                                }}
                                                className="w-full max-w-3xl text-secondary-700 text-base leading-relaxed border-2 border-primary-500 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200 min-h-[80px]"
                                                placeholder="Add a description..."
                                                rows={4}
                                            />
                                            <div className="flex items-center gap-2 text-xs text-secondary-500">
                                                <span>Click outside or Ctrl+Enter to save</span>
                                                {isUpdatingTask && <Loader2 className="h-4 w-4 animate-spin" />}
                                            </div>
                                        </div>
                                    ) : (
                                        <div 
                                            className={`text-secondary-700 text-base leading-relaxed max-w-3xl min-h-[24px] ${canEditTask ? 'cursor-text rounded px-2 py-1 -mx-2 -my-1 transition-colors group' : ''}`}
                                            onDoubleClick={canEditTask ? () => startInlineEdit('description', taskData.task.description || '') : undefined}
                                            title={canEditTask ? 'Double-click to edit' : undefined}
                                        >
                                            {taskData.task.description || (canEditTask ? <span className="text-secondary-400 italic">Add description...</span> : '—')}
                                            {canEditTask && <Edit className="h-3.5 w-3.5 inline-block ml-2 opacity-0 group-hover:opacity-50 text-secondary-500 align-middle" />}
                                        </div>
                                    )}
                                </div>

                                {/* Key Info Row */}
                                <div className="flex flex-wrap gap-6">
                                    {/* Created Info */}
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-secondary-100 flex items-center justify-center">
                                            <User className="h-4 w-4 text-secondary-600" />
                                        </div>
                                        <div>
                                            <div className="text-sm text-secondary-500">Created by</div>
                                            <div className="font-medium text-secondary-900">{taskData.created_by_user?.full_name || taskData.created_by_user?.username || 'Unknown'}</div>
                                        </div>
                                    </div>

                                    {/* Created Date */}
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-secondary-100 flex items-center justify-center">
                                            <Calendar className="h-4 w-4 text-secondary-600" />
                                        </div>
                                        <div>
                                            <div className="text-sm text-secondary-500">Created at</div>
                                            <div className="font-medium text-secondary-900">{taskData.task.created_at ? toUTCPlus5(taskData.task.created_at) : '—'}</div>
                                        </div>
                                    </div>

                                    {/* Due Date - Inline editable */}
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-secondary-100 flex items-center justify-center">
                                            <Calendar className="h-4 w-4 text-secondary-600" />
                                        </div>
                                        <div>
                                            <div className="text-sm text-secondary-500">Due date</div>
                                            {editingField === 'due_date' ? (
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        ref={inlineEditInputRef}
                                                        type="date"
                                                        value={inlineEditValue}
                                                        onChange={e => setInlineEditValue(e.target.value)}
                                                        onBlur={saveInlineEdit}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') { e.preventDefault(); saveInlineEdit(); }
                                                            if (e.key === 'Escape') cancelInlineEdit();
                                                        }}
                                                        className="font-medium text-secondary-900 border-b-2 border-primary-500 bg-transparent px-1 py-0.5 focus:outline-none"
                                                    />
                                                    {isUpdatingTask && <Loader2 className="h-4 w-4 animate-spin" />}
                                                </div>
                                            ) : (
                                                <div 
                                                    className={`font-medium text-secondary-900 ${canEditTask ? 'cursor-text rounded px-1 -mx-1 transition-colors group inline-flex items-center gap-1' : ''}`}
                                                    onDoubleClick={canEditTask ? () => startInlineEdit('due_date', taskData.task.due_date ? format(new Date(taskData.task.due_date), 'yyyy-MM-dd') : '') : undefined}
                                                    title={canEditTask ? 'Double-click to edit' : undefined}
                                                >
                                                    {taskData.task.due_date ? formatDueDate(taskData.task.due_date) : (canEditTask ? <span className="text-secondary-400 italic">Set date...</span> : '—')}
                                                    {canEditTask && <Edit className="h-3.5 w-3.5 opacity-0 group-hover:opacity-50 text-secondary-500" />}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Estimated Time - Inline editable */}
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-secondary-100 flex items-center justify-center">
                                            <Clock className="h-4 w-4 text-secondary-600" />
                                        </div>
                                        <div>
                                            <div className="text-sm text-secondary-500">Estimated</div>
                                            {editingField === 'estimated' ? (
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        ref={inlineEditInputRef}
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={inlineEditValue}
                                                        onChange={e => setInlineEditValue(e.target.value)}
                                                        onBlur={saveInlineEdit}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') { e.preventDefault(); saveInlineEdit(); }
                                                            if (e.key === 'Escape') cancelInlineEdit();
                                                        }}
                                                        onFocus={e => e.target.select()}
                                                        className="w-24 min-w-[4rem] font-medium text-secondary-900 border-b-2 border-primary-500 bg-transparent px-1 focus:outline-none"
                                                        placeholder="0"
                                                    />
                                                    <span className="text-secondary-600">hours</span>
                                                    {isUpdatingTask && <Loader2 className="h-4 w-4 animate-spin" />}
                                                </div>
                                            ) : (
                                                <div 
                                                    className={`font-medium text-secondary-900 ${canEditTask ? 'cursor-text rounded px-1 -mx-1 transition-colors group inline-flex items-center gap-1' : ''}`}
                                                    onDoubleClick={canEditTask ? () => startInlineEdit('estimated', taskData.task.estimated_hours?.toString() ?? '') : undefined}
                                                    title={canEditTask ? 'Double-click to edit' : undefined}
                                                >
                                                    {taskData.task.estimated_hours != null ? `${taskData.task.estimated_hours} hours` : (canEditTask ? <span className="text-secondary-400 italic">Set estimate...</span> : '—')}
                                                    {canEditTask && <Edit className="h-3.5 w-3.5 opacity-0 group-hover:opacity-50 text-secondary-500" />}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Status and Priority Row */}
                                <div className="flex flex-wrap gap-4">
                                    {/* Status */}
                                    {taskData.task.status && (
                                        <div>
                                            <div className="text-sm text-secondary-500 mb-2">Status</div>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-3 py-1.5 rounded-md text-sm font-medium ${getPriorityColor(taskData.task.status)}`}>
                                                    {taskData.task.status}
                                                </span>
                                                {isUpdatingStatus && (
                                                    <div className="flex items-center gap-1 text-xs text-secondary-500">
                                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                                                        Updating...
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Sprint */}
                                    {taskSprint && (
                                        <div>
                                            <div className="text-sm text-secondary-500 mb-2">Sprint</div>
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                                                <Flag className="h-3.5 w-3.5" />
                                                {taskSprint.name}
                                                {taskSprint.start_date && taskSprint.end_date && (
                                                    <span className="text-xs opacity-80">
                                                        ({String(taskSprint.start_date).slice(0, 10)} – {String(taskSprint.end_date).slice(0, 10)})
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    )}

                                    {/* Priority - Inline editable */}
                                    <div>
                                        <div className="text-sm text-secondary-500 mb-2">Priority</div>
                                        {editingField === 'priority' ? (
                                            <div className="flex items-center gap-2">
                                                <select
                                                    ref={inlineEditInputRef}
                                                    value={inlineEditValue}
                                                    onChange={e => setInlineEditValue(e.target.value)}
                                                    onBlur={saveInlineEdit}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') { e.preventDefault(); saveInlineEdit(); }
                                                        if (e.key === 'Escape') cancelInlineEdit();
                                                    }}
                                                    className="font-medium text-secondary-900 border-2 border-primary-500 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-200"
                                                    autoFocus
                                                >
                                                    <option value="low">Low</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="high">High</option>
                                                    <option value="urgent">Urgent</option>
                                                </select>
                                                {isUpdatingTask && <Loader2 className="h-4 w-4 animate-spin" />}
                                            </div>
                                        ) : (
                                            <span 
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${getPriorityColor(taskData.task.priority_level || 'medium')} ${canEditTask ? 'cursor-pointer hover:ring-2 hover:ring-primary-200 transition-all group' : ''}`}
                                                onClick={canEditTask ? () => startInlineEdit('priority', taskData.task.priority_level || 'medium') : undefined}
                                                title={canEditTask ? 'Click to edit' : undefined}
                                            >
                                                {taskData.task.priority_level ? taskData.task.priority_level.charAt(0).toUpperCase() + taskData.task.priority_level.slice(1) : 'Medium'}
                                                {canEditTask && <Edit className="h-3.5 w-3.5 opacity-0 group-hover:opacity-70" />}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Tags */}
                                {taskData.task.tags && taskData.task.tags.length > 0 && (
                                    <div>
                                        <div className="text-sm text-secondary-500 mb-3">Tags</div>
                                        <div className="flex flex-wrap gap-2">
                                            {taskData.task.tags.map((tag, index) => (
                                                <span key={index} className="px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-md text-sm font-medium hover:bg-secondary-200 transition-colors cursor-default">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                        {/* GitHub Integration Section */}
                                <div className="mt-6">
                                    {taskData.task.github_integration ? (
                                        // Repository is connected - show repository info with edit/disconnect buttons
                                        <div className="p-4 rounded-lg">
                                <div className="flex items-center gap-3 mb-4">
                                    {/* GitHub Logo */}
                                                <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                        </svg>
                                    </div>
                                                                                                                                                  <div className="flex-1 min-w-0">
                                                   <div className="text-base font-semibold text-gray-900 mb-1">
                                        <a
                                            href={`https://github.com/${taskData.task.github_integration.repository}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                                           className="hover:text-blue-600 hover:underline transition-colors cursor-pointer"
                                                           title="View repository on GitHub"
                                        >
                                                           {taskData.task.github_integration.repository}
                                        </a>
                                    </div>
                                                   <div className="flex items-center gap-2">
                                                       <a 
                                                           href={`https://github.com/${taskData.task.github_integration.repository}/tree/${taskData.task.github_integration.branch_name}`}
                                                           target="_blank"
                                                           rel="noopener noreferrer"
                                                           className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full hover:bg-gray-200 hover:text-gray-800 transition-colors cursor-pointer"
                                                           title="View branch on GitHub"
                                                       >
                                                           <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                               <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                                                           </svg>
                                                           {taskData.task.github_integration.branch_name}
                                                       </a>
                                                       {/* Collaborators Icon */}
                                        <button
                                                           onClick={() => setShowCollaboratorsModal(true)}
                                                           className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full hover:bg-blue-200 transition-colors cursor-pointer"
                                                           title="View collaborators"
                                        >
                                                           <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                               <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                                           </svg>
                                                           {taskData.task.github_integration.collaborators?.length || 0}
                                        </button>
                                                   </div>
                                               </div>
                                            </div>
                                                                                          {/* Action Buttons */}
                                              <div className="flex gap-3">
                                                  {/* Edit Button - Admin and project leads only */}
                                                  {(user?.role === 'admin' || user?.role === 'project_lead') && (
                                            <button
                                                      onClick={() => setShowGitHubConnectModal(true)}
                                                      className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-white dark:bg-secondary-800 text-gray-700 dark:text-secondary-300 border border-gray-300 dark:border-secondary-600 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                                                      title="Edit Repository Settings"
                                            >
                                                      <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                      </svg>
                                                      Edit Settings
                                            </button>
                                                  )}
                                                  {/* Disconnect Button - Admin only */}
                                                  {user?.role === 'admin' && (
                                            <button
                                                onClick={() => handleDisconnectGitHub()}
                                                          disabled={githubDisconnectLoading}
                                                          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-white text-red-600 border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                          title="Disconnect Repository"
                                                      >
                                                          {githubDisconnectLoading ? (
                                                              <>
                                                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                                                                  Disconnecting...
                                                              </>
                                                          ) : (
                                                              <>
                                                                  <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                                                      <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                  </svg>
                                                Disconnect
                                                              </>
                                                          )}
                                            </button>
                                                  )}
                                    </div>
                                </div>
                                                                        ) : (
                                        // No repository connected - show connect button
                                        <div>
                                            {user?.role === 'admin' && (
                                                <button
                                                    onClick={() => setShowGitHubConnectModal(true)}
                                                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors"
                                                >
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                                    </svg>
                                                    Connect Repository
                                                </button>
                                            )}
                            </div>
                        )}
                                </div>
                            </div>
                        </div>
                        {/* Right: Assigned Members */}
                        <div className="w-full lg:w-72 p-4 flex flex-col items-start mt-8 lg:mt-0" ref={addMemberDropdownRef}>
                            <div className="font-semibold text-secondary-700 mb-3 flex items-center justify-between w-full gap-2">
                                <div className="flex items-center gap-2">
                                    <User className="h-4 w-4" />
                                    Assigned Members
                                    {taskData.assigned_members && taskData.assigned_members.length > 0 && (
                                        <span className="bg-primary-100 text-primary-700 text-xs font-medium px-2 py-1 rounded-full">
                                            {taskData.assigned_members.length}
                                        </span>
                                    )}
                                </div>
                                {canEditTask && (
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowAddMemberDropdown(!showAddMemberDropdown)}
                                            className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs text-secondary-500 hover:text-primary-600 transition-colors"
                                            title="Add member"
                                        >
                                            <UserPlus className="h-3.5 w-3.5" />
                                            Add
                                        </button>
                                        {showAddMemberDropdown && (
                                            <div className="absolute right-0 top-full mt-1 z-50 min-w-[200px] bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
                                                {(() => {
                                                    const membersToShow = (projectData?.assigned_members?.length > 0 ? projectData.assigned_members : allUsers);
                                                    const availableToAdd = Array.isArray(membersToShow) ? membersToShow.filter(m => !taskData.task.assigned_to?.includes(m.id)) : [];
                                                    return availableToAdd.length > 0 ? (
                                                    availableToAdd.map(member => (
                                                        <button
                                                            key={member.id}
                                                            onClick={() => handleAddMemberToTask(member.id)}
                                                            disabled={isUpdatingTask}
                                                            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-secondary-200 transition-colors disabled:opacity-50"
                                                        >
                                                            <div className="h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-xs flex-shrink-0">
                                                                {member.full_name ? member.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : member.username[0].toUpperCase()}
                                                            </div>
                                                            <span className="truncate">{member.full_name || member.username}</span>
                                                        </button>
                                                    ))
                                                    ) : (
                                                        <div className="px-3 py-1.5 text-sm text-secondary-500">No members to add</div>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {Array.isArray(taskData.assigned_members) && taskData.assigned_members.length > 0 ? (
                                <div className="w-full">
                                    {/* Compact view for many users */}
                                    {taskData.assigned_members.length > 4 ? (
                                        <div className="space-y-3">
                                            {/* Show first 3 users with full details */}
                                            {taskData.assigned_members.slice(0, 3).map(member => (
                                                <div key={member.id} className="flex items-center gap-3 p-2 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors group">
                                                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm flex-shrink-0">
                                                        {member.full_name ? member.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : member.username[0].toUpperCase()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-secondary-800 truncate">
                                                            {member.full_name || member.username}
                                                            {member.id === user?.id && ' (You)'}
                                                        </div>
                                                        <div className="text-xs text-secondary-500 capitalize">
                                                            {member.role || 'Member'}
                                                        </div>
                                                    </div>
                                                    {canEditTask && taskData.task.assigned_to?.length > 1 && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleRemoveMemberFromTask(member.id); }}
                                                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 text-red-500 transition-all"
                                                            title="Remove"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            {/* Show remaining users count */}
                                            <button 
                                                onClick={() => setShowAllMembersModal(true)}
                                                className="w-full text-center p-2 bg-primary-50 rounded-lg border border-primary-200 hover:bg-primary-100 transition-colors cursor-pointer"
                                            >
                                                <div className="text-sm font-medium text-primary-700">
                                                    +{taskData.assigned_members.length - 3} more
                                                </div>
                                                <div className="text-xs text-primary-600">
                                                    Click to view all
                                                </div>
                                            </button>
                                        </div>
                                    ) : (
                                        /* Show all users with full details when 4 or fewer */
                                        <div className="space-y-3">
                                    {taskData?.assigned_members && Array.isArray(taskData.assigned_members) && taskData.assigned_members.map(member => (
                                                <div key={member.id} className="flex items-center gap-3 p-2 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors group">
                                                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm flex-shrink-0">
                                                {member.full_name ? member.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : member.username[0].toUpperCase()}
                                            </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-secondary-800 truncate">
                                                {member.full_name || member.username}
                                                {member.id === user?.id && ' (You)'}
                                                        </div>
                                                        <div className="text-xs text-secondary-500 capitalize">
                                                            {member.role || 'Member'}
                                                        </div>
                                            </div>
                                                    {canEditTask && taskData.task.assigned_to?.length > 1 && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleRemoveMemberFromTask(member.id); }}
                                                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 text-red-500 transition-all"
                                                            title="Remove"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    )}
                                        </div>
                                    ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center p-4 bg-secondary-50 rounded-lg border border-dashed border-secondary-300 w-full">
                                    <User className="h-8 w-8 text-secondary-400 mx-auto mb-2" />
                                    <div className="text-secondary-500 text-sm">No members assigned</div>
                                    <div className="text-secondary-400 text-xs mb-2">Assign team members to get started</div>
                                    {canEditTask && (
                                        <button
                                            onClick={() => setShowAddMemberDropdown(!showAddMemberDropdown)}
                                            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-secondary-500 hover:text-primary-600 transition-colors"
                                        >
                                            <UserPlus className="h-3.5 w-3.5" />
                                            Add
                                        </button>
                                    )}
                                </div>
                            )}
                            
                            
                                                </div>
                       

                    </div>
                    {/* Subtasks Section - Show always, not just when there are subtasks */}
                    <div className="card p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <List className="h-5 w-5 text-primary-600" />
                                <h3 className="text-base font-semibold text-secondary-900">
                                    Subtasks {subtasks.length > 0 && `(${subtasks.length})`}
                                </h3>
                            </div>
                            {(user?.role === 'admin' || user?.role === 'project_lead' || 
                              (user?.role === 'member' && taskData.task.assigned_to?.includes(user.id))) && (
                                <button
                                    onClick={() => setShowSubtaskModal(true)}
                                    className="btn-primary flex items-center gap-2 text-sm"
                                    disabled={taskData.task.status === 'Done'}
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Subtask
                                </button>
                            )}
                        </div>
                        
                        {subtasks.length > 0 ? (
                            <div className="space-y-2">
                                {subtasks.map(subtask => (
                                    <div 
                                        key={subtask.id} 
                                        className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors cursor-pointer"
                                        onClick={() => navigateToSubtask(subtask.id)}
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <div className={`font-medium ${subtask.status === 'Done' ? 'line-through text-secondary-500' : 'text-secondary-900'}`}>
                                                    {subtask.name}
                                                </div>
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                                    Subtask
                                                </span>
                                            </div>
                                            {subtask.description && (
                                                <div className="text-sm text-secondary-600 mt-1">{subtask.description}</div>
                                            )}
                                            <div className="flex items-center gap-4 mt-2 text-xs text-secondary-500">
                                                {subtask.estimated_hours && (
                                                    <span>Est: {subtask.estimated_hours}h</span>
                                                )}
                                                {subtask.priority_level !== 'medium' && (
                                                    <span className={`px-2 py-1 rounded ${getPriorityColor(subtask.priority_level)}`}>
                                                        {subtask.priority_level}
                                                    </span>
                                                )}
                                                {subtask.due_date && (
                                                    <span>Due: {format(new Date(subtask.due_date), 'MMM d')}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {subtask.assigned_to && subtask.assigned_to.length > 0 && (
                                                <div className="flex -space-x-1">
                                                    {subtask.assigned_to.slice(0, 3).map(userId => {
                                                        const member = taskData.assigned_members?.find(m => m.id === userId);
                                                        return member ? (
                                                            <div key={userId} className="h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs border-2 border-white">
                                                                {member.full_name ? member.full_name[0] : member.username[0]}
                                                            </div>
                                                        ) : null;
                                                    })}
                                                    {subtask.assigned_to.length > 3 && (
                                                        <div className="h-6 w-6 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-600 font-bold text-xs border-2 border-white">
                                                            +{subtask.assigned_to.length - 3}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            
                                            {(user?.role === 'admin' || user?.role === 'project_lead' || 
                                              (user?.role === 'member' && taskData.task.assigned_to?.includes(user.id) && subtask.created_by === user.id)) && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent card click
                                                        handleEditSubtask(subtask);
                                                    }}
                                                    className="text-secondary-400 hover:text-blue-600 p-1"
                                                    title="Edit subtask"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                            )}
                                            
                                            {(user?.role === 'admin' || user?.role === 'project_lead' || 
                                              (user?.role === 'member' && taskData.task.assigned_to?.includes(user.id) && subtask.created_by === user.id)) && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent card click
                                                        handleDeleteSubtask(subtask.id);
                                                    }}
                                                    className="text-secondary-400 hover:text-red-600 p-1"
                                                    title="Delete subtask"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center p-6 bg-secondary-50 rounded-lg border border-dashed border-secondary-300">
                                <List className="h-12 w-12 text-secondary-400 mx-auto mb-3" />
                                <div className="text-secondary-600 font-medium mb-2">No subtasks yet</div>
                                <div className="text-secondary-500 text-sm mb-4">
                                    Break down this task into smaller, manageable pieces
                                </div>
                                {(user?.role === 'admin' || user?.role === 'project_lead' || 
                                  (user?.role === 'member' && taskData.task.assigned_to?.includes(user.id))) && (
                                    <button
                                        onClick={() => setShowSubtaskModal(true)}
                                        className="btn-secondary flex items-center gap-2 mx-auto"
                                        disabled={taskData.task.status === 'Done'}
                                    >
                                        <Plus className="h-4 w-4" />
                                        Create First Subtask
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Files & Attachments */}
                    <div className="bg-white dark:bg-secondary-800 rounded-lg border dark:border-secondary-700 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                <Paperclip className="h-5 w-5 text-gray-600" />
                                Files & Attachments
                                {taskData.screenshots && taskData.screenshots.length > 0 && (
                                    <span className="bg-primary-100 text-primary-800 text-xs font-medium px-2 py-1 rounded-full">
                                        {taskData.screenshots.length}
                                    </span>
                                )}
                            </h3>
                        </div>

                        {/* Upload Section */}
                        <div className="mb-6">
                            <label className="block w-full">
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors cursor-pointer bg-gray-50 hover:bg-gray-100">
                                    <div className="flex flex-col items-center gap-2">
                                        <Upload className="h-8 w-8 text-gray-400" />
                                        <span className="text-gray-600 font-medium">Add File</span>
                                        <span className="text-gray-500 text-sm">Click to upload screenshots, documents, or other files</span>
                                        <div className="text-xs text-gray-400 mt-1">
                                            Supported: PDF, Word, PNG, JPG, JPEG, Excel, Spreadsheet
                                        </div>
                                    </div>
                                </div>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    onChange={handleAttachmentUpload}
                                    disabled={isAttachmentUploading}
                                />
                            </label>
                        </div>

                        {/* Upload Progress */}
                        {isAttachmentUploading && (
                            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                    <span className="text-blue-800 font-medium">Uploading file...</span>
                                </div>
                                <div className="w-full bg-blue-200 rounded-full h-2">
                                    <div 
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${attachmentUploadProgress}%` }}
                                    ></div>
                                </div>
                                <div className="text-right text-blue-700 text-sm mt-1">{attachmentUploadProgress}%</div>
                            </div>
                        )}

                        {/* Attachments Grid */}
                        {taskData.screenshots && taskData.screenshots.length > 0 && (
                            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                                {taskData.screenshots.map((ss, index) => (
                                    <div key={ss.id} className="relative group bg-white dark:bg-secondary-800 rounded-lg border dark:border-secondary-700 border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 overflow-hidden">
                                        {/* File Preview */}
                                        <div className="relative">
                                            {ss.content_type?.startsWith('image/') ? (
                                                <div className="overflow-hidden cursor-pointer relative">
                                                    <Attachment
                                                        attachment={ss}
                                                        size="auto"
                                                        onClick={(attachment, url) => handleFilePreview(attachment)}
                                                        showPreview={true}
                                                        showDownload={true}
                                                        showFileInfo={false}
                                                        className="rounded-t-lg"
                                                    />
                                                    
                                                    {/* Hover Action Buttons for Images */}
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleFilePreview(ss);
                                                                }}
                                                                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                                                                title="View"
                                                            >
                                                                <Eye className="h-3 w-3 text-gray-700" />
                                                            </button>
                                                            <button
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    const link = document.createElement('a');
                                                                    const freshUrl = await getAttachmentUrl(ss.filename || ss.blob_name);
                                                                    link.href = freshUrl || ss.url;
                                                                    link.download = ss.filename || 'attachment';
                                                                    link.target = '_blank';
                                                                    document.body.appendChild(link);
                                                                    link.click();
                                                                    document.body.removeChild(link);
                                                                }}
                                                                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                                                                title="Download"
                                                            >
                                                                <Download className="h-3 w-3 text-gray-700" />
                                                            </button>
                                                            <button
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    const freshUrl = await getAttachmentUrl(ss.filename || ss.blob_name);
                                                                    window.open(freshUrl || ss.url, '_blank');
                                                                }}
                                                                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                                                                title="Open in new tab"
                                                            >
                                                                <ExternalLink className="h-3 w-3 text-gray-700" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-t-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer relative">
                                                    {ss.content_type === 'application/pdf' ? (
                                                        <div className="w-full h-full flex items-center justify-center bg-red-50">
                                                            <div className="text-center">
                                                                <div className="text-red-500 text-3xl mb-1">📄</div>
                                                                <div className="text-red-700 text-xs font-medium">PDF</div>
                                                            </div>
                                                        </div>
                                                    ) : ss.content_type?.includes('word') ? (
                                                        <div className="w-full h-full flex items-center justify-center bg-blue-50">
                                                            <div className="text-center">
                                                                <WordIcon className="h-8 w-8 mx-auto mb-1" />
                                                                <div className="text-blue-700 text-xs font-medium">Word</div>
                                                            </div>
                                                        </div>
                                                    ) : ss.content_type?.includes('excel') || ss.content_type?.includes('spreadsheet') ? (
                                                        <div className="w-full h-full flex items-center justify-center bg-green-50">
                                                            <div className="text-center">
                                                                <ExcelIcon className="h-8 w-8 mx-auto mb-1" />
                                                                <div className="text-green-700 text-xs font-medium">Excel</div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center bg-gray-50">
                                                            <div className="text-center">
                                                                <div className="text-gray-500 text-3xl mb-1">📎</div>
                                                                <div className="text-gray-700 text-xs font-medium">File</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Hover Action Buttons */}
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleFilePreview(ss);
                                                                }}
                                                                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                                                                title="View"
                                                            >
                                                                <Eye className="h-3 w-3 text-gray-700" />
                                                            </button>
                                                            <button
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    const link = document.createElement('a');
                                                                    const freshUrl = await getAttachmentUrl(ss.filename || ss.blob_name);
                                                                    link.href = freshUrl || ss.url;
                                                                    link.download = ss.filename || 'attachment';
                                                                    link.target = '_blank';
                                                                    document.body.appendChild(link);
                                                                    link.click();
                                                                    document.body.removeChild(link);
                                                                }}
                                                                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                                                                title="Download"
                                                            >
                                                                <Download className="h-3 w-3 text-gray-700" />
                                                            </button>
                                                            <button
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    const freshUrl = await getAttachmentUrl(ss.filename || ss.blob_name);
                                                                    window.open(freshUrl || ss.url, '_blank');
                                                                }}
                                                                className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                                                                title="Open in new tab"
                                                            >
                                                                <ExternalLink className="h-3 w-3 text-gray-700" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* File Type Badge */}
                                            <div className="absolute top-2 left-2">
                                                {ss.content_type?.startsWith('image/') ? (
                                                    <div className="bg-blue-500 text-white px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                                                        <Image className="h-2 w-2" />
                                                        Image
                                                    </div>
                                                ) : ss.content_type === 'application/pdf' ? (
                                                    <div className="bg-red-500 text-white px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                                                        <FileText className="h-2 w-2" />
                                                        PDF
                                                    </div>
                                                ) : ss.content_type?.includes('word') ? (
                                                    <div className="bg-blue-600 text-white px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                                                        <FileText className="h-2 w-2" />
                                                        Word
                                                    </div>
                                                ) : ss.content_type?.includes('excel') || ss.content_type?.includes('spreadsheet') ? (
                                                    <div className="bg-green-600 text-white px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                                                        <FileText className="h-2 w-2" />
                                                        Excel
                                                    </div>
                                                ) : (
                                                    <div className="bg-gray-500 text-white px-1.5 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                                                        <File className="h-2 w-2" />
                                                        File
                                                    </div>
                                                )}
                                            </div>

                                            {/* Delete Button */}
                                            <button
                                                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-600 hover:scale-110 shadow-lg"
                                                onClick={() => handleDeleteScreenshot(ss.id)}
                                                title="Delete attachment"
                                            >
                                                <X className="h-2 w-2" />
                                            </button>
                                        </div>

                                        {/* File Info */}
                                        <div className="p-2">
                                            <div className="text-xs font-medium text-gray-900 truncate mb-1" title={ss.filename}>
                                                {ss.filename}
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <span>{ss.created_at ? toUTCPlus5(ss.created_at) : 'Unknown'}</span>
                                                {ss.size && (
                                                    <span className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                                                        {(ss.size / 1024).toFixed(1)} KB
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                       
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-3">
                    <Panel padding={false} className="max-h-[85vh] flex flex-col overflow-hidden">
                        <div className="px-3 pt-2">
                            <ViewTabs
                                tabs={[
                                    { id: 'activity', label: 'Activity', icon: Clock },
                                    { id: 'notes', label: `Notes (${notes.length})`, icon: StickyNote },
                                ]}
                                activeId={activeTab}
                                onChange={setActiveTab}
                            />
                        </div>
                        {/* Tab Content */}
                        {activeTab === 'activity' ? (
                            <>
                            <div className="flex-1 overflow-y-auto space-y-2 px-3 py-3 activity-scrollbar overflow-x-hidden" ref={activityContainerRef} style={{ height: activityContainerHeight, minHeight: '200px', maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
                                {activityHasMore && !isLoadingMore && (
                                    <div className="text-center py-1.5 text-[11px] text-secondary-500 bg-secondary-50 dark:bg-secondary-900/40 rounded-md border border-secondary-200 dark:border-secondary-700">
                                        Scroll up to load more
                                    </div>
                                )}
                            
                            {!activityHasMore && activity.length > 0 && (
                                <div className="text-center py-1.5 text-[11px] text-secondary-400">
                                    Beginning of activity
                                </div>
                            )}
                            
                            {isUpdatingStatus && (
                                <div className="flex items-start gap-3 text-sm animate-pulse bg-primary-50 dark:bg-primary-900/20 p-3 rounded-lg border border-primary-200 dark:border-primary-800">
                                    <div className="h-7 w-7 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-700 dark:text-primary-300 font-semibold text-xs flex-shrink-0">
                                        {(user?.full_name || user?.username || 'U')[0].toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium text-secondary-900 dark:text-white text-sm">You</span>
                                            <span className="text-[11px] text-secondary-500">Just now</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-primary-700 dark:text-primary-300">
                                            <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary-600"></div>
                                            Updating status…
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {activity
                                .map(item => {
                                    // Normalize action_type to type for consistent handling
                                    if (!item.type && item.action_type) {
                                        item.type = item.action_type;
                                    }
                                    return item;
                                })
                                .filter(item => {
                                    // Only show if type is recognized and has details/message, or is a comment/status_change/time_entry/screenshot/timer_start/screenshot_deleted/task_assigned/task_created
                                    const handledTypes = [
                                        'comment',
                                        'status_change',
                                        'time_entry',
                                        'time_entry_created',
                                        'time_entry_updated',
                                        'time_entry_deleted',
                                        'screenshot',
                                        'timer_start',
                                        'screenshot_deleted',
                                        'task_assigned',
                                        'task_created',
                                        'task_updated',
                                        'timer_stopped',
                                        'task_deleted',
                                        'subtask_created',
                                        'subtask_updated',
                                        'subtask_deleted',
                                        'subtask_status_changed',
                                        'subtask_assigned',
                                        'github_oauth_connected',
                                        'github_oauth_disconnected',
                                        'github_collaborator_added',
                                        'github_collaborator_removed',
                                        'github_commit_created',
                                        'github_pull_request_created',
                                        'github_commit_comment',
                                        'github_branch_updated',
                                        'time_entry_with_commit',
                                        'commit_time_linked',
                                        'release_feature_created',
                                        'release_feature_updated',
                                        'release_feature_deployed',
                                        'commit_added_to_release',
                                        'code_review_created',
                                        'code_review_updated',
                                        'code_review_completed',
                                        'pr_review_status_changed',
                                        'comment'
                                    ];
                                    const itemType = item.type || item.action_type;
                                    if (handledTypes.includes(itemType)) return true;
                                    // If details has a message/comment/status, show it
                                    if (item.details && (item.details.message || item.details.comment || item.details.status)) return true;
                                    return false;
                                })
                                .map((item, idx) => {
                                    return (
                                        <div key={item.id} className="flex items-start gap-2.5 text-sm bg-white dark:bg-secondary-800/60 rounded-lg p-2.5 border border-secondary-200 dark:border-secondary-700 hover:border-secondary-300 dark:hover:border-secondary-600 transition-colors overflow-hidden">
                                            {/* Icon */}
                                            <div className="pt-0.5 flex-shrink-0">
                                                {item.type === 'comment' && <div className="p-1.5 bg-primary-50 dark:bg-primary-900/30 rounded-md"><MessageSquare className="h-3.5 w-3.5 text-primary-600" /></div>}
                                                {item.type === 'status_change' && <div className="p-1.5 bg-green-50 dark:bg-green-900/30 rounded-md"><Tag className="h-3.5 w-3.5 text-green-600" /></div>}
                                                {item.type === 'time_entry' && <div className="p-1.5 bg-primary-50 dark:bg-primary-900/30 rounded-md"><Clock className="h-3.5 w-3.5 text-primary-600" /></div>}
                                                {item.type === 'screenshot' && (
                                                    item.details?.filename ? (
                                                        <div className="p-1.5 bg-pink-100 rounded-md text-pink-600 text-sm">
                                                            {item.details.filename.toLowerCase().endsWith('.pdf') ? '📄' : 
                                                             item.details.filename.toLowerCase().match(/\.(doc|docx)$/) ? '📝' : '📷'}
                                                        </div>
                                                    ) : <div className="p-1.5 bg-pink-100 rounded-md"><Image className="h-3.5 w-3.5 text-pink-600" /></div>
                                                )}
                                                {item.type === 'timer_start' && <div className="p-1.5 bg-green-100 rounded-md"><Play className="h-3.5 w-3.5 text-green-600" /></div>}
                                                {item.type === 'timer_stopped' && <div className="p-1.5 bg-orange-100 rounded-md"><Pause className="h-3.5 w-3.5 text-orange-600" /></div>}
                                                {item.type === 'screenshot_deleted' && <div className="p-1.5 bg-pink-100 rounded-md"><Image className="h-3.5 w-3.5 text-pink-600" /></div>}
                                                {item.type === 'task_assigned' && <div className="p-1.5 bg-blue-100 rounded-md"><UserPlus className="h-3.5 w-3.5 text-blue-600" /></div>}
                                                {item.type === 'task_created' && <div className="p-1.5 bg-green-100 rounded-md"><Plus className="h-3.5 w-3.5 text-green-600" /></div>}
                                                {item.type === 'task_deleted' && <div className="p-1.5 bg-red-100 rounded-md"><Trash2 className="h-3.5 w-3.5 text-red-600" /></div>}
                                                {item.type === 'subtask_created' && <div className="p-1.5 bg-indigo-100 rounded-md"><List className="h-3.5 w-3.5 text-indigo-600" /></div>}
                                                {item.type === 'subtask_updated' && <div className="p-1.5 bg-indigo-100 rounded-md"><Edit className="h-3.5 w-3.5 text-indigo-600" /></div>}
                                                {item.type === 'subtask_deleted' && <div className="p-1.5 bg-red-100 rounded-md"><List className="h-3.5 w-3.5 text-red-600" /></div>}
                                                {item.type === 'subtask_status_changed' && <div className="p-1.5 bg-yellow-100 rounded-md"><Tag className="h-3.5 w-3.5 text-yellow-600" /></div>}
                                                {item.type === 'subtask_assigned' && <div className="p-1.5 bg-teal-100 rounded-md"><UserPlus className="h-3.5 w-3.5 text-teal-600" /></div>}
                                                {(item.type === 'time_entry_created' || item.type === 'time_entry_updated') && <div className="p-1.5 bg-primary-50 dark:bg-primary-900/30 rounded-md"><Clock className="h-3.5 w-3.5 text-primary-600" /></div>}
                                                {item.type === 'time_entry_deleted' && <div className="p-1.5 bg-red-50 dark:bg-red-900/30 rounded-md"><Clock className="h-3.5 w-3.5 text-red-600" /></div>}
                                                {item.type === 'task_updated' && <div className="p-1.5 bg-primary-50 dark:bg-primary-900/30 rounded-md"><Edit className="h-3.5 w-3.5 text-primary-600" /></div>}
                                                {item.type === 'github_oauth_connected' && <div className="p-1.5 bg-green-50 dark:bg-green-900/30 rounded-md"><svg className="h-3.5 w-3.5 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" /></svg></div>}
                                                {item.type === 'github_oauth_disconnected' && <div className="p-1.5 bg-red-50 dark:bg-red-900/30 rounded-md"><svg className="h-3.5 w-3.5 text-red-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.30.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" /></svg></div>}
                                                {item.type === 'github_collaborator_added' && <div className="p-1.5 bg-primary-50 dark:bg-primary-900/30 rounded-md"><UserPlus className="h-3.5 w-3.5 text-primary-600" /></div>}
                                                {item.type === 'github_collaborator_removed' && <div className="p-1.5 bg-red-50 dark:bg-red-900/30 rounded-md"><UserMinus className="h-3.5 w-3.5 text-red-600" /></div>}
                                                {item.type === 'github_commit_created' && <div className="p-1.5 bg-green-50 dark:bg-green-900/30 rounded-md"><svg className="h-3.5 w-3.5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg></div>}
                                                {item.type === 'github_pull_request_created' && <div className="p-1.5 bg-primary-50 dark:bg-primary-900/30 rounded-md"><svg className="h-3.5 w-3.5 text-primary-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" /></svg></div>}
                                                {item.type === 'github_commit_comment' && <div className="p-1.5 bg-green-50 dark:bg-green-900/30 rounded-md"><svg className="h-3.5 w-3.5 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" /></svg></div>}
                                            </div>
                                            {/* Content */}
                                            <div className="flex-1 min-w-0 overflow-hidden">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <div className="h-6 w-6 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-700 dark:text-primary-300 font-semibold text-xs overflow-hidden flex-shrink-0">
                                                        {item.user?.avatar ? (
                                                            <img 
                                                                src={item.user.avatar} 
                                                                alt={`${item.user?.full_name || item.user?.username || 'User'} avatar`} 
                                                                className="h-6 w-6 rounded-full object-cover"
                                                                onError={(e) => {
                                                                    // Fallback to initials if image fails to load
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div className={`h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs ${item.user?.avatar ? 'hidden' : ''}`}>
                                                            {(item.user?.full_name || item.user?.username || 'U')[0].toUpperCase()}
                                                        </div>
                                                    </div>
                                                    <span className="text-xs font-medium text-secondary-900">
                                                        {item.user_id === user?.id ? 'You' : (item.user?.full_name || item.user?.username || 'Unknown')}
                                                    </span>
                                                    <span className="text-xs text-secondary-500" title={formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}>
                                                        {toUTCPlus5(item.timestamp)}
                                                        {(item.details?.updated_at || item.details?.is_edited) && (
                                                            <span className="ml-1 text-blue-600 italic">(edited)</span>
                                                        )}
                                                    </span>
                                                </div>
                                                {/* Details by type */}
                                                {(item.type === 'comment' || item.type === 'comment_deleted') && (
                                                    <div className={`text-secondary-700 text-sm rounded p-1.5 relative group overflow-hidden ${
                                                        item.type === 'comment_deleted' 
                                                            ? 'bg-red-50 border border-red-200' 
                                                            : 'bg-secondary-50'
                                                    }`}>
                                                         <div className="flex justify-between items-start gap-2">
                                                             <div className="flex-1 min-w-0 overflow-hidden">
                                                                {/* Enhanced comment display with parsed references */}
                                                                {item.details?.github_references && item.details.github_references.length > 0 ? (
                                                                    <div className="space-y-2">
                                                                        <div className="flex items-center gap-2">
                                                                            {item.type === 'comment_deleted' ? (
                                                                                <div className="flex items-center gap-2">
                                                                                     <div className="text-gray-400 italic line-through break-words leading-relaxed overflow-wrap-anywhere">{renderCommentWithMentions(item.details.comment)}</div>
                                                                                    <span className="text-xs text-red-500 font-medium">(deleted)</span>
                                                                                </div>
                                                                            ) : editingCommentId === item.details?.comment_id ? (
                                                                                <div className="flex-1">
                                                                                    <textarea
                                                                                        value={inlineEditText}
                                                                                        onChange={(e) => setInlineEditText(e.target.value)}
                                                                                        onKeyDown={(e) => {
                                                                                            if (e.key === 'Enter' && e.ctrlKey) {
                                                                                                e.preventDefault();
                                                                                                handleSaveInlineComment(item.details?.comment_id);
                                                                                            } else if (e.key === 'Escape') {
                                                                                                e.preventDefault();
                                                                                                handleCancelInlineEdit();
                                                                                            }
                                                                                        }}
                                                                                        className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                                                                        rows={2}
                                                                                        autoFocus
                                                                                    />
                                                                                    <div className="text-xs text-secondary-500 mt-1">
                                                                                        Press Ctrl+Enter to save, Esc to cancel
                                                                                    </div>
                                                                                    <div className="flex gap-2 mt-2">
                                                                                        <button
                                                                                            onClick={() => handleSaveInlineComment(item.details?.comment_id)}
                                                                                            disabled={!inlineEditText.trim() || isUpdatingComment}
                                                                                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                        >
                                                                                            {isUpdatingComment ? 'Saving...' : 'Save'}
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={() => handleCancelInlineEdit()}
                                                                                            disabled={isUpdatingComment}
                                                                                            className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                        >
                                                                                            Cancel
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <>
                                                                                    {/* Check if this is a voice note */}
                                                                                    {item.details?.voice_note_url ? (
                                                                                        <div className="space-y-2">
                                                                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                                                                <Mic className="h-4 w-4" />
                                                                                            </div>
                                                                                            <VoiceNotePlayer 
                                                                                                audioUrl={item.details.voice_note_url}
                                                                                                duration={item.details.voice_note_duration}
                                                                                                className="max-w-xs"
                                                                                            />
                                                                                            {item.details?.comment && item.details.comment !== '[Voice Note]' && (
                                                                                            <div className="mt-2 text-sm text-gray-700 break-words leading-relaxed overflow-wrap-anywhere">
                                                                                                {renderCommentWithMentions(item.details.comment)}
                                                                                            </div>
                                                                                            )}
                                                                                        </div>
                                                                                    ) : (
                                                                                         <div className="text-gray-900 break-words leading-relaxed overflow-wrap-anywhere">{renderCommentWithMentions(item.details.comment)}</div>
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {item.details.github_references.map((ref, idx) => (
                                                                                <div 
                                                                                    key={idx} 
                                                                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full cursor-pointer hover:bg-blue-200 transition-colors"
                                                                                    onMouseEnter={(e) => handleReferenceHover({
                                                                                        ...ref,
                                                                                        clientX: e.clientX,
                                                                                        clientY: e.clientY
                                                                                    })}
                                                                                    onMouseLeave={handleReferenceLeave}
                                                                                >
                                                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                                        {ref.type === 'commit' && (
                                                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                                                        )}
                                                                                        {ref.type === 'issue' && (
                                                                                            <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                                                                        )}
                                                                                        {ref.type === 'pr' && (
                                                                                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                                                                        )}
                                                                                        {ref.type === 'file' && (
                                                                                            <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                                                                                        )}
                                                                                    </svg>
                                                                                    <span className="font-medium">{ref.type}</span>
                                                                                    <span className="font-mono">{ref.value}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center gap-2">
                                                                        {item.type === 'comment_deleted' ? (
                                                                            <div className="flex items-center gap-2">
                                                                                 <div className="text-secondary-400 italic line-through break-words leading-relaxed overflow-wrap-anywhere">{renderCommentWithMentions(item.details?.comment)}</div>
                                                                                <span className="text-xs text-red-500 font-medium">(deleted)</span>
                                                                            </div>
                                                                        ) : editingCommentId === item.details?.comment_id ? (
                                                                            <div className="flex-1">
                                                                                <textarea
                                                                                    value={inlineEditText}
                                                                                    onChange={(e) => setInlineEditText(e.target.value)}
                                                                                    onKeyDown={(e) => {
                                                                                        if (e.key === 'Enter' && e.ctrlKey) {
                                                                                            e.preventDefault();
                                                                                            handleSaveInlineComment(item.details?.comment_id);
                                                                                        } else if (e.key === 'Escape') {
                                                                                            e.preventDefault();
                                                                                            handleCancelInlineEdit();
                                                                                        }
                                                                                    }}
                                                                                    className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                                                                    rows={2}
                                                                                    autoFocus
                                                                                />
                                                                                <div className="text-xs text-secondary-500 mt-1">
                                                                                    Press Ctrl+Enter to save, Esc to cancel
                                                                                </div>
                                                                                <div className="flex gap-2 mt-2">
                                                                                    <button
                                                                                        onClick={() => handleSaveInlineComment(item.details?.comment_id)}
                                                                                        disabled={!inlineEditText.trim() || isUpdatingComment}
                                                                                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                    >
                                                                                        {isUpdatingComment ? 'Saving...' : 'Save'}
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => handleCancelInlineEdit()}
                                                                                        disabled={isUpdatingComment}
                                                                                        className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                    >
                                                                                        Cancel
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                {/* Check if this is a voice note */}
                                                                                {item.details?.voice_note_url ? (
                                                                                    <div className="space-y-2">
                                                                                       
                                                                                        <VoiceNotePlayer 
                                                                                            audioUrl={item.details.voice_note_url}
                                                                                            duration={item.details.voice_note_duration}
                                                                                            className="max-w-xs"
                                                                                        />
                                                                                        {item.details?.comment && item.details.comment !== '[Voice Note]' && (
                                                                                            <div className="mt-2 text-sm text-gray-700 break-words leading-relaxed overflow-wrap-anywhere">
                                                                                                {renderCommentWithMentions(item.details.comment)}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    ) : (
                                                                                     <div className="break-words leading-relaxed overflow-wrap-anywhere">{renderCommentWithMentions(item.details?.comment)}</div>
                                                                                 )}
                                                                             </>
                                                                         )}
                                                                     </div>
                                                                )}
                                                            </div>
                                                            {/* Action buttons - only show for own comments or admins/project leads, and not for deleted comments, and hide when editing */}
                                                            {(item.user_id === user?.id || user?.role === 'admin' || user?.role === 'project_lead') && item.type !== 'comment_deleted' && editingCommentId !== item.details?.comment_id && !item.details?.voice_note_url && (
                                                                <div className="flex items-center gap-1 ml-2">
                                                                    {/* Edit button - only for own comments and not for voice notes */}
                                                                    {item.user_id === user?.id && (
                                                                        <button
                                                                            onClick={() => handleEditComment(item.details?.comment_id, item.details?.comment || '')}
                                                                            className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                                                                            title="Edit comment"
                                                                        >
                                                                            <Edit className="h-3 w-3" />
                                                                        </button>
                                                                    )}
                                                                    {/* Delete button */}
                                                                    <button
                                                                        onClick={() => handleDeleteComment(item.details?.comment_id)}
                                                                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                                                        title="Delete comment"
                                                                    >
                                                                        <Trash2 className="h-3 w-3" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Emoji Reactions Section */}
                                                        {item.type === 'comment' && item.type !== 'comment_deleted' && (
                                                            <div className="relative mt-2 flex items-center gap-2">
                                                                {/* Existing Reactions */}
                                                                {item.details?.reactions && Object.keys(item.details.reactions).length > 0 && (
                                                                    <div className="flex items-center gap-1">
                                                                        {Object.entries(item.details.reactions).map(([emoji, users]) => (
                                                                            <button
                                                                                key={emoji}
                                                                                onClick={() => {
                                                                                    const hasReacted = users.some(u => u.id === user?.id);
                                                                                    if (hasReacted) {
                                                                                        handleRemoveReaction(item.details?.comment_id, emoji);
                                                                                    } else {
                                                                                        handleAddReaction(item.details?.comment_id, emoji);
                                                                                    }
                                                                                }}
                                                                                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                                                                                    users.some(u => u.id === user?.id) 
                                                                                        ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                                                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                                }`}
                                                                                title={`${users.map(u => u.full_name || u.username).join(', ')}`}
                                                                            >
                                                                                <span>{emoji}</span>
                                                                                <span>{users.length}</span>
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                
                                                                
                                                                
                                                                {/* Emoji Picker */}
                                                                {showEmojiPicker === item.details?.comment_id && (
                                                                    <div className="emoji-picker-container absolute bottom-full left-0 mb-2 z-50 bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg shadow-lg p-3 w-80">
                                                                        {/* Category Tabs */}
                                                                        <div className="flex gap-1 mb-3 border-b border-gray-100">
                                                                            {Object.keys(emojiCategories).map((category) => (
                                                                                <button
                                                                                    key={category}
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        e.stopPropagation();
                                                                                        setSelectedEmojiCategory(category);
                                                                                    }}
                                                                                    className={`px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${
                                                                                        selectedEmojiCategory === category
                                                                                            ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500'
                                                                                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                                                                    }`}
                                                                                    type="button"
                                                                                >
                                                                                    {category === 'recent' ? '🕒' : 
                                                                                     category === 'smileys' ? '😀' :
                                                                                     category === 'gestures' ? '👋' :
                                                                                     category === 'people' ? '👥' :
                                                                                     category === 'animals' ? '🐶' :
                                                                                     category === 'food' ? '🍎' :
                                                                                     category === 'activities' ? '⚽' :
                                                                                     category === 'travel' ? '🚗' :
                                                                                     category === 'objects' ? '📱' :
                                                                                     category === 'symbols' ? '❤️' : '📱'}
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                        
                                                                        {/* Emoji Grid */}
                                                                        <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                                                                            {emojiCategories[selectedEmojiCategory].map((emoji) => (
                                                                                <button
                                                                                    key={emoji}
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        e.stopPropagation();
                                                                                        handleAddReaction(item.details?.comment_id, emoji);
                                                                                        setShowEmojiPicker(null);
                                                                                    }}
                                                                                    className="p-2 hover:bg-gray-100 rounded text-lg transition-colors"
                                                                                    title={emoji}
                                                                                    type="button"
                                                                                >
                                                                                    {emoji}
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {item.type === 'status_change' && (
                                                    <div className="text-green-700 text-sm">Changed status to <span className="font-semibold">{item.details?.status}</span></div>
                                                )}
                                                {item.type === 'screenshot' && (
                                                    <div className="flex items-center gap-2">
                                                        {item.details?.screenshot ? (
                                                            // Use enriched screenshot data if available
                                                            item.details.screenshot.content_type?.startsWith('image/') ? (
                                                                <Attachment
                                                                    attachment={item.details.screenshot}
                                                                    size="xs"
                                                                    onClick={(attachment, url) => setSelectedScreenshot(url)}
                                                                    showPreview={true}
                                                                    showDownload={true}
                                                                />
                                                            ) : item.details.screenshot.content_type?.includes('pdf') ? (
                                                                <div 
                                                                    className="h-12 w-16 rounded border bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors"
                                                                    onClick={() => handleFilePreview(item.details.screenshot)}
                                                                    title="PDF Document"
                                                                >
                                                                    <div className="text-center">
                                                                        <div className="text-red-500 text-xl">📄</div>
                                                                        <div className="text-red-700 text-xs font-medium">PDF</div>
                                                                    </div>
                                                                </div>
                                                            ) : item.details.screenshot.content_type?.includes('word') ? (
                                                                <div 
                                                                    className="h-12 w-16 rounded border bg-blue-50 flex items-center justify-center cursor-pointer hover:bg-blue-100 transition-colors"
                                                                    onClick={() => handleFilePreview(item.details.screenshot)}
                                                                    title="Word Document"
                                                                >
                                                                    <div className="text-center">
                                                                        <WordIcon className="h-6 w-6 mx-auto mb-1" />
                                                                        <div className="text-blue-700 text-xs font-medium">Word</div>
                                                                    </div>
                                                                </div>
                                                            ) : item.details.screenshot.content_type?.includes('excel') || item.details.screenshot.content_type?.includes('spreadsheet') ? (
                                                                <div 
                                                                    className="h-12 w-16 rounded border bg-green-50 flex items-center justify-center cursor-pointer hover:bg-green-100 transition-colors"
                                                                    onClick={() => handleFilePreview(item.details.screenshot)}
                                                                    title="Excel Spreadsheet"
                                                                >
                                                                    <div className="text-center">
                                                                        <ExcelIcon className="h-6 w-6 mx-auto mb-1" />
                                                                        <div className="text-green-700 text-xs font-medium">Excel</div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div 
                                                                    className="h-12 w-16 rounded border bg-gray-50 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors"
                                                                    onClick={() => handleFilePreview(item.details.screenshot)}
                                                                    title="File"
                                                                >
                                                                    <div className="text-center">
                                                                        <div className="text-gray-500 text-xl">📎</div>
                                                                        <div className="text-gray-700 text-xs font-medium">File</div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        ) : (
                                                            // Fallback to filename-based logic for backward compatibility
                                                            item.details?.filename?.toLowerCase().match(/\.(png|jpg|jpeg)$/) ? (
                                                                <img
                                                                    src={`${BACKEND_URL}/uploads/task_screenshots/${taskId}/${item.details?.filename}`}
                                                                    alt="Attachment"
                                                                    className="h-12 w-16 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                                                                    onClick={() => setSelectedScreenshot(`${BACKEND_URL}/uploads/task_screenshots/${taskId}/${item.details?.filename}`)}
                                                                />
                                                            ) : (
                                                                <div className="h-12 w-16 rounded border bg-secondary-100 flex items-center justify-center">
                                                                    {item.details?.filename?.toLowerCase().endsWith('.pdf') ? '📄' : 
                                                                     item.details?.filename?.toLowerCase().match(/\.(doc|docx)$/) ? '📝' : 
                                                                     item.details?.filename?.toLowerCase().match(/\.(xls|xlsx)$/) ? '📊' : '📎'}
                                                                </div>
                                                            )
                                                        )}
                                                        <div className="flex flex-col">
                                                            <span className="text-xs text-secondary-500">File uploaded</span>
                                                            {item.details?.screenshot?.filename && (
                                                                <span className="text-xs text-gray-400 truncate max-w-32" title={item.details.screenshot.filename}>
                                                                    {item.details.screenshot.filename}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                {item.type === 'timer_start' && (
                                                    <div className="flex items-center gap-2 py-1">
                                                        <Play className="h-5 w-5 text-green-500" />
                                                        <span>
                                                            <span className="font-semibold text-green-700">Started timer</span>
                                                            <span className="ml-1 text-secondary-700">at {item.timestamp ? toUTCPlus5(item.timestamp) : ''}</span>
                                                        </span>
                                                    </div>
                                                )}
                                                {item.type === 'timer_stopped' && (
                                                    <div className="flex items-center gap-2 py-1">
                                                        <Square className="h-5 w-5 text-red-500" />
                                                        <span>
                                                            <span className="font-semibold text-red-700">Timer Stopped:</span>
                                                            <span className="ml-1 text-secondary-700">
                                                                Logged time {
                                                                    item.details?.start_time && item.details?.end_time
                                                                        ? formatDurationHMS((new Date(item.details.end_time) - new Date(item.details.start_time)) / 1000)
                                                                        : formatDurationHMS(item.details?.duration || 0)
                                                                }
                                                            </span>
                                                        </span>
                                                    </div>
                                                )}
                                                {item.type === 'screenshot_deleted' && (
                                                    <div className="flex items-center gap-2">
                                                        <Image className="h-5 w-5 text-pink-500" />
                                                        <span className="text-xs text-secondary-500">Screenshot deleted</span>
                                                    </div>
                                                )}
                                                {item.type === 'task_assigned' && (
                                                    <div className="text-blue-700 text-sm">
                                                        {item.details?.message}
                                                    </div>
                                                )}
                                                {item.type === 'task_created' && (
                                                    <div className="text-secondary-700 text-sm">
                                                        {item.user_id === user?.id ? 'You created this task' : `${item.user?.full_name || item.user?.username || 'Someone'} created this task`}
                                                    </div>
                                                )}
                                                {item.type === 'task_deleted' && (
                                                    <div className="text-red-700 text-sm">
                                                        Task deleted by {item.user?.full_name || item.user?.username || 'Someone'}
                                                    </div>
                                                )}
                                                {item.type === 'subtask_created' && (
                                                    <div className="text-sm text-primary-700 dark:text-primary-300">
                                                        Subtask created by {item.user?.full_name || item.user?.username || 'Someone'}
                                                    </div>
                                                )}
                                                {item.type === 'subtask_updated' && (
                                                    <div className="text-indigo-700 text-sm">
                                                        Subtask updated by {item.user?.full_name || item.user?.username || 'Someone'}
                                                        {item.details?.changes && (
                                                            <div className="text-xs text-secondary-600 mt-1">
                                                                {Object.keys(item.details.changes.updated || {}).join(', ')} changed
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {item.type === 'subtask_deleted' && (
                                                    <div className="text-red-700 text-sm">
                                                        Subtask deleted by {item.user?.full_name || item.user?.username || 'Someone'}
                                                    </div>
                                                )}
                                                {(item.type === 'time_entry_created' || item.type === 'time_entry') && (
                                                    <div className="text-sm text-secondary-800 dark:text-secondary-200">
                                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                                            <span>
                                                                Tracked{' '}
                                                                <span className="font-semibold text-primary-700 dark:text-primary-300 tabular-nums">
                                                                    {formatTime(item.details?.duration || 0)}
                                                                </span>
                                                            </span>
                                                            {item.details?.is_manual && <StatusPill status="Manual" />}
                                                        </div>
                                                        {item.details?.description && (
                                                            <p className="text-xs text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900/40 border border-secondary-100 dark:border-secondary-700 rounded-md px-2.5 py-2 leading-relaxed">
                                                                {item.details.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                                {item.type === 'time_entry_updated' && (
                                                    <div className="text-sm text-secondary-800 dark:text-secondary-200">
                                                        Time entry updated
                                                        {item.details?.duration && (
                                                            <span className="ml-2 font-semibold text-primary-700 dark:text-primary-300 tabular-nums">
                                                                {formatTime(item.details.duration)}
                                                            </span>
                                                        )}
                                                        {item.details?.description && (
                                                            <p className="mt-1 text-xs text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900/40 border border-secondary-100 dark:border-secondary-700 rounded-md px-2.5 py-2 leading-relaxed">
                                                                {item.details.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                                {item.type === 'time_entry_deleted' && (
                                                    <div className="text-red-700 text-sm">
                                                        Time entry deleted by {item.user?.full_name || item.user?.username || 'Someone'}
                                                    </div>
                                                )}
                                                {item.type === 'task_updated' && (
                                                    <div className="text-blue-700 text-sm">
                                                        Task updated by {item.user?.full_name || item.user?.username || 'Someone'}
                                                        {item.details?.changes && (
                                                            <div className="text-xs text-secondary-600 mt-1">
                                                                {Object.keys(item.details.changes.updated || {}).join(', ')} changed
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {item.type === 'subtask_status_changed' && (
                                                    <div className="text-yellow-700 text-sm">
                                                        Subtask status changed to <span className="font-semibold">{item.details?.status}</span>
                                                    </div>
                                                )}
                                                {item.type === 'subtask_assigned' && (
                                                    <div className="text-teal-700 text-sm">
                                                        {item.details?.assigned_user_name ? (
                                                            <>Subtask assigned to <span className="font-semibold">{item.details.assigned_user_name}</span> by {item.details?.assigner_name || 'Someone'}</>
                                                        ) : (
                                                            <>Subtask assigned by {item.details?.assigner_name || 'Someone'}</>
                                                        )}
                                                    </div>
                                                )}
                                                {item.type === 'github_oauth_connected' && (
                                                    <div className="text-green-700 text-sm">
                                                        Connected to GitHub repository <span className="font-semibold">{item.details?.repository}</span>
                                                        {item.details?.branch_name && (
                                                            <span className="ml-1">on branch <span className="font-semibold">{item.details.branch_name}</span></span>
                                                        )}
                                                    </div>
                                                )}
                                                {item.type === 'github_oauth_disconnected' && (
                                                    <div className="text-red-700 text-sm">
                                                        Disconnected from GitHub repository <span className="font-semibold">{item.details?.repository}</span>
                                                        {item.details?.branch_name && (
                                                            <span className="ml-1">on branch <span className="font-semibold">{item.details.branch_name}</span></span>
                                                        )}
                                                    </div>
                                                )}
                                                {item.type === 'github_collaborator_added' && (
                                                    <div className="text-blue-700 text-sm">
                                                        Added <span className="font-semibold">@{item.details?.username}</span> as collaborator with <span className="font-semibold">{item.details?.permission}</span> permission
                                                    </div>
                                                )}
                                                {item.type === 'github_collaborator_removed' && (
                                                    <div className="text-red-700 text-sm">
                                                        Removed <span className="font-semibold">@{item.details?.username}</span> as collaborator
                                                    </div>
                                                )}
                                                {item.type === 'github_commit_created' && (
                                                    <div className="text-green-700 text-sm">
                                                        Created commit <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">{item.details?.commit_sha?.substring(0, 7)}</span>
                                                        {item.details?.branch && (
                                                            <span className="ml-1">on branch <span className="font-semibold">{item.details.branch}</span></span>
                                                        )}
                                                    </div>
                                                )}
                                                {item.type === 'github_pull_request_created' && (
                                                    <div className="text-purple-700 text-sm">
                                                        Created pull request #{item.details?.pr_number}
                                                        {item.details?.pr_url && (
                                                            <a 
                                                                href={item.details.pr_url} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="ml-1 text-purple-600 hover:text-purple-800 underline"
                                                            >
                                                                View on GitHub
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                                {item.type === 'github_commit_comment' && (
                                                    <div className="text-green-700 text-sm">
                                                        <div className="bg-green-50 rounded p-2 mb-2">
                                                            <span>{renderCommentWithMentions(item.details?.comment)}</span>
                                                        </div>
                                                        {item.details?.github_commit && (
                                                            <div className="flex items-center gap-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                                </svg>
                                                                <span className="font-mono">
                                                                    {item.details.github_commit.sha.substring(0, 7)}
                                                                </span>
                                                                <span>•</span>
                                                                <span className="truncate max-w-[200px]">{item.details.github_commit.message}</span>
                                                                <span>•</span>
                                                                <a 
                                                                    href={`https://github.com/${item.details.github_commit.repository}/commit/${item.details.github_commit.sha}`}
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="text-green-700 hover:text-green-800 underline"
                                                                >
                                                                    View on GitHub
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                
                                                {/* Time Entry with Commit */}
                                                {item.type === 'time_entry_with_commit' && (
                                                    <div className="text-blue-700 text-sm">
                                                        <div className="bg-blue-50 rounded p-2 mb-2">
                                                            <span>Time entry logged: {item.details?.description}</span>
                                                        </div>
                                                        {item.details?.commit_sha && (
                                                            <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                                </svg>
                                                                <span className="font-mono">{item.details.commit_sha}</span>
                                                                <span>•</span>
                                                                <span>{item.details.commit_message}</span>
                                                                <span>•</span>
                                                                <span>{item.details.duration_minutes} min</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                
                                                {/* Commit Time Linked */}
                                                {item.type === 'commit_time_linked' && (
                                                    <div className="text-purple-700 text-sm">
                                                        <div className="bg-purple-50 rounded p-2 mb-2">
                                                            <span>Commit linked to time entry</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                            </svg>
                                                            <span className="font-mono">{item.details?.commit_sha}</span>
                                                            <span>•</span>
                                                            <span>{item.details?.commit_message}</span>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Release Feature Created */}
                                                {item.type === 'release_feature_created' && (
                                                    <div className="text-indigo-700 text-sm">
                                                        <div className="bg-indigo-50 rounded p-2 mb-2">
                                                            <span>Release feature created: {item.details?.feature_name}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full">
                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                            <span>Version: {item.details?.release_version}</span>
                                                            <span>•</span>
                                                            <span>{item.details?.commit_count} commits</span>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Release Feature Updated */}
                                                {item.type === 'release_feature_updated' && (
                                                    <div className="text-indigo-700 text-sm">
                                                        <div className="bg-indigo-50 rounded p-2 mb-2">
                                                            <span>Release feature updated: {item.details?.feature_name}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full">
                                                            <span>Version: {item.details?.release_version}</span>
                                                            <span>•</span>
                                                            <span>Status: {item.details?.new_status}</span>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Release Feature Deployed */}
                                                {item.type === 'release_feature_deployed' && (
                                                    <div className="text-green-700 text-sm">
                                                        <div className="bg-green-50 rounded p-2 mb-2">
                                                            <span>Release feature deployed: {item.details?.feature_name}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                            <span>Version: {item.details?.release_version}</span>
                                                            <span>•</span>
                                                            <span>Deployed successfully</span>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Commit Added to Release */}
                                                {item.type === 'commit_added_to_release' && (
                                                    <div className="text-indigo-700 text-sm">
                                                        <div className="bg-indigo-50 rounded p-2 mb-2">
                                                            <span>Commits added to release: {item.details?.feature_name}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full">
                                                            <span>Version: {item.details?.release_version}</span>
                                                            <span>•</span>
                                                            <span>{item.details?.new_commits?.length || 0} new commits</span>
                                                            <span>•</span>
                                                            <span>Total: {item.details?.total_commits} commits</span>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Code Review Created */}
                                                {item.type === 'code_review_created' && (
                                                    <div className="text-orange-700 text-sm">
                                                        <div className="bg-orange-50 rounded p-2 mb-2">
                                                            <span>Code review created for PR #{item.details?.pr_number}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                                                            <span>{item.details?.pr_title}</span>
                                                            <span>•</span>
                                                            <span>Reviewer: {item.details?.reviewer_name}</span>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Code Review Updated */}
                                                {item.type === 'code_review_updated' && (
                                                    <div className="text-orange-700 text-sm">
                                                        <div className="bg-orange-50 rounded p-2 mb-2">
                                                            <span>Code review updated for PR #{item.details?.pr_number}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                                                            <span>{item.details?.pr_title}</span>
                                                            <span>•</span>
                                                            <span>Status: {item.details?.new_status}</span>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Code Review Completed */}
                                                {item.type === 'code_review_completed' && (
                                                    <div className="text-green-700 text-sm">
                                                        <div className="bg-green-50 rounded p-2 mb-2">
                                                            <span>Code review completed for PR #{item.details?.pr_number}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                                            <span>{item.details?.pr_title}</span>
                                                            <span>•</span>
                                                            <span>Status: {item.details?.new_status}</span>
                                                            {item.details?.review_comments && (
                                                                <>
                                                                    <span>•</span>
                                                                    <span>Comments: {item.details.review_comments}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* GitHub Branch Updated */}
                                                {item.type === 'github_branch_updated' && (
                                                    <div className="text-purple-700 text-sm">
                                                        <div className="bg-purple-50 rounded p-2 mb-2">
                                                            <span>GitHub branch updated</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                                                            <span>Repository: {item.details?.repository}</span>
                                                            <span>•</span>
                                                            <span>{item.details?.old_branch} → {item.details?.new_branch}</span>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* PR Review Status Changed */}
                                                {item.type === 'pr_review_status_changed' && (
                                                    <div className="text-blue-700 text-sm">
                                                        <div className="bg-blue-50 rounded p-2 mb-2">
                                                            <span>PR status updated: #{item.details?.pr_number}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                                            <span>{item.details?.pr_title}</span>
                                                            <span>•</span>
                                                            <span>Status: {item.details?.new_pr_status}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            {isLoadingMore && (
                                <div className="w-full py-2.5 px-3 bg-secondary-50 dark:bg-secondary-900/40 text-secondary-600 dark:text-secondary-300 text-sm rounded-lg border border-secondary-200 dark:border-secondary-700 flex items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary-600"></div>
                                    Loading more…
                                </div>
                            )}
                            {activity.length === 0 && (
                                <EmptyState
                                    icon={Clock}
                                    title="No activity yet"
                                    description="Start working on this task to see updates here."
                                    className="py-8"
                                />
                            )}
                        </div>
                        <div className="px-3 pb-3 pt-3 border-t border-secondary-200 dark:border-secondary-700">
                            {/* GitHub Commit Attachment Indicator */}
                            {selectedCommit && taskData?.task?.github_integration && (
                                <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg overflow-hidden" style={{ maxWidth: '100%', wordWrap: 'break-word' }}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3 flex-1 min-w-0 overflow-hidden" style={{ maxWidth: 'calc(100% - 2rem)' }}>
                                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="flex-1 min-w-0 overflow-hidden" style={{ maxWidth: 'calc(100% - 3rem)' }}>
                                                <div 
                                                    className="text-sm font-medium text-green-800 cursor-pointer hover:text-green-900 transition-colors mb-1 break-words overflow-wrap-anywhere"
                                                    onMouseEnter={(e) => handleReferenceHover({
                                                        type: 'commit',
                                                        value: selectedCommit.sha,
                                                        clientX: e.clientX,
                                                        clientY: e.clientY
                                                    })}
                                                    onMouseLeave={handleReferenceLeave}
                                                >
                                                    Commit attached: {selectedCommit.sha.substring(0, 7)}
                                                </div>
                                                <div className="text-xs text-green-600 break-words leading-relaxed overflow-wrap-anywhere word-break-break-all hyphens-auto line-clamp-2" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', hyphens: 'auto' }}>
                                                    {selectedCommit.message}
                                                </div>
                                                <a
                                                    href={`https://github.com/${taskData.task.github_integration.repository}/commit/${selectedCommit.sha}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 mt-2 transition-colors break-words"
                                                >
                                                    <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="break-words overflow-wrap-anywhere">View on GitHub</span>
                                                </a>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedCommit(null)}
                                            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors flex-shrink-0"
                                            title="Remove commit attachment"
                                        >
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}
                            

                            
                            <form onSubmit={handleAddComment} className="relative bg-secondary-50/80 dark:bg-secondary-900/40 border border-secondary-200 dark:border-secondary-700 rounded-lg focus-within:ring-2 focus-within:ring-primary-500/30 focus-within:border-primary-500 transition-all">
                                <div className="flex flex-col gap-2 p-3">
                                    {!showVoiceRecorder ? (
                                        <div className="flex-1 relative">
                                            <textarea
                                                placeholder="Add a comment..."
                                                className="w-full bg-transparent border-0 resize-none focus:outline-none text-secondary-900 dark:text-white placeholder-secondary-400 leading-relaxed min-h-[52px] max-h-[200px] text-sm"
                                                value={newComment}
                                                onChange={handleCommentInputChange}
                                                ref={commentInputRef}
                                                onBlur={() => setTimeout(() => setShowMentionSuggestions(false), 200)}
                                                onClick={e => setCursorPosition(e.target.selectionStart)}
                                                onKeyUp={e => setCursorPosition(e.target.selectionStart)}
                                                onInput={(e) => {
                                                    // Auto-resize textarea
                                                    e.target.style.height = 'auto';
                                                    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                                                }}
                                                disabled={taskData.task.status === 'Done' && (user?.role === 'member')}
                                                rows="2"
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex-1">
                                            <VoiceRecorder
                                                onSend={handleVoiceNoteSend}
                                                onCancel={handleVoiceRecorderCancel}
                                                disabled={addingComment || (taskData.task.status === 'Done' && (user?.role === 'member'))}
                                                autoStart={true}
                                            />
                                        </div>
                                    )}
                                    
                                    <div className="flex items-center justify-between gap-2 flex-shrink-0">
                                        <div className="flex items-center gap-2">
                                {/* Mention suggestions dropdown */}
                                {showMentionSuggestions && Array.isArray(taskData?.assigned_members) && (
                                    <div className="absolute left-0 bottom-12 z-50 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-xl w-64 max-h-48 overflow-y-auto">
                                        {taskData.assigned_members
                                            .filter(m => m.id !== user?.id)
                                            .filter(m =>
                                                !mentionFilter ||
                                                m.username.toLowerCase().includes(mentionFilter.toLowerCase()) ||
                                                (m.full_name && m.full_name.toLowerCase().includes(mentionFilter.toLowerCase()))
                                            )
                                            .map(member => (
                                                <div
                                                    key={member.id}
                                                    className="px-4 py-3 hover:bg-primary-50 cursor-pointer flex items-center gap-3 transition-colors duration-200"
                                                    onMouseDown={() => handleMentionSelect(member)}
                                                >
                                                    <div className="h-7 w-7 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs">
                                                        {member.full_name ? member.full_name[0] : member.username[0]}
                                                    </div>
                                                    <div className="flex-1">
                                                        <span className="font-medium text-secondary-900">@{member.username}</span>
                                                        {member.full_name && <div className="text-secondary-500 text-xs">{member.full_name}</div>}
                                                    </div>
                                                </div>
                                            ))}
                                        {taskData.assigned_members.filter(m => m.id !== user?.id).filter(m =>
                                            !mentionFilter ||
                                            m.username.toLowerCase().includes(mentionFilter.toLowerCase()) ||
                                            (m.full_name && m.full_name.toLowerCase().includes(mentionFilter.toLowerCase()))
                                        ).length === 0 && (
                                                <div className="px-4 py-3 text-secondary-400 text-sm text-center">No matches found</div>
                                            )}
                                    </div>
                                )}
                                        {/* GitHub icon for commit attachment - only show when repo is connected */}
                                        {taskData?.task?.github_integration && (
                                            <button
                                                type="button"
                                                onClick={() => user?.github_connected ? setShowGitHubCommitModal(true) : setShowGitHubConnectModal(true)}
                                                className="flex items-center justify-center w-8 h-8 rounded-md transition-colors hover:bg-secondary-100 dark:hover:bg-secondary-700 text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200"
                                                title={user?.github_connected ? 'Attach GitHub Commit' : 'Connect GitHub to attach commits'}
                                            >
                                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                                </svg>
                                            </button>
                                        )}
                                        
                                        {/* Voice recording button */}
                                        <button
                                            type="button"
                                            onClick={toggleVoiceRecorder}
                                            className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
                                                showVoiceRecorder 
                                                    ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                                                    : 'hover:bg-secondary-100 dark:hover:bg-secondary-700 text-secondary-500 hover:text-secondary-800'
                                            } ${taskData.task.status === 'Done' && (user?.role === 'member') ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            disabled={taskData.task.status === 'Done' && (user?.role === 'member')}
                                            title="Record Voice Note"
                                        >
                                            {showVoiceRecorder ? (
                                                <MicOff className="h-4 w-4" />
                                            ) : (
                                                <Mic className="h-4 w-4" />
                                            )}
                                        </button>

                                        {/* Emoji picker button */}
                                        <button
                                            type="button"
                                            onClick={() => setShowEmojiPicker(showEmojiPicker === 'comment' ? null : 'comment')}
                                            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors text-secondary-500 hover:text-secondary-800"
                                            title="Add Emoji"
                                        >
                                            <Smile className="h-4 w-4" />
                                        </button>

                                        {/* File attachment button */}
                                        <label className={`cursor-pointer flex items-center justify-center w-8 h-8 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors ${taskData.task.status === 'Done' && (user?.role === 'member') ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                                                onChange={handleCommentAttachmentUpload}
                                                className="hidden"
                                                disabled={taskData.task.status === 'Done' && (user?.role === 'member')}
                                            />
                                            <Paperclip className="h-4 w-4 text-secondary-500" title="Attach File (PDF, Word, PNG, JPG - Max 5MB)" />
                                        </label>
                                
                                {/* Upload Progress for Pin Button */}
                                {isCommentUploading && (
                                    <div className="absolute top-full left-0 right-0 mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200 shadow-lg z-10">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                            <span className="text-blue-800 text-sm font-medium">Uploading file...</span>
                                        </div>
                                        <div className="w-full bg-blue-200 rounded-full h-1.5">
                                            <div 
                                                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                                style={{ width: `${commentUploadProgress}%` }}
                                            ></div>
                                        </div>
                                        <div className="text-right text-blue-700 text-xs mt-1">{commentUploadProgress}%</div>
                                    </div>
                                )}
                                
                                        </div>
                                        
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="!w-8 !h-8 !p-0 !rounded-md"
                                            disabled={addingComment || (taskData.task.status === 'Done' && (user?.role === 'member'))}
                                            title="Send Comment"
                                        >
                                            {addingComment ? (
                                                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                                            ) : (
                                                <Send className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Emoji Picker for Comment Input */}
                                {showEmojiPicker === 'comment' && (
                                    <div className="emoji-picker-container absolute bottom-full left-0 mb-2 bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg shadow-lg p-3 z-50 w-80">
                                        {/* Category Tabs */}
                                        <div className="flex gap-1 mb-3 border-b border-gray-100">
                                            {Object.keys(emojiCategories).map((category) => (
                                                <button
                                                    key={category}
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setSelectedEmojiCategory(category);
                                                    }}
                                                    className={`px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${
                                                        selectedEmojiCategory === category
                                                            ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500'
                                                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {category === 'recent' ? '🕒' : 
                                                     category === 'smileys' ? '😀' :
                                                     category === 'gestures' ? '👋' :
                                                     category === 'people' ? '👥' :
                                                     category === 'animals' ? '🐶' :
                                                     category === 'food' ? '🍎' :
                                                     category === 'activities' ? '⚽' :
                                                     category === 'travel' ? '🚗' :
                                                     category === 'objects' ? '📱' :
                                                     category === 'symbols' ? '❤️' : '📱'}
                                                </button>
                                            ))}
                                        </div>
                                        
                                        {/* Emoji Grid */}
                                        <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                                            {emojiCategories[selectedEmojiCategory].map((emoji) => (
                                                <button
                                                    key={emoji}
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        insertEmojiIntoComment(emoji);
                                                    }}
                                                    className="p-2 hover:bg-gray-100 rounded text-lg transition-colors"
                                                    title={emoji}
                                                >
                                                    {emoji}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </form>
                        </div>
                        </>
                        ) : (
                            <div className="flex-1 flex flex-col px-3 py-3" style={{ maxHeight: '500px', minHeight: '300px', height: '60vh' }}>
                                {/* Notes List */}
                                <div className="flex-1 overflow-y-auto space-y-2 mb-3">
                                    {notesLoading ? (
                                        <div className="text-center py-8 text-secondary-500">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto mb-3"></div>
                                            <p className="text-sm">Loading notes…</p>
                                        </div>
                                    ) : notes.length === 0 ? (
                                        <EmptyState
                                            icon={StickyNote}
                                            title="No notes yet"
                                            description="Add your first note below."
                                            className="py-8"
                                        />
                                    ) : (
                                        notes.map((note) => (
                                            <div key={note.id} className="bg-white dark:bg-secondary-800/60 rounded-lg p-3 border border-secondary-200 dark:border-secondary-700 hover:border-secondary-300 transition-colors">
                                                <div className="flex items-start justify-between gap-2 mb-1.5">
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs">
                                                            {note.created_by_name ? note.created_by_name[0].toUpperCase() : 'U'}
                                                        </div>
                                                        <span className="font-medium text-secondary-900 text-xs">{note.created_by_name}</span>
                                                        <div className="flex items-center gap-1">
                                                            {note.visibility === 'private' ? (
                                                                <Lock className="h-3 w-3 text-red-500" title="Private note" />
                                                            ) : (
                                                                <Globe className="h-3 w-3 text-green-500" title="Public note" />
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                                                                            <span className="text-xs text-secondary-500">
                                                        {toUTCPlus5(note.created_at)}
                                                    </span>
                                                        {note.created_by === user?.id && (
                                                            <div className="flex items-center gap-0.5">
                                                                <button
                                                                    onClick={() => handleToggleNoteVisibility(note.id, note.visibility)}
                                                                    className="p-0.5 hover:bg-secondary-200 rounded transition-colors"
                                                                    title={`Make ${note.visibility === 'private' ? 'public' : 'private'}`}
                                                                    disabled={updatingNoteVisibility === note.id}
                                                                >
                                                                    {updatingNoteVisibility === note.id ? (
                                                                        <div className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-secondary-600"></div>
                                                                    ) : note.visibility === 'private' ? (
                                                                        <EyeOff className="h-2.5 w-2.5 text-secondary-600" />
                                                                    ) : (
                                                                        <Eye className="h-2.5 w-2.5 text-secondary-600" />
                                                                    )}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingNoteId(note.id);
                                                                        setEditingNoteContent(note.content);
                                                                    }}
                                                                    className="p-0.5 hover:bg-secondary-200 rounded transition-colors"
                                                                    title="Edit note"
                                                                >
                                                                    <Edit className="h-2.5 w-2.5 text-secondary-600" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteNoteClick(note)}
                                                                    className="p-0.5 hover:bg-red-100 rounded transition-colors"
                                                                    title="Delete note"
                                                                >
                                                                    <Trash2 className="h-2.5 w-2.5 text-red-500" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {editingNoteId === note.id ? (
                                                    <div className="space-y-2">
                                                        <textarea
                                                            value={editingNoteContent}
                                                            onChange={(e) => setEditingNoteContent(e.target.value)}
                                                            className="w-full p-2 border border-secondary-300 rounded-md text-sm resize-none"
                                                            rows={3}
                                                            placeholder="Edit your note..."
                                                        />
                                                        <div className="flex justify-end gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    setEditingNoteId(null);
                                                                    setEditingNoteContent('');
                                                                }}
                                                                className="px-3 py-1 text-sm text-secondary-600 hover:text-secondary-800 transition-colors"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={() => handleEditNote(note.id, editingNoteContent)}
                                                                className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                                disabled={editingNoteId === note.id}
                                                            >
                                                                {editingNoteId === note.id ? (
                                                                    <>
                                                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                                                        Saving...
                                                                    </>
                                                                ) : (
                                                                    'Save'
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <p className="text-xs text-secondary-800 whitespace-pre-wrap leading-relaxed">{note.content}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                                
                                {/* Add Note Form */}
                                <div className="border-t border-secondary-100 pt-4">
                                    <form onSubmit={handleAddNote} className="space-y-3">
                                        <div className="flex items-center gap-2 mb-2">
                                            <label className="flex items-center gap-1 text-sm">
                                                <input
                                                    type="radio"
                                                    name="visibility"
                                                    value="public"
                                                    checked={noteVisibility === 'public'}
                                                    onChange={(e) => setNoteVisibility(e.target.value)}
                                                    className="text-primary-600"
                                                />
                                                <Globe className="h-4 w-4 text-green-500" />
                                                Public
                                            </label>
                                            <label className="flex items-center gap-1 text-sm">
                                                <input
                                                    type="radio"
                                                    name="visibility"
                                                    value="private"
                                                    checked={noteVisibility === 'private'}
                                                    onChange={(e) => setNoteVisibility(e.target.value)}
                                                    className="text-primary-600"
                                                />
                                                <Lock className="h-4 w-4 text-red-500" />
                                                Private
                                            </label>
                                        </div>
                                        
                                        <textarea
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                            className="w-full p-3 border border-secondary-200 rounded-lg text-sm resize-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                                            rows={3}
                                            placeholder="Add a note..."
                                            disabled={taskData.task.status === 'Done' && (user?.role === 'member')}
                                        />
                                        
                                        <div className="flex justify-end">
                                            <button
                                                type="submit"
                                                className={`btn-primary flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
                                                    taskData.task.status === 'Done' && (user?.role === 'member') ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                                disabled={addingNote || !newNote.trim() || (taskData.task.status === 'Done' && (user?.role === 'member'))}
                                            >
                                                {addingNote ? (
                                                    <>
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                        Adding...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Plus className="h-4 w-4" />
                                                        Add Note
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </Panel>
                </div>
            </div>
            {selectedScreenshot && (
                <div
                    className="modal-backdrop-full flex items-center justify-center"
                    onClick={() => setSelectedScreenshot(null)}
                    style={{ cursor: 'zoom-out' }}
                >
                    <div
                        className={`bg-transparent p-0 relative flex flex-col items-center justify-center ${isScreenshotMaximized ? '' : ''}`}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="absolute top-2 right-2 flex gap-2 z-10">
                            <button
                                className="text-gray-600 hover:text-black text-xl font-bold"
                                onClick={() => setSelectedScreenshot(null)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                            <button
                                className="text-gray-600 hover:text-black text-xl font-bold"
                                onClick={() => setIsScreenshotMaximized(v => !v)}
                                aria-label={isScreenshotMaximized ? 'Minimize' : 'Maximize'}
                            >
                                {isScreenshotMaximized ? '🗗' : '🗖'}
                            </button>
                        </div>
                        <img
                            src={selectedScreenshot}
                            alt="Full Screenshot"
                            className={isScreenshotMaximized ? 'w-screen h-screen object-contain rounded' : 'max-w-[80vw] max-h-[80vh] object-contain rounded'}
                            style={{ display: 'block', margin: '0 auto' }}
                        />
                    </div>
                </div>
            )}
            
            {/* File Preview Modal for PDFs and Word Documents */}
            {filePreviewModal.open && filePreviewModal.file && (
                <div
                    className="modal-backdrop-full flex items-center justify-center animate-in fade-in duration-200"
                    onClick={() => setFilePreviewModal({ open: false, file: null })}
                >
                    <div
                        className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] max-h-[90dvh] overflow-hidden animate-in zoom-in-95 duration-200"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {filePreviewModal.file.filename}
                            </h3>
                            <div className="flex items-center gap-3">
                                <a
                                    href={filePreviewModal.file.freshUrl || filePreviewModal.file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm font-medium flex items-center gap-2 shadow-sm"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    Open in New Tab
                                </a>
                                <button
                                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all duration-200"
                                    onClick={() => setFilePreviewModal({ open: false, file: null })}
                                    aria-label="Close"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                        
                        <div className="border rounded-lg overflow-hidden relative">
                            {filePreviewModal.file.content_type?.startsWith('image/') ? (
                                <div className="w-full h-[50vh] sm:h-[60vh] md:h-[70vh] max-h-[70dvh] bg-gray-100 flex items-center justify-center">
                                    <img
                                        src={filePreviewModal.file.freshUrl || filePreviewModal.file.url}
                                        alt={filePreviewModal.file.filename}
                                        className="max-w-full max-h-full object-contain"
                                        style={{ maxHeight: '70vh' }}
                                    />
                                </div>
                            ) : filePreviewModal.file.content_type === 'application/pdf' ? (
                                <iframe
                                    src={`${filePreviewModal.file.freshUrl || filePreviewModal.file.url}#toolbar=0`}
                                    className="w-full h-[50vh] sm:h-[60vh] md:h-[70vh] max-h-[70dvh]"
                                    title={filePreviewModal.file.filename}
                                />
                            ) : filePreviewModal.file.content_type?.includes('word') || filePreviewModal.file.content_type?.includes('excel') || filePreviewModal.file.content_type?.includes('spreadsheet') ? (
                                <div className="w-full h-[50vh] sm:h-[60vh] md:h-[70vh] max-h-[70dvh] bg-gray-100 relative">
                                    {/* Loading overlay */}
                                    {filePreviewModal.loading && (
                                        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
                                            <div className="text-center">
                                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                                <div className={`text-lg font-medium ${filePreviewModal.file.content_type?.includes('word') ? 'text-blue-600' : 'text-green-600'}`}>
                                                    Loading {filePreviewModal.file.content_type?.includes('word') ? 'Word document' : 'Excel file'}...
                                                </div>
                                                <p className="text-gray-500 text-sm mt-2">This may take a few moments</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <iframe
                                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(filePreviewModal.file.freshUrl || filePreviewModal.file.url)}`}
                                        className="w-full h-full border-0"
                                        title={filePreviewModal.file.filename}
                                        onLoad={() => {
                                            // Hide loading when iframe loads
                                            setFilePreviewModal(prev => ({ ...prev, loading: false }));
                                        }}
                                        onError={() => {
                                            // Fallback if Office Online viewer fails
                                            setFilePreviewModal(prev => ({ ...prev, loading: false }));
                                            const fallbackDiv = document.querySelector('.office-fallback');
                                            if (fallbackDiv) {
                                                fallbackDiv.style.display = 'flex';
                                            }
                                        }}
                                    />
                                    
                                    {/* Fallback message if iframe fails to load */}
                                    <div className="office-fallback hidden absolute inset-0 bg-gray-100 flex items-center justify-center">
                                        <div className="text-center">
                                            <div className={`text-6xl mb-4 ${filePreviewModal.file.content_type?.includes('word') ? 'text-blue-500' : 'text-green-500'}`}>
                                                {filePreviewModal.file.content_type?.includes('word') ? '📝' : '📊'}
                                            </div>
                                            <p className="text-gray-600 mb-2">
                                                {filePreviewModal.file.content_type?.includes('word') ? 'Word document' : 'Excel file'} preview unavailable
                                            </p>
                                            <a
                                                href={filePreviewModal.file.freshUrl || filePreviewModal.file.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`px-6 py-3 text-white rounded-lg hover:opacity-90 transition-colors duration-200 font-medium shadow-sm flex items-center gap-2 mx-auto w-fit ${
                                                    filePreviewModal.file.content_type?.includes('word') ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
                                                }`}
                                            >
                                                <Download className="h-4 w-4" />
                                                Download & Open
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full h-[50vh] sm:h-[60vh] md:h-[70vh] max-h-[70dvh] bg-gray-100 flex items-center justify-center">
                                    <div className="text-center">
                                        <div className="text-gray-500 text-6xl mb-4">📎</div>
                                        <p className="text-gray-600 mb-2">File type not supported for preview</p>
                                        <a
                                            href={filePreviewModal.file.freshUrl || filePreviewModal.file.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium shadow-sm flex items-center gap-2 mx-auto w-fit"
                                        >
                                            <Download className="h-4 w-4" />
                                            Download File
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                            <div className="grid grid-cols-3 gap-4 text-sm">
                                <div className="text-center">
                                    <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">Size</div>
                                    <div className="font-medium text-gray-800">{(filePreviewModal.file.size / 1024).toFixed(1)} KB</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">Type</div>
                                    <div className="font-medium text-gray-800">{filePreviewModal.file.content_type}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">Uploaded</div>
                                    <div className="font-medium text-gray-800">{filePreviewModal.file.created_at ? toUTCPlus5(filePreviewModal.file.created_at) : 'Unknown'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* All Members Modal */}
            {showAllMembersModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-lg font-semibold text-secondary-900 flex items-center gap-2">
                                <User className="h-5 w-5 text-primary-600" />
                                All Assigned Members
                            </h3>
                            <button
                                onClick={() => setShowAllMembersModal(false)}
                                className="text-secondary-400 hover:text-secondary-600 p-1"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        
                        <div className="p-4 overflow-y-auto max-h-[60vh]">
                            <div className="space-y-3">
                                {taskData?.assigned_members && Array.isArray(taskData.assigned_members) && taskData.assigned_members.map(member => (
                                    <div key={member.id} className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors">
                                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-lg flex-shrink-0">
                                            {member.full_name ? member.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : member.username[0].toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-secondary-800">
                                                {member.full_name || member.username}
                                                {member.id === user?.id && ' (You)'}
                                            </div>
                                            <div className="text-xs text-secondary-500 capitalize">
                                                {member.role || 'Member'}
                                            </div>
                                            <div className="text-xs text-secondary-400">
                                                {member.email}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className="p-4 border-t bg-secondary-50">
                            <button
                                onClick={() => setShowAllMembersModal(false)}
                                className="w-full px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            <ConfirmModal
                open={deleteModal.open}
                title="Delete Attachment"
                message="Are you sure you want to delete this attachment?"
                onCancel={() => setDeleteModal({ open: false, screenshotId: null })}
                onConfirm={handleDeleteScreenshotConfirmed}
                confirmText="Delete"
                cancelText="Cancel"
            />

            {/* Delete Subtask Confirmation Modal */}
            <ConfirmModal
                open={showDeleteSubtaskModal}
                title="Delete Subtask"
                message="Are you sure you want to delete this subtask? This action cannot be undone."
                onConfirm={confirmDeleteSubtask}
                onCancel={() => {
                    setShowDeleteSubtaskModal(false);
                    setSubtaskToDelete(null);
                }}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonStyle="danger"
            />

            {/* Delete Comment Confirmation Modal */}
            <ConfirmModal
                open={showDeleteCommentModal}
                title="Delete Comment"
                message="Are you sure you want to delete this comment? This action cannot be undone."
                onConfirm={confirmDeleteComment}
                onCancel={() => {
                    setShowDeleteCommentModal(false);
                    setCommentToDelete(null);
                }}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonStyle="danger"
            />

            {/* Duplicate Task Confirmation Modal */}
            <ConfirmModal
                open={showDuplicateModal}
                title="Duplicate Task"
                message="Duplicate this task? A copy will be created in the same project with the same details (name, description, estimate, priority, tags, due date, and assignees). Time entries, comments, and attachments will not be copied."
                onConfirm={handleDuplicateTask}
                onCancel={() => setShowDuplicateModal(false)}
                confirmText="Duplicate"
                cancelText="Cancel"
                isLoading={createTaskMutation.isPending}
            />


            
            {/* Time Breakdown Modal */}
            {showTimeBreakdownModal && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowTimeBreakdownModal(false);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            setShowTimeBreakdownModal(false);
                        }
                    }}
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="time-breakdown-title"
                >
                    <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-xl border border-secondary-200 dark:border-secondary-700 max-w-3xl w-full max-h-[90vh] max-h-[90dvh] flex flex-col overflow-hidden">
                        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-secondary-200 dark:border-secondary-700">
                            <div className="min-w-0">
                                <h3
                                    id="time-breakdown-title"
                                    className="text-base font-semibold text-secondary-900 dark:text-white flex items-center gap-2"
                                >
                                    <Clock className="h-4 w-4 text-primary-600 flex-shrink-0" />
                                    Time Breakdown by Member
                                </h3>
                                <p className="text-xs text-secondary-500 mt-0.5">
                                    Total {formatTime(
                                        getTimeBreakdownByMember().reduce((sum, m) => sum + m.totalTime, 0)
                                    )}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowTimeBreakdownModal(false)}
                                className="p-1.5 rounded-lg text-secondary-400 hover:text-secondary-700 hover:bg-secondary-100 dark:hover:bg-secondary-700 dark:hover:text-secondary-200 transition-colors"
                                title="Close (Esc)"
                                aria-label="Close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="p-4 overflow-y-auto flex-1 min-h-0">
                            {(() => {
                                try {
                                    const memberBreakdown = getTimeBreakdownByMember();
                                    if (memberBreakdown.length === 0) {
                                        return (
                                            <EmptyState
                                                icon={Clock}
                                                title="No time entries yet"
                                                description="No time has been tracked for this task."
                                                className="py-10"
                                            />
                                        );
                                    }

                                    const totalEntries = memberBreakdown.reduce(
                                        (sum, member) => sum + member.entries.length,
                                        0
                                    );
                                    const totalTime = memberBreakdown.reduce(
                                        (sum, member) => sum + member.totalTime,
                                        0
                                    );
                                    const manualEntries = memberBreakdown.reduce(
                                        (sum, member) =>
                                            sum + member.entries.filter((entry) => entry.isManual).length,
                                        0
                                    );

                                    let entryCounter = 0;

                                    return (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                <StatCard
                                                    icon={Clock}
                                                    label="Total Time"
                                                    value={formatTime(totalTime)}
                                                />
                                                <StatCard
                                                    icon={Users}
                                                    label="Members"
                                                    value={memberBreakdown.length}
                                                />
                                                <StatCard
                                                    icon={List}
                                                    label="Time Entries"
                                                    value={totalEntries}
                                                />
                                                <StatCard
                                                    icon={Edit}
                                                    label="Manual Entries"
                                                    value={manualEntries}
                                                />
                                            </div>

                                            <div className="space-y-3">
                                                {memberBreakdown.map((member) => (
                                                    <Panel
                                                        key={member.userId}
                                                        padding={false}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50/80 dark:bg-secondary-900/40">
                                                            <div className="flex items-center gap-3 min-w-0">
                                                                <div className="h-9 w-9 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-700 dark:text-primary-300 font-semibold text-xs flex-shrink-0">
                                                                    {member.userName
                                                                        .split(' ')
                                                                        .map((n) => n[0])
                                                                        .join('')
                                                                        .slice(0, 2)
                                                                        .toUpperCase()}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="text-sm font-semibold text-secondary-900 dark:text-white truncate">
                                                                        {member.userName}
                                                                    </p>
                                                                    <p className="text-xs text-secondary-500">
                                                                        {member.entries.length}{' '}
                                                                        {member.entries.length === 1
                                                                            ? 'entry'
                                                                            : 'entries'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <p className="text-base font-bold text-primary-600 dark:text-primary-400 tabular-nums flex-shrink-0">
                                                                {formatTime(member.totalTime)}
                                                            </p>
                                                        </div>

                                                        <ul className="divide-y divide-secondary-100 dark:divide-secondary-700">
                                                            {member.entries.map((entry, index) => {
                                                                entryCounter += 1;
                                                                const entryNumber = entryCounter;
                                                                return (
                                                                    <li
                                                                        key={`${member.userId}-${index}`}
                                                                        className="px-4 py-3 hover:bg-secondary-50/70 dark:hover:bg-secondary-900/30 transition-colors"
                                                                    >
                                                                        <div className="flex items-start justify-between gap-3 mb-2">
                                                                            <div className="flex flex-wrap items-center gap-2 min-w-0">
                                                                                <span className="text-base font-semibold text-secondary-900 dark:text-white tabular-nums">
                                                                                    {formatTime(entry.duration)}
                                                                                </span>
                                                                                <StatusPill
                                                                                    status={
                                                                                        entry.isManual
                                                                                            ? 'Manual'
                                                                                            : 'Tracked'
                                                                                    }
                                                                                    showDot
                                                                                />
                                                                            </div>
                                                                            <span className="text-[11px] text-secondary-400 tabular-nums flex-shrink-0">
                                                                                #{entryNumber}
                                                                            </span>
                                                                        </div>

                                                                        {entry.description && (
                                                                            <p className="text-sm text-secondary-700 dark:text-secondary-300 bg-secondary-50 dark:bg-secondary-900/50 border border-secondary-100 dark:border-secondary-700 rounded-lg px-3 py-2 mb-2 leading-relaxed">
                                                                                {entry.description}
                                                                            </p>
                                                                        )}

                                                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-secondary-500">
                                                                            {entry.startTime && (
                                                                                <span>
                                                                                    <span className="font-medium text-secondary-600 dark:text-secondary-400">
                                                                                        Started
                                                                                    </span>{' '}
                                                                                    {toUTCPlus5(entry.startTime)}
                                                                                </span>
                                                                            )}
                                                                            {entry.endTime && (
                                                                                <span>
                                                                                    <span className="font-medium text-secondary-600 dark:text-secondary-400">
                                                                                        Ended
                                                                                    </span>{' '}
                                                                                    {toUTCPlus5(entry.endTime)}
                                                                                </span>
                                                                            )}
                                                                            <span className="text-primary-600 dark:text-primary-400 font-medium tabular-nums">
                                                                                Duration {formatTime(entry.duration)}
                                                                            </span>
                                                                        </div>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </Panel>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                } catch (error) {
                                    console.error('Error rendering time breakdown:', error);
                                    return (
                                        <EmptyState
                                            icon={AlertTriangle}
                                            title="Couldn’t load time breakdown"
                                            description="Please try closing and opening this again."
                                            className="py-10"
                                        />
                                    );
                                }
                            })()}
                        </div>

                        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50/80 dark:bg-secondary-900/40">
                            <p className="text-sm text-secondary-600 dark:text-secondary-400">
                                Total task time{' '}
                                <span className="font-semibold text-primary-600 dark:text-primary-400 tabular-nums">
                                    {formatTime(getTotalTrackedTime())}
                                </span>
                            </p>
                            <Button variant="primary" onClick={() => setShowTimeBreakdownModal(false)}>
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Collaborator Modal */}
            {showAddCollaboratorModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={() => setShowAddCollaboratorModal(false)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">Add Team Collaborator</h3>
                            <button
                                onClick={() => setShowAddCollaboratorModal(false)}
                                className="text-secondary-400 hover:text-secondary-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-2">
                                    GitHub Username <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="input-field w-full"
                                    placeholder="github-username"
                                    value={newCollaborator.username}
                                    onChange={(e) => setNewCollaborator({ ...newCollaborator, username: e.target.value })}
                                />
                                <p className="text-xs text-secondary-500 mt-1">
                                    Enter the GitHub username (without @ symbol)
                                </p>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-2">
                                    Permission Level
                                </label>
                                <select
                                    className="input-field w-full"
                                    value={newCollaborator.permission}
                                    onChange={(e) => setNewCollaborator({ ...newCollaborator, permission: e.target.value })}
                                >
                                    <option value="pull">Read</option>
                                    <option value="push">Write</option>
                                    <option value="admin">Admin</option>
                                </select>
                                <p className="text-xs text-secondary-500 mt-1">
                                    Read: Can view and clone, Write: Can push code, Admin: Full access
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex gap-2 justify-end mt-6">
                            <button
                                type="button"
                                onClick={() => setShowAddCollaboratorModal(false)}
                                className="btn-secondary"
                            >
                                Cancel
                            </button>
                            <button 
                                type="button"
                                onClick={addCollaborator}
                                className="btn-primary"
                                disabled={!newCollaborator.username.trim() || addCollaboratorLoading}
                            >
                                {addCollaboratorLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Adding...
                                    </>
                                ) : (
                                    'Add Collaborator'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Collaborators List Modal */}
            {showCollaboratorsModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={() => setShowCollaboratorsModal(false)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold">Repository Collaborators</h3>
                            <button
                                onClick={() => setShowCollaboratorsModal(false)}
                                className="text-secondary-400 hover:text-secondary-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="space-y-3">
                            {taskData.task.github_integration.collaborators && taskData.task.github_integration.collaborators.length > 0 ? (
                                taskData.task.github_integration.collaborators.map((collaborator, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900">@{collaborator.username}</div>
                                                <div className="text-sm text-gray-500 capitalize">{collaborator.permission} access</div>
                                            </div>
                                        </div>
                                        {user?.role === 'admin' && (
                                            <button
                                                onClick={() => {
                                                    setCollaboratorToRemove({
                                                        username: collaborator.username,
                                                        index: index
                                                    });
                                                    setShowRemoveCollaboratorModal(true);
                                                }}
                                                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                                title="Remove collaborator"
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        )}
                                        {user?.role !== 'admin' && (
                                            <div className="text-xs text-gray-400 px-2 py-1 bg-gray-100 rounded">
                                                View only
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8">
                                    <div className="text-gray-400 mb-2">
                                        <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="text-gray-500 text-sm font-medium">No collaborators yet</div>
                                    <div className="text-gray-400 text-xs mt-1">Add team members to collaborate on this repository</div>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex gap-2 justify-end mt-6">
                            {user?.role === 'admin' && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCollaboratorsModal(false);
                                        setShowAddCollaboratorModal(true);
                                    }}
                                className="btn-primary"
                            >
                                Add Collaborator
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setShowCollaboratorsModal(false)}
                                className="btn-secondary"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Collaborator Confirmation Modal */}
            {showRemoveCollaboratorModal && collaboratorToRemove && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={() => setShowRemoveCollaboratorModal(false)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-red-600">Remove Collaborator</h3>
                            <button
                                onClick={() => setShowRemoveCollaboratorModal(false)}
                                className="text-secondary-400 hover:text-secondary-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
                                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="font-medium text-red-800">Are you sure?</div>
                                    <div className="text-sm text-red-600">
                                        You are about to remove <span className="font-semibold">@{collaboratorToRemove.username}</span> from repository <span className="font-semibold">{taskData.task.github_integration.repository}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="text-sm text-gray-600">
                                This action will:
                                <ul className="list-disc list-inside mt-2 space-y-1">
                                    <li>Remove their access to this repository</li>
                                    <li>Revoke their permissions immediately</li>
                                    <li>Cannot be undone automatically</li>
                                </ul>
                            </div>
                        </div>
                        
                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                type="button"
                                onClick={() => setShowRemoveCollaboratorModal(false)}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowRemoveCollaboratorModal(false);
                                    setShowCollaboratorsModal(false);
                                    removeCollaborator(collaboratorToRemove.index);
                                    setCollaboratorToRemove(null);
                                }}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                            >
                                Remove Collaborator
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* GitHub Commit Selection Modal */}
            {showGitHubCommitModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={() => setShowGitHubCommitModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center shadow-sm">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Select GitHub Commit</h3>
                                    <p className="text-sm text-gray-500">Choose a commit to attach to your comment</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowGitHubCommitModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4">
                            <div className="space-y-4">
                                {/* Repository Info */}
                                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3 border border-gray-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center shadow-sm">
                                            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                            </svg>
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-semibold text-gray-900 text-base">
                                                {taskData.task.github_integration.repository}
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    {taskData.task.github_integration.branch_name}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* User Commits Note */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-blue-900">Personal Commits Only</div>
                                            <div className="text-xs text-blue-700">Showing commits created by {user?.github_username || user?.email}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Search and Filters */}
                                <div className="space-y-3">
                                    {/* Search Bar */}
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search commits by message, author, SHA, or branch..."
                                            value={commitSearchQuery}
                                            onChange={(e) => handleCommitSearch(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white shadow-sm"
                                        />
                                        <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>

                                    {/* Filter Controls */}
                                    <div className="flex flex-wrap gap-2">
                                        {/* Filter Type */}
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-gray-700">Filter:</span>
                                            <select
                                                value={commitFilter}
                                                onChange={(e) => handleCommitFilterChange(e.target.value)}
                                                className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            >
                                                <option value="all">All Commits</option>
                                                <option value="recent">Recent (Last 20)</option>
                                                <option value="branch">By Branch</option>
                                                <option value="author">By Author</option>
                                            </select>
                                        </div>

                                        {/* Branch Filter */}
                                        {commitFilter === 'branch' && availableBranches.length > 0 && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium text-gray-700">Branch:</span>
                                                <select
                                                    value={selectedBranch}
                                                    onChange={(e) => handleBranchChange(e.target.value)}
                                                    className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                >
                                                    {availableBranches.map(branch => (
                                                        <option key={branch} value={branch}>{branch}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Author Filter */}
                                        {commitFilter === 'author' && availableAuthors.length > 0 && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium text-gray-700">Author:</span>
                                                <select
                                                    value={selectedAuthor}
                                                    onChange={(e) => handleAuthorChange(e.target.value)}
                                                    className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                >
                                                    {availableAuthors.map(author => (
                                                        <option key={author} value={author}>{author}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>

                                    {/* Results Count */}
                                    <div className="text-xs text-gray-600 bg-gray-50 rounded-md px-2 py-1">
                                        Showing {filteredCommits.length} of {commits.length} commits
                                    </div>
                                </div>

                                {/* Smart Commit Suggestions */}
                                {commits.length > 0 && (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                                                <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-sm font-semibold text-gray-900">Smart Suggestions</div>
                                                <div className="text-xs text-gray-500">Related to this task</div>
                                            </div>
                                        </div>
                                        {getSmartCommitSuggestions().map((suggestion, idx) => (
                                            <div key={idx} className="space-y-2">
                                                <div className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded-md">
                                                    {suggestion.category}
                                                </div>
                                                <div className="space-y-1">
                                                    {suggestion.commits.map((commit, commitIdx) => (
                                                        <div
                                                            key={commitIdx}
                                                            onClick={() => handleSmartCommitSuggestion(commit)}
                                                            className="p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 group"
                                                        >
                                                            <div className="flex items-start gap-2">
                                                                <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center flex-shrink-0">
                                                                    <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                                    </svg>
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <span className="font-mono text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                                                                            {commit.sha.substring(0, 7)}
                                                                        </span>
                                                                        <span className="text-xs text-gray-500">
                                                                            {commit.author?.name}
                                                                        </span>
                                                                    </div>
                                                                    <div className="text-xs text-gray-900 line-clamp-2 mb-1">
                                                                        {commit.message}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        {toUTCPlus5DateOnly(commit.date)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Commits List */}
                                <div className="space-y-2">
                                    {commitsLoading ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                            <span className="ml-3 text-gray-600 font-medium">Loading commits...</span>
                                        </div>
                                    ) : filteredCommits.length > 0 ? (
                                        filteredCommits.map((commit, index) => (
                                                <div
                                                    key={commit.sha}
                                                    onClick={() => handleCommitSelect(commit)}
                                                    className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all duration-200 group shadow-sm hover:shadow-md"
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <div className="w-6 h-6 bg-green-100 rounded-md flex items-center justify-center">
                                                                    <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                                    </svg>
                                                                </div>
                                                                <span className="font-mono text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-md">
                                                                    {commit.sha.substring(0, 7)}
                                                                </span>
                                                                <span className="text-xs text-gray-600 font-medium">
                                                                    {commit.author?.name || 'Unknown'}
                                                                </span>
                                                                {commit.branch && (
                                                                    <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full font-medium">
                                                                        {commit.branch}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-sm font-medium text-gray-900 mb-2 line-clamp-2 leading-relaxed">
                                                                {commit.message}
                                                            </div>
                                                            <div className="flex items-center gap-3 text-xs text-gray-500">
                                                                <span className="flex items-center gap-1">
                                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                                    </svg>
                                                                    {toUTCPlus5(commit.date)}
                                                                </span>
                                                                {commit.files_changed && (
                                                                    <span className="flex items-center gap-1">
                                                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                            <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                                                                        </svg>
                                                                        {commit.files_changed} files
                                                                    </span>
                                                                )}
                                                                {commit.additions && commit.deletions && (
                                                                    <span className="flex items-center gap-1">
                                                                        <span className="text-green-600 font-medium text-xs">+{commit.additions}</span>
                                                                        <span className="text-red-600 font-medium text-xs">-{commit.deletions}</span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="ml-3 flex gap-1">
                                                            {/* Create Time Entry Button */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCreateTimeEntryFromCommit(commit);
                                                                }}
                                                                className="p-1.5 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-md transition-colors group-hover:bg-blue-200"
                                                                title="Create time entry from this commit"
                                                            >
                                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                                </svg>
                                                            </button>
                                                            
                                                            {/* Select Commit Button */}
                                                            <div className="p-1.5 bg-green-100 text-green-600 rounded-md">
                                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                    ) : commits.length > 0 ? (
                                        <div className="text-center py-8">
                                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                                <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                            </div>
                                            <div className="text-gray-600 text-base font-medium mb-1">No commits match your filters</div>
                                            <div className="text-gray-500 text-sm">
                                                Try adjusting your search or filter criteria
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                                <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="text-gray-600 text-base font-medium mb-1">No commits found</div>
                                            <div className="text-gray-500 text-sm">
                                                {user?.github_username 
                                                    ? `No commits found that were created by you (${user.github_username})`
                                                    : 'No commits found that were created by you'
                                                }
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Footer */}
                        <div className="flex gap-2 justify-end p-4 border-t border-gray-100 bg-gray-50">
                            <button
                                type="button"
                                onClick={() => setShowGitHubCommitModal(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={fetchRepositoryCommits}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                disabled={commitsLoading}
                            >
                                {commitsLoading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                        Refreshing...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Refresh Commits
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* GitHub Context Hover Tooltip */}
            {hoveredReference && (
                <div 
                    className="fixed z-[10000] bg-white dark:bg-secondary-800 border border-gray-200 dark:border-secondary-700 rounded-lg shadow-xl p-4 max-w-md"
                    style={{
                        left: hoveredReference.clientX + 10,
                        top: hoveredReference.clientY - 10,
                        pointerEvents: 'none'
                    }}
                >
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                {hoveredReference.type === 'commit' && (
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                )}
                                {hoveredReference.type === 'issue' && (
                                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                )}
                                {hoveredReference.type === 'pr' && (
                                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                )}
                                {hoveredReference.type === 'file' && (
                                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                                )}
                            </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-900 capitalize">
                            {hoveredReference.type}
                        </span>
                        <span className="text-sm font-mono text-gray-600">
                            {hoveredReference.value}
                        </span>
                    </div>

                    {contextLoading ? (
                        <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-xs text-gray-600">Loading...</span>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {(() => {
                                const cacheKey = `${hoveredReference.type}:${hoveredReference.value}`;
                                const context = referenceContext[cacheKey];
                                
                                if (!context) return null;
                                
                                switch (hoveredReference.type) {
                                    case 'commit':
                                        return (
                                            <div className="space-y-2">
                                                <div className="text-sm font-medium text-gray-900 line-clamp-2">
                                                    {context.message}
                                                </div>
                                                <div className="text-xs text-gray-600 space-y-1">
                                                    <div>Author: {context.author?.name}</div>
                                                    <div>Date: {toUTCPlus5(context.date)}</div>
                                                    {context.files_changed && (
                                                        <div>Files: {context.files_changed}</div>
                                                    )}
                                                    {context.additions && context.deletions && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-green-600">+{context.additions}</span>
                                                            <span className="text-red-600">-{context.deletions}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {context.files && context.files.length > 0 && (
                                                    <div className="text-xs">
                                                        <div className="font-medium text-gray-700 mb-1">Changed Files:</div>
                                                        <div className="space-y-1 max-h-20 overflow-y-auto">
                                                            {context.files.slice(0, 5).map((file, idx) => (
                                                                <div key={idx} className="flex items-center gap-2 text-gray-600">
                                                                    <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                                                                    <span className="truncate">{file.filename}</span>
                                                                    <span className="text-xs text-gray-500">
                                                                        {file.changes} changes
                                                                    </span>
                                                                </div>
                                                            ))}
                                                            {context.files.length > 5 && (
                                                                <div className="text-xs text-gray-500 italic">
                                                                    +{context.files.length - 5} more files
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    
                                    case 'issue':
                                        return (
                                            <div className="space-y-2">
                                                <div className="text-sm font-medium text-gray-900 line-clamp-2">
                                                    {context.title}
                                                </div>
                                                <div className="text-xs text-gray-600 space-y-1">
                                                    <div>Author: {context.user?.login}</div>
                                                    <div>Created: {toUTCPlus5DateOnly(context.created_at)}</div>
                                                    <div>State: {context.state}</div>
                                                    {context.labels && context.labels.length > 0 && (
                                                        <div className="flex flex-wrap gap-1">
                                                            {context.labels.slice(0, 3).map((label, idx) => (
                                                                <span 
                                                                    key={idx} 
                                                                    className="px-2 py-1 text-xs rounded-full"
                                                                    style={{ 
                                                                        backgroundColor: `#${label.color}`,
                                                                        color: parseInt(label.color, 16) > 0xffffff / 2 ? '#000' : '#fff'
                                                                    }}
                                                                >
                                                                    {label.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                {context.body && (
                                                    <div className="text-xs text-gray-600 line-clamp-3">
                                                        {context.body.replace(/<[^>]*>/g, '')}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    
                                    case 'pr':
                                        return (
                                            <div className="space-y-2">
                                                <div className="text-sm font-medium text-gray-900 line-clamp-2">
                                                    {context.title}
                                                </div>
                                                <div className="text-xs text-gray-600 space-y-1">
                                                    <div>Author: {context.user?.login}</div>
                                                    <div>Created: {toUTCPlus5DateOnly(context.created_at)}</div>
                                                    <div>State: {context.state}</div>
                                                    <div>Base: {context.base?.ref} ← Head: {context.head?.ref}</div>
                                                </div>
                                                {context.body && (
                                                    <div className="text-xs text-gray-600 line-clamp-3">
                                                        {context.body.replace(/<[^>]*>/g, '')}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    
                                    case 'file':
                                        return (
                                            <div className="space-y-2">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {context.name}
                                                </div>
                                                <div className="text-xs text-gray-600 space-y-1">
                                                    <div>Path: {context.path}</div>
                                                    <div>Size: {context.size} bytes</div>
                                                    <div>Type: {context.type}</div>
                                                    {context.updated_at && (
                                                        <div>Modified: {toUTCPlus5DateOnly(context.updated_at)}</div>
                                                    )}
                                                </div>
                                                {context.content && context.content.length > 0 && (
                                                    <div className="text-xs">
                                                        <div className="font-medium text-gray-700 mb-1">Preview:</div>
                                                        <div className="bg-gray-50 p-2 rounded text-xs font-mono text-gray-600 max-h-20 overflow-y-auto">
                                                            {context.content.slice(0, 200)}...
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    
                                    default:
                                        return null;
                                }
                            })()}
                        </div>
                    )}
                </div>
            )}

            {/* Release Management Modal */}
            {showReleaseManagementModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={() => setShowReleaseManagementModal(false)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] max-h-[90dvh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4 flex-shrink-0">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                                Release Management
                            </h3>
                            <button
                                onClick={() => setShowReleaseManagementModal(false)}
                                className="text-secondary-400 hover:text-secondary-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="space-y-4 flex-1 overflow-y-auto">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-sm text-gray-600">
                                    Release Management features are coming soon! This will allow you to:
                                    <ul className="list-disc list-inside mt-2 space-y-1">
                                        <li>Create release features for tracking commits</li>
                                        <li>Group commits by feature/release version</li>
                                        <li>Track feature status (in_progress, completed, deployed)</li>
                                        <li>Manage deployment workflows</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Code Review Modal */}
            {showCodeReviewModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={() => setShowCodeReviewModal(false)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] max-h-[90dvh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4 flex-shrink-0">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
                                </svg>
                                Code Review Integration
                            </h3>
                            <button
                                onClick={() => setShowCodeReviewModal(false)}
                                className="text-secondary-400 hover:text-secondary-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="space-y-4 flex-1 overflow-y-auto">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-sm text-gray-600">
                                    Code Review Integration features are coming soon! This will allow you to:
                                    <ul className="list-disc list-inside mt-2 space-y-1">
                                        <li>Create code reviews linked to GitHub PRs</li>
                                        <li>Track review status and comments</li>
                                        <li>Link PRs to specific tasks</li>
                                        <li>Manage approval workflows</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Time Entry Modal */}
            {showTimeEntryModal && selectedCommitForTimeEntry && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={() => setShowTimeEntryModal(false)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                                Create Time Entry
                            </h3>
                            <button
                                onClick={() => setShowTimeEntryModal(false)}
                                className="text-secondary-400 hover:text-secondary-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            {/* Commit Info */}
                            <div className="bg-gray-50 rounded-lg p-3">
                                <div className="text-sm text-gray-600 mb-2">Selected Commit:</div>
                                <div className="text-xs font-mono text-gray-800 bg-white p-2 rounded border">
                                    {selectedCommitForTimeEntry.sha.substring(0, 7)} - {selectedCommitForTimeEntry.message}
                                </div>
                            </div>
                            
                            {/* Time Entry Form */}
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description
                                    </label>
                                    <input
                                        type="text"
                                        id="time-entry-description"
                                        defaultValue={`Working on: ${selectedCommitForTimeEntry.message}`}
                                        className="input-field w-full"
                                        placeholder="Enter time entry description"
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Duration (minutes)
                                    </label>
                                    <input
                                        type="number"
                                        id="time-entry-duration"
                                        defaultValue="60"
                                        min="1"
                                        className="input-field w-full"
                                        placeholder="Enter duration in minutes"
                                    />
                                </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => setShowTimeEntryModal(false)}
                                    className="btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        const description = document.getElementById('time-entry-description').value;
                                        const duration = parseInt(document.getElementById('time-entry-duration').value);
                                        
                                        if (!description.trim()) {
                                            toast.error('Please enter a description');
                                            return;
                                        }
                                        
                                        if (isNaN(duration) || duration <= 0) {
                                            toast.error('Please enter a valid duration');
                                            return;
                                        }
                                        
                                        handleSubmitTimeEntry(description, duration);
                                    }}
                                    className="btn-primary flex-1"
                                >
                                    Create Time Entry
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Note Confirmation Modal */}
            {showDeleteNoteModal && noteToDelete && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl w-full max-w-md mx-4 p-6 shadow-2xl relative border border-red-100">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 rounded-full">
                                <Trash2 className="h-6 w-6 text-red-600" />
                            </div>
                            <h2 className="text-xl font-bold text-secondary-900">Delete Note</h2>
                        </div>
                        
                        <div className="mb-6">
                            <p className="text-secondary-600 mb-4">
                                Are you sure you want to delete this note? This action cannot be undone.
                            </p>
                            
                            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                <div className="flex items-start gap-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="h-6 w-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs">
                                            {noteToDelete.created_by_name ? noteToDelete.created_by_name[0].toUpperCase() : 'U'}
                                        </div>
                                        <span className="font-medium text-secondary-900 text-sm">{noteToDelete.created_by_name}</span>
                                        <div className="flex items-center gap-1">
                                            {noteToDelete.visibility === 'private' ? (
                                                <Lock className="h-3 w-3 text-red-500" title="Private note" />
                                            ) : (
                                                <Globe className="h-3 w-3 text-green-500" title="Public note" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <p className="text-sm text-secondary-800 mt-2 line-clamp-3">
                                    {noteToDelete.content}
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex gap-3 justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowDeleteNoteModal(false);
                                    setNoteToDelete(null);
                                }}
                                className="btn-secondary px-4 py-2"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteNote}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={deletingNote}
                            >
                                {deletingNote ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="h-4 w-4" />
                                        Delete Note
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskDetail; 